import React, { useState, useContext, useRef, useEffect } from "react";
import {
  Modal,
  Button,
  Col,
  Card,
  Row,
  Form,
  Tooltip,
  OverlayTrigger,
  InputGroup,
  Dropdown,
  Badge,
  Accordion,
  Alert,
} from "react-bootstrap";
import Select from "react-select";
import { Link, useLocation } from "react-router-dom";
import { openHelpCenter } from "./help/helpCenterEvents.js";
import {
  faCopy,
  faRefresh,
  faClock,
  faExclamationCircle,
  faCheckCircle,
  faTimesCircle,
  faCheckToSlot,
  faUserAlt,
  faPalette,
  faCheck,
  faExternalLinkAlt,
  faSave,
  faRecordVinyl,
  faSquareFull,
  faTimes,
  faPlus,
  faTabletScreenButton,
  faFilePen,
  faColumns,
  faDesktop,
  faCog,
  faFileText,
  faNetworkWired,
  faDownload,
  faArrowRight,
  faCogs,
  faUserSecret,
  faUnlock,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import { Formik } from "formik";
import { formatInTimeZone } from "date-fns-tz";
import InputMask from "react-input-mask";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DropzoneComponent from "./Dropzone.js";
import { domainConfig } from "../config.js";
import { ShowConfirm } from "./Alerts.js";
import { ColumnSortingTable } from "./Table.js";
import PopoverPicker from "./PopoverPicker.js";
import NotyfContext from "../contexts/NotyfContext.js";
import { confirmAlert } from "react-confirm-alert";
import { format, parse, isValid, differenceInDays } from "date-fns";
import Datetime from "react-datetime";
import useLayout from "../hooks/useLayout.js";
import useAppSelector from "../hooks/useAppSelector.js";
import {
  generateSixDigitCode,
  HelpTooltip,
  redirectURL,
  handleShare,
  getFirstError,
  handleFileUpload,
  generateExcelTemplate,
  isFutureDate,
  isRegistrationOpen,
  textToArray,
  ShareButton,
  getContrastColor,
} from "../assets/functions.js";
import {
  getRegisterTemplateReference,
  getMissingRegistrationTemplateTypes,
  REGISTRATION_EMAIL_USER_TYPES,
} from "../utils/registrationEmailTemplates.js";
import WhatsAppRegistrationFields from "./WhatsAppRegistrationFields.js";
import useAuth from "../hooks/useAuth.js";
import {
  statusX,
  colorsX,
  layoutX,
  getReactivationDates,
  handlePreviewSuccess,
  FormCompositionCard,
  FooterFormComponent,
  BackgroundFormComponent,
} from "./EventsComponents";
import { selectEvents } from "../redux/slices/events.js";
import { ComponentsTabs } from "./EventsComponentsTabs.js";
import { DEFAULT_TEMPLATES } from "../utils/emailTemplates.js";
import { getEventShareUrls } from "../utils/eventShareUrls.js";
import EventWebappPreview from "./EventWebappPreview.js";
import ImportValidationReportModal from "./ImportValidationReportModal.js";

const pwaMaskableImageTooltip = "Use um PNG quadrado de 512x512 com o elemento principal centralizado e margem de respiro, de preferência com fundo preto. Esse arquivo pode ser recortado pelo sistema ao instalar o app.";
const ogImageTooltip = "Use uma imagem de 1200x630 para compartilhamento em WhatsApp, LinkedIn, Facebook e outras redes. Evite textos muito pequenos e mantenha o conteúdo principal centralizado.";
const metaTagTitleTooltip = "Título exibido no compartilhamento do link e usado por mecanismos de busca. Prefira um texto curto, claro e com o nome da plataforma ou evento.";
const metaTagDescriptionTooltip = "Resumo do link exibido nas redes sociais e buscadores. Prefira uma descrição objetiva, com contexto suficiente para incentivar o clique.";

const event02UserTypes = [
  { value: "1", label: "Usuários gestores", field: "emails" },
  { value: "2", label: "Expositores", field: "emailsExhibitors" },
  { value: "3", label: "Participantes", field: "emailsParticipants" },
];

const countEmailLines = (value) => (
  typeof value === "string"
    ? value.split(/\r?\n/).filter((email) => email.trim() !== "").length
    : 0
);

const MetaTagFields = ({
  titleName,
  titleValue,
  titleTouched,
  titleError,
  descriptionName,
  descriptionValue,
  descriptionTouched,
  descriptionError,
  handleChange,
}) => {
  return (
    <Row className="mx-0 mb-3">
      <Form.Group as={Col} md="12" className="mb-3">
        <Form.Label>
          Título da Meta Tag
          <OverlayTrigger overlay={<Tooltip>{metaTagTitleTooltip}</Tooltip>}>
            <span className="ms-1 d-inline-block">
              <HelpTooltip icon={faExclamationCircle} />
            </span>
          </OverlayTrigger>
        </Form.Label>
        <Form.Control
          type="text"
          name={titleName}
          value={titleValue || ""}
          onChange={handleChange}
          maxLength={150}
          isInvalid={!!titleError}
          isValid={titleTouched && !titleError}
        />
        <Form.Control.Feedback type="valid"></Form.Control.Feedback>
        <Form.Control.Feedback type="invalid">
          {titleError}
        </Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="12" className="mb-3">
        <Form.Label>
          Descrição da Meta Tag
          <OverlayTrigger
            overlay={<Tooltip>{metaTagDescriptionTooltip}</Tooltip>}
          >
            <span className="ms-1 d-inline-block">
              <HelpTooltip icon={faExclamationCircle} />
            </span>
          </OverlayTrigger>
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name={descriptionName}
          value={descriptionValue || ""}
          onChange={handleChange}
          maxLength={255}
          isInvalid={!!descriptionError}
          isValid={descriptionTouched && !descriptionError}
        />
        <Form.Text className="text-muted">
          Opcional. Esses campos são usados pelo novo sistema de PWA e metatags.
        </Form.Text>
        <Form.Control.Feedback type="valid"></Form.Control.Feedback>
        <Form.Control.Feedback type="invalid">
          {descriptionError}
        </Form.Control.Feedback>
      </Form.Group>
    </Row>
  );
};

const SimplifiedTextColorPicker = ({
  color,
  error,
  label = "Cor do texto",
  onChange,
}) => (
  <Col sm={6} className={error ? "is-invalid" : ""}>
    <Row className="mb-2">
      <div
        className={`d-flex justify-content-start align-items-center ${error ? "is-invalid" : ""}`}
      >
        <PopoverPicker
          color={color}
          onChangeX={onChange}
          classes={`me-1 picker ${error ? "is-invalid" : ""}`}
        />
        <Form.Label className="mb-0 ms-2">{label}</Form.Label>
      </div>
      <Form.Control.Feedback type="invalid">
        {getFirstError(error)}
      </Form.Control.Feedback>
    </Row>
  </Col>
);

const RegistrationSettings = ({
  dataTable,
  dataAddForm,
  setDataAddForm,
  values,
  touched,
  errors,
  handleChange,
  setFieldValue,
}) => {
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingEmailTemplates, setLoadingEmailTemplates] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    setLoadingEmailTemplates(true);
    if (!dataTable?.evento_id) {
      setEmailTemplates([]);
      setLoadingEmailTemplates(false);
      return;
    }

    axios.get(`/api/event/email-templates/${dataTable.evento_id}`, { signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        if (res.data && res.data.message) {
          const options = res.data.message.map((t) => ({
            value: t.template_id || t.id,
            label: t.name || t.nome,
            template_version: t.template_version ?? null,
            available: Boolean(t.html?.trim()),
          }));
          setEmailTemplates(options);
        } else {
          setEmailTemplates([]);
        }
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setEmailTemplates([]);
        console.error("Error fetching email templates:", err);
      })
      .finally(() => { if (!controller.signal.aborted) setLoadingEmailTemplates(false); });
    return () => controller.abort();
  }, [dataTable?.evento_id]);

  const registerConfig = values.json?.email?.register || {};
  const emailTemplatesReady = !loadingEmailTemplates
    && getMissingRegistrationTemplateTypes(registerConfig, emailTemplates).length === 0;
  const getSelectedRegisterEmailTemplate = (type) => {
    const { template_id: templateId } = getRegisterTemplateReference(
      registerConfig,
      type,
    );

    if (templateId === null || templateId === undefined || templateId === "") {
      return null;
    }

    return emailTemplates.find((template) => String(template.value) === String(templateId)) || null;
  };

  const setRegisterTemplate = (type, option) => {
    if (!option) return;
    setFieldValue("json.email.register.templates", {
      ...(registerConfig.templates || {}),
      [String(type)]: {
        template_id: option ? option.value : null,
        template_version: option ? (option.template_version ?? null) : null,
      },
    });

    if (Number(type) === 0) {
      setFieldValue("json.email.register.template_id", option ? option.value : null);
      setFieldValue(
        "json.email.register.template_version",
        option ? (option.template_version ?? null) : null,
      );
    }

  };

  const registrationEndDateIsFuture = isFutureDate(
    values.json.form.register_end_date,
  );
  const registrationStatusIsOpen = isRegistrationOpen(values.json.form.status);
  const registrationIsOpen = isRegistrationOpen(
    values.json.form.status,
    values.json.form.register_end_date,
  );
  const registrationClosed = !registrationIsOpen;

  useEffect(() => {
    if (registrationEndDateIsFuture && !registrationStatusIsOpen) {
      setFieldValue("json.form.status", true, false);
    }
  }, [
    registrationEndDateIsFuture,
    registrationStatusIsOpen,
    setFieldValue,
  ]);

  return (
    <>
      <Row className="mb-3 mx-0">
        <p className="text-start fw-bold mb-2">Inscrições</p>
        <Form.Group as={Col} md="4" className={"mb-3"}>
          <Form.Label>Situação</Form.Label>
          <Form.Select
            name="status"
            onChange={(e) => {
              const newValue =
                e.target.value === true || e.target.value === "true"
                  ? true
                  : false;

              // If reactivating (switching from false to true)
              if (newValue === true && registrationClosed) {
                const reactivationDates = getReactivationDates(
                  values.end_date,
                );
                setFieldValue(
                  "end_date",
                  reactivationDates.formattedEventEndDate,
                );
                setFieldValue(
                  "json.form.register_end_date",
                  reactivationDates.formattedRegisterEndDate,
                );
              }

              setFieldValue("json.form.status", newValue);
            }}
            value={registrationIsOpen}
            isInvalid={!!errors.json?.form?.status}
            isValid={touched.json?.form?.status && !errors.json?.form?.status}
          >
            <option value={true}>
              {registrationClosed ? "Reativar" : "Em aberto"}
            </option>
            <option value={false}>Encerrado</option>
          </Form.Select>
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.json?.form?.status}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" as={Col} md="4" sm={6}>
          <Form.Label>Data de ínicio</Form.Label>
          <div
            className={`position-relative ${errors.json?.form?.register_start_date ? "is-invalid" : touched.json?.form?.register_start_date && !errors.json?.form?.register_start_date ? "is-valid" : ""}`}
          >
            <Datetime
              value={values.json.form.register_start_date}
              onChange={(date) => {
                if (
                  date &&
                  typeof date === "object" &&
                  date._isValid !== false
                ) {
                  const formattedDate = format(
                    new Date(date),
                    "dd/MM/yyyy HH:mm",
                  );
                  setFieldValue("json.form.register_start_date", formattedDate);
                } else {
                  setFieldValue("json.form.register_start_date", "");
                }
              }}
              dateFormat="DD/MM/YYYY"
              timeFormat="HH:mm"
              inputProps={{
                name: "json.form.register_start_date",
                placeholder: "dd/MM/yyyy HH:mm",
                className: `form-control ${errors.json?.form?.register_start_date
                  ? "is-invalid"
                  : touched.json?.form?.register_start_date &&
                    !errors.json?.form?.register_start_date
                    ? "is-valid"
                    : ""
                  }`,
              }}
              locale="pt-br"
            />
          </div>
          {touched.json?.form?.register_start_date &&
            !errors.json?.form?.register_start_date && (
              <div className="valid-feedback d-block"></div>
            )}
          {errors.json?.form?.register_start_date && (
            <div className="invalid-feedback d-block">
              {errors.json?.form?.register_start_date}
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3" as={Col} md="4" sm={6}>
          <Form.Label>Data de término</Form.Label>
          <div
            className={`position-relative ${errors.json?.form?.register_end_date ? "is-invalid" : touched.json?.form?.register_end_date && !errors.json?.form?.register_end_date ? "is-valid" : ""}`}
          >
            <Datetime
              value={values.json.form.register_end_date}
              onChange={(date) => {
                if (
                  date &&
                  typeof date === "object" &&
                  date._isValid !== false
                ) {
                  const formattedDate = format(
                    new Date(date),
                    "dd/MM/yyyy HH:mm",
                  );
                  setFieldValue("json.form.register_end_date", formattedDate);
                } else {
                  setFieldValue("json.form.register_end_date", "");
                }
              }}
              dateFormat="DD/MM/YYYY"
              timeFormat="HH:mm"
              inputProps={{
                name: "json.form.register_end_date",
                placeholder: "dd/MM/yyyy HH:mm",
                className: `form-control ${errors.json?.form?.register_end_date
                  ? "is-invalid"
                  : touched.json?.form?.register_end_date &&
                    !errors.json?.form?.register_end_date
                    ? "is-valid"
                    : ""
                  }`,
              }}
              locale="pt-br"
              isValidDate={(current) => {
                // Only allow dates >= register_start_date if register_start_date is set
                if (values.json.form.register_start_date) {
                  try {
                    const startDate = parse(
                      values.json.form.register_start_date,
                      "dd/MM/yyyy HH:mm",
                      new Date(),
                    );
                    if (isValid(startDate)) {
                      return current.toDate() >= startDate;
                    }
                  } catch {
                    return true;
                  }
                }
                return true;
              }}
            />
          </div>
          {touched.json?.form?.register_end_date &&
            !errors.json?.form?.register_end_date && (
              <div className="valid-feedback d-block"></div>
            )}
          {errors.json?.form?.register_end_date && (
            <div className="invalid-feedback d-block">
              {errors.json?.form?.register_end_date}
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check
            className="w-auto"
            checked={values.json.email.register.status ? true : false}
            type="checkbox"
            name="json.email.register.status"
            label="Envio de email automático após registro"
            disabled={!registerConfig.status && !emailTemplatesReady}
            onChange={() => {
              const nextStatus = !values.json.email.register.status;
              if (nextStatus && !emailTemplatesReady) return;
              setFieldValue(
                "json.email.register.status",
                nextStatus,
              );
            }}
          />
          {!loadingEmailTemplates && !emailTemplatesReady && <Form.Text className="text-muted d-block">Configure um modelo válido para cada tipo de usuário antes de ativar o envio automático de e-mail.</Form.Text>}
          {(values.json.email.register.status || (!loadingEmailTemplates && !emailTemplatesReady)) && (
            <div className="border p-3 mt-2 mb-0">
              <Row className="g-3">
                {REGISTRATION_EMAIL_USER_TYPES.map(({ type, label }) => (
                  <Col xs={12} lg={4} key={type}>
                    <Form.Group className="mb-0">
                      <Form.Label>Modelo de e-mail para {label}</Form.Label>
                      <Select
                        options={emailTemplates}
                        value={getSelectedRegisterEmailTemplate(type)}
                        onChange={(option) => setRegisterTemplate(type, option)}
                        placeholder="Selecione um modelo..."
                        isClearable={false}
                        styles={{
                          control: (provided) => ({ ...provided, padding: 4 }),
                        }}
                        noOptionsMessage={() => "Nenhum modelo de e-mail encontrado."}
                      />
                    </Form.Group>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Form.Group>
        <WhatsAppRegistrationFields eventId={dataTable.evento_id} values={values} setFieldValue={setFieldValue}
          fields={dataAddForm?.fields || values.json.form.fields} fieldsUsers={dataAddForm?.fieldsUsers || values.json.form.fieldsUsers}
          setDataAddForm={dataAddForm ? setDataAddForm : undefined} />
      </Row>

    </>
  );
};

export const EventDefinitions = ({
  dataTable,
  values,
  touched,
  errors,
  handleChange,
  setFieldValue,
  setActiveTab,
}) => {
  const eventEndDateIsFuture = isFutureDate(values.end_date);
  const effectiveEventStatus = eventEndDateIsFuture ? 2 : values.status;

  useEffect(() => {
    if (eventEndDateIsFuture && Number(values.status) !== 2) {
      setFieldValue("status", 2, false);
    }
  }, [eventEndDateIsFuture, setFieldValue, values.status]);

  return (
    <>
      <Row>
        <Form.Control type="hidden" name="status" value={true} />
        <Form.Control type="hidden" name="evento_id" value={values.event_id} />
        <Form.Group className="mb-3" as={Col} sm="12">
          <Form.Label>Título</Form.Label>
          <Form.Control
            type="text"
            name="title"
            value={values.title}
            onChange={handleChange}
            isInvalid={!!errors.title}
            isValid={touched.title && !errors.title}
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.title}
          </Form.Control.Feedback>
        </Form.Group>

        {/* <Form.Group className="mb-3" as={Col} sm="12">
          <Form.Label>Descrição</Form.Label>
          <Form.Control
            name="description"
            as="textarea"
            value={values.description}
            onChange={handleChange}
            isInvalid={!!errors.description}
            isValid={touched.description && !errors.description}
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.description}
          </Form.Control.Feedback>
        </Form.Group> */}
      </Row>
      <Row className="mb-3">
        <p className="text-start fw-bold mb-2">Status do evento</p>
        <Form.Group as={Col} md="3" className={"mb-3"}>
          <Form.Label>Situação</Form.Label>
          <Form.Select
            name="status"
            disabled={Number(effectiveEventStatus) > 1 ? false : true}
            onChange={(e) => {
              const currentStatus = Number(effectiveEventStatus);
              const newStatus = Number(e.target.value);

              if (currentStatus > 1) {
                // When reactivating (selecting status 2 from status 3 or 4)
                if (newStatus === 2 && [3, 4].includes(currentStatus)) {
                  const reactivationDates = getReactivationDates(
                    values.end_date,
                    true,
                  );
                  setFieldValue(
                    "end_date",
                    reactivationDates.formattedEventEndDate,
                  );
                  setFieldValue(
                    "json.form.register_end_date",
                    reactivationDates.formattedRegisterEndDate,
                  );
                }
                setFieldValue("status", newStatus);
              }
            }}
            value={effectiveEventStatus}
            isInvalid={!!errors.status}
            isValid={touched.status && !errors.status}
          >
            {statusX.map((element, index) => {
              const currentStatus = Number(effectiveEventStatus);

              if (currentStatus > 1) {
                if (index > 1) {
                  // Check if this is "Em andamento" (index 2) and values.status is 3 or 4
                  if (index === 2 && [3, 4].includes(currentStatus)) {
                    return (
                      <option key={"status" + index} value={index}>
                        Reativar
                      </option>
                    );
                  }
                  return (
                    <option key={"status" + index} value={index}>
                      {element}
                    </option>
                  );
                } else {
                  return "";
                }
              } else {
                return (
                  <option key={"status" + index} value={index}>
                    {element}
                  </option>
                );
              }
            })}
          </Form.Select>
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.status}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="mb-3" as={Col} md="3" sm={6}>
          <Form.Label>Data de criação</Form.Label>
          <div
            className={`position-relative ${errors.creation_date ? "is-invalid" : touched.creation_date && !errors.creation_date ? "is-valid" : ""}`}
          >
            <Datetime
              value={values.creation_date}
              onChange={(date) => {
                if (
                  date &&
                  typeof date === "object" &&
                  date._isValid !== false
                ) {
                  const formattedDate = format(
                    new Date(date),
                    "dd/MM/yyyy HH:mm",
                  );
                  setFieldValue("creation_date", formattedDate);
                } else {
                  setFieldValue("creation_date", "");
                }
              }}
              dateFormat="DD/MM/YYYY"
              timeFormat="HH:mm"
              inputProps={{
                name: "creation_date",
                placeholder: "dd/MM/yyyy HH:mm",
                className: `form-control ${errors.creation_date
                  ? "is-invalid"
                  : touched.creation_date && !errors.creation_date
                    ? "is-valid"
                    : ""
                  }`,
              }}
              locale="pt-br"
            />
          </div>
          {touched.creation_date && !errors.creation_date && (
            <div className="valid-feedback d-block"></div>
          )}
          {errors.creation_date && (
            <div className="invalid-feedback d-block">
              {errors.creation_date}
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3" as={Col} md="3" sm={6}>
          <Form.Label>Data de término</Form.Label>
          <div
            className={`position-relative ${errors.end_date ? "is-invalid" : touched.end_date && !errors.end_date ? "is-valid" : ""}`}
          >
            <Datetime
              value={values.end_date}
              onChange={(date) => {
                if (
                  date &&
                  typeof date === "object" &&
                  date._isValid !== false
                ) {
                  const formattedDate = format(
                    new Date(date),
                    "dd/MM/yyyy HH:mm",
                  );
                  setFieldValue("end_date", formattedDate);
                } else {
                  setFieldValue("end_date", "");
                }
              }}
              dateFormat="DD/MM/YYYY"
              timeFormat="HH:mm"
              inputProps={{
                name: "end_date",
                placeholder: "dd/MM/yyyy HH:mm",
                className: `form-control ${errors.end_date
                  ? "is-invalid"
                  : touched.end_date && !errors.end_date
                    ? "is-valid"
                    : ""
                  }`,
              }}
              locale="pt-br"
            />
          </div>
          {touched.end_date && !errors.end_date && (
            <div className="valid-feedback d-block"></div>
          )}
          {errors.end_date && (
            <div className="invalid-feedback d-block">{errors.end_date}</div>
          )}
        </Form.Group>
      </Row>
      <Row className="mb-3">
        <p className="text-start fw-bold mb-2">Módulos</p>
        <Form.Group className="mb-3">
          <Form.Check
            className="w-auto"
            checked={values.json.networking?.status ? true : false}
            type="checkbox"
            name="json.networking.status"
            label="Networking"
            onChange={(e) => {
              const newStatus = !values.json.networking?.status;
              setFieldValue("json.networking.status", newStatus);
              if (setActiveTab) {
                if (newStatus) {
                  setActiveTab("all");
                } else {
                  setActiveTab("form");
                }
              }
            }}
          />
          <Form.Check
            className="w-auto"
            checked={values.json.credentialAccess.status ? true : false}
            type="checkbox"
            name="json.credentialAccess.status"
            label="Credenciamento"
            onChange={(e) => {
              const newStatus = !values.json.credentialAccess.status;
              setFieldValue("json.credentialAccess.status", newStatus);
              // Auto-switch tabs based on module status
              if (setActiveTab) {
                if (newStatus) {
                  // Switch to credentialAccess tab when enabled
                  setActiveTab("credentialAccess");
                } else {
                  // Switch to form tab when disabled
                  setActiveTab("form");
                }
              }
            }}
          />
          <Form.Check
            className="w-auto"
            checked={values.json.sympla.status ? true : false}
            type="checkbox"
            name="json.sympla.status"
            label={"Sympla"}
            onChange={(e) => {
              const newStatus = !values.json.sympla.status;
              setFieldValue("json.sympla.status", newStatus);
              // Auto-switch tabs based on module status
              if (setActiveTab) {
                if (newStatus) {
                  // Switch to sympla tab when enabled
                  setActiveTab("sympla");
                } else {
                  // Switch to form tab when disabled
                  setActiveTab("form");
                }
              }
            }}
          />
        </Form.Group>
      </Row>
    </>
  );
};

export const AdvancedSettingsDefinitions = ({
  dataTable,
  values,
  touched,
  errors,
  handleChange,
  setFieldValue,
  onDelete,
}) => {
  const userState = useAuth();
  const selectEventsData = useAppSelector(selectEvents);
  const [serverConfigUnlocked, setServerConfigUnlocked] = useState(false);

  const handleServerConfigToggle = () => {
    if (serverConfigUnlocked) {
      setServerConfigUnlocked(false);
      return;
    }

    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Liberar edição dos servidores"
          description="Alterar a URL da API ou do WebSocket pode interromper a comunicação com a Plataforma de eventos e deixar o evento inacessível. Só continue se você tiver certeza dos novos endereços. Deseja liberar a edição?"
          action={() => setServerConfigUnlocked(true)}
          onClose={onClose}
          textConfirm="Liberar edição"
        />
      ),
    });
  };

  return (
    <>
      <Row className="mb-3 mx-0">
        {userState.tipo === 1 && (
          <>
            <p className="text-start fw-bold mb-2">
              <FontAwesomeIcon icon={faUserSecret} className="me-1" /> Administrador
            </p>
            <Form.Group as={Col} md="12" className={"mb-3"}>
              <Form.Label>Conta associada</Form.Label>
              <Select
                className="px-0"
                options={selectEventsData.usersList}
                getOptionLabel={(option) => option.email}
                getOptionValue={(option) => option.user_id}
                defaultValue={selectEventsData.usersList.find(
                  (user) => user.user_id === values.user_id,
                )}
                onChange={(option) => setFieldValue("user_id", option.user_id)}
                placeholder={"Clique para selecionar..."}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    padding: 5,
                  }),
                }}
              />
            </Form.Group>
          </>
        )}
      </Row>

      <Row className="mb-3 mx-0">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <p className="text-start fw-bold mb-0">
            <FontAwesomeIcon icon={faNetworkWired} className="me-1" /> Servidores
          </p>
          <Button
            type="button"
            variant={serverConfigUnlocked ? "outline-secondary" : "primary"}
            size="sm"
            onClick={handleServerConfigToggle}
          >
            <FontAwesomeIcon icon={serverConfigUnlocked ? faLock : faUnlock} />
          </Button>
        </div>
        <Form.Group as={Col} sm="6" md="3">
          <Form.Label>Web socket</Form.Label>
          <Form.Control
            name="json.server.eventosSocketAPI"
            type="text"
            value={values.json.server?.eventosSocketAPI || ""}
            onChange={handleChange}
            disabled={!serverConfigUnlocked}
            isInvalid={!!errors.json?.server?.eventosSocketAPI}
            isValid={
              touched.json?.server?.eventosSocketAPI &&
              !errors.json?.server?.eventosSocketAPI
            }
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.json?.server?.eventosSocketAPI}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} sm="6" md="3">
          <Form.Label>Rest API</Form.Label>
          <Form.Control
            name="json.server.eventosAPI"
            type="text"
            value={values.json.server?.eventosAPI || ""}
            onChange={handleChange}
            disabled={!serverConfigUnlocked}
            isInvalid={!!errors.json?.server?.eventosAPI}
            isValid={
              touched.json?.server?.eventosAPI &&
              !errors.json?.server?.eventosAPI
            }
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.json?.server?.eventosAPI}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} sm="6" md="3">
          <Form.Label>Aplicação</Form.Label>
          <Form.Control
            name="json.server.eventosApp"
            type="text"
            value={values.json.server?.eventosApp || ""}
            onChange={handleChange}
            disabled={!serverConfigUnlocked}
            isInvalid={!!errors.json?.server?.eventosApp}
            isValid={
              touched.json?.server?.eventosApp &&
              !errors.json?.server?.eventosApp
            }
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.json?.server?.eventosApp}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} sm="6" md="3">
          <Form.Label>Imagens</Form.Label>
          <Form.Control
            name="json.server.imageServer"
            type="text"
            value={values.json.server?.imageServer ?? domainConfig.imageServer ?? ""}
            onChange={handleChange}
            disabled={!serverConfigUnlocked}
          />
        </Form.Group>
      </Row>

      <Form.Group as={Col} className="mx-2 mt-3 pt-3">
        <h5 className="card-title mb-0"><FontAwesomeIcon
          icon={faExclamationCircle}
          className="me-1 text-danger"
        />Zona de Perigo</h5>
        <hr className="mt-2"></hr>
      </Form.Group>
      <Row className="mb-3 mx-0">
        <Form.Group className="mb-3">
          <Card className="border-danger">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <Card.Title className="text-danger mb-1" style={{ fontSize: "0.9rem" }}>
                    Exclusão do evento
                  </Card.Title>
                  <Card.Text className="text-muted mb-0" style={{ fontSize: "0.8rem" }}>
                    Uma vez excluído, todos os dados do evento, participantes e configurações serão removidos permanentemente. Esta ação não pode ser desfeita.
                  </Card.Text>
                </div>
              </div>
              <div className="mt-2">
                <Button
                  type="button"
                  variant="outline-danger"
                  className="btn-sm"
                  onClick={onDelete}
                >
                  Sim, desejo excluir o evento
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Form.Group>
      </Row>
    </>
  );
};

export const RegisterDefinitions = ({
  dataTable,
  values,
  touched,
  errors,
  handleChange,
  setFieldValue,
  setDataAddForm,
  dataAddForm,
  setModal,
}) => {
  const importUsersInput = useRef();
  const location = useLocation();
  const registeredParticipants = Math.max(
    Number(dataTable?.total_participantes) || 0,
    0,
  );
  const selectedUserType = String(values.selectedUserType || "1");
  const selectedUserTypeConfig = event02UserTypes.find(
    (userType) => userType.value === selectedUserType,
  ) || event02UserTypes[0];
  const selectedUserEmails = values[selectedUserTypeConfig.field] || "";
  const selectedUserEmailCount = countEmailLines(selectedUserEmails);

  return (
    <>
      <RegistrationSettings
        dataTable={dataTable}
        dataAddForm={dataAddForm}
        setDataAddForm={setDataAddForm}
        values={values}
        touched={touched}
        errors={errors}
        handleChange={handleChange}
        setFieldValue={setFieldValue}
      />
      <Row className="mb-3 mx-0">
        <p className="text-start fw-bold mb-2">
          Regras de inscrição
        </p>
        <Form.Label>Composição do formulário dos participante</Form.Label>
        <FormCompositionCard
          platform={dataTable.platform}
          dataAddForm={dataAddForm}
          setDataAddForm={setDataAddForm}
          setModal={setModal}
          values={values}
          setFieldValue={setFieldValue}
        ></FormCompositionCard>
      </Row>
      <Row className="mb-3 mx-0">
        <Form.Label>Composição do formulário dos usuários gestores</Form.Label>
        <FormCompositionCard
          platform={dataTable.platform}
          dataAddForm={dataAddForm}
          setDataAddForm={setDataAddForm}
          setModal={setModal}
          user={true}
          values={values}
          setFieldValue={setFieldValue}
        ></FormCompositionCard>
      </Row>
      <Row className="mx-0 mb-3">
        <Form.Group className="mb-3">
          <Form.Check
            checked={
              values.json.form.authorizationMessage.status ? true : false
            }
            type="checkbox"
            name="json.form.authorizationMessage.status"
            label="Checkbox de Consentimento"
            onChange={(e) => {
              setFieldValue(
                "json.form.authorizationMessage.status",
                values.json.form.authorizationMessage.status ? false : true,
              );
            }}
          />
          {values.json.form.authorizationMessage.status ? (
            <div className="border p-3 mt-2">
              <Form.Group className="mb-3" as={Col} sm="6">
                <Form.Label>Texto</Form.Label>
                <Form.Control
                  name="json.form.authorizationMessage.value"
                  value={values.json.form.authorizationMessage.value}
                  onChange={handleChange}
                  isInvalid={!!errors?.json?.form?.authorizationMessage?.value}
                  isValid={
                    touched?.json?.form?.authorizationMessage?.value &&
                    !errors?.json?.form?.authorizationMessage?.value
                  }
                />
                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  {errors?.json?.form?.authorizationMessage?.value}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} sm="6">
                <Form.Label>Política de Privacidade (URL)</Form.Label>
                <Form.Control
                  name="json.form.authorizationLink"
                  value={values.json.form.authorizationLink}
                  onChange={handleChange}
                  isInvalid={!!errors?.json?.form?.authorizationLink}
                  isValid={
                    touched?.json?.form?.authorizationLink &&
                    !errors?.json?.form?.authorizationLink
                  }
                />
                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  {errors?.json?.form?.authorizationLink}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          ) : (
            ""
          )}
        </Form.Group>
      </Row>



      <Row className="mb-3 mx-0">
        <p className="text-start fw-bold mb-2">
          Regras de inscrição
        </p>
        <Form.Group className="mb-3">
          <Form.Check
            checked={values.json.subscriptionLimit.status ? true : false}
            type="checkbox"
            name="json.subscriptionLimit.status"
            label="Ativar limite de inscrições"
            onChange={(e) => {
              setFieldValue(
                "json.subscriptionLimit.status",
                values.json.subscriptionLimit.status ? false : true,
              );
            }}
          />
          {values.json.subscriptionLimit.status ? (
            <div className="border p-3 mt-2 mb-3">
              <Form.Group className="mb-3">
                <Form.Label>Total de inscrições disponíveis</Form.Label>
                <Form.Control
                  type={"number"}
                  name="json.subscriptionLimit.limit"
                  min={registeredParticipants}
                  step={1}
                  value={values.json.subscriptionLimit.limit}
                  onChange={(event) => {
                    const rawValue = event.target.value;
                    if (rawValue === "") {
                      setFieldValue("json.subscriptionLimit.limit", "");
                      return;
                    }

                    const enteredValue = Number(rawValue);
                    setFieldValue(
                      "json.subscriptionLimit.limit",
                      Number.isFinite(enteredValue)
                        ? Math.max(Math.trunc(enteredValue), registeredParticipants)
                        : rawValue,
                    );
                  }}
                  isInvalid={!!errors?.json?.subscriptionLimit?.limit}
                  isValid={
                    touched?.json?.subscriptionLimit?.limit &&
                    !errors?.json?.subscriptionLimit?.limit
                  }
                />
                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  {errors?.json?.subscriptionLimit?.limit}
                </Form.Control.Feedback>
                <Form.Text className="text-muted d-block mt-1">
                  Participantes registrados: {registeredParticipants}
                </Form.Text>
              </Form.Group>
              <p className="text-start fw-bold mb-2">
                Tela de aviso
                <OverlayTrigger
                  overlay={
                    <Tooltip>
                      O usuário será redirecionado para essa tela em caso de
                      limite de inscrição atingido
                    </Tooltip>
                  }
                >
                  <HelpTooltip icon={faExclamationCircle} />
                </OverlayTrigger>
              </p>
              <Form.Group className="mb-3" as={Col} sm="12">
                <Form.Label>Título</Form.Label>
                <Form.Control
                  as="textarea"
                  name="json.subscriptionLimit.disableFormTitle"
                  value={values.json.subscriptionLimit.disableFormTitle}
                  onChange={handleChange}
                  isInvalid={!!errors?.json?.subscriptionLimit?.disableFormTitle}
                  isValid={
                    touched?.json?.subscriptionLimit?.disableFormTitle &&
                    !errors?.json?.subscriptionLimit?.disableFormTitle
                  }
                />
                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  {errors?.json?.subscriptionLimit?.disableFormTitle}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col} sm="12">
                <Form.Label>Descrição</Form.Label>
                <Form.Control
                  as="textarea"
                  name="json.subscriptionLimit.disableDescription"
                  value={values.json.subscriptionLimit.disableDescription}
                  onChange={handleChange}
                  isInvalid={
                    !!errors?.json?.subscriptionLimit?.disableDescription
                  }
                  isValid={
                    touched?.json?.subscriptionLimit?.disableDescription &&
                    !errors?.json?.subscriptionLimit?.disableDescription
                  }
                />
                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  {errors?.json?.subscriptionLimit?.disableDescription}
                </Form.Control.Feedback>
              </Form.Group>
            </div>) : ""}
          <Form.Check
            checked={values.json.form.validEmails?.status || false}
            type="checkbox"
            name="json.form.validEmails.status"
            label={
              <>
                Restringir domínios de e-mail permitidos
                <OverlayTrigger
                  overlay={
                    <Tooltip>
                      Quando ativado, apenas e-mails dos domínios especificados
                      serão aceitos no registro
                    </Tooltip>
                  }
                >
                  <HelpTooltip icon={faExclamationCircle} />
                </OverlayTrigger>
              </>
            }
            onChange={(e) => {
              setFieldValue("json.form.validEmails.status", e.target.checked);
            }}
          />
          {values.json.form.validEmails?.status && (
            <div className="p-3 border mt-2">
              <Form.Group>
                <Form.Label>Adicionar domínio permitido</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    placeholder="exemplo.com ou @exemplo.com"
                    name="json.form.validEmails.newDomain"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const input = e.target;
                        let domain = input.value.trim().toLowerCase();
                        if (domain) {
                          if (!domain.startsWith("@")) {
                            domain = "@" + domain;
                          }
                          const currentList =
                            values.json.form.validEmails?.list || [];
                          if (!currentList.includes(domain)) {
                            setFieldValue("json.form.validEmails.list", [
                              ...currentList,
                              domain,
                            ]);
                            input.value = "";
                          }
                        }
                      }
                    }}
                    isInvalid={!!errors?.json?.form?.validEmails?.list}
                  />
                  <Button
                    variant="primary"
                    style={{ whiteSpace: "nowrap" }}
                    onClick={(e) => {
                      const input = e.currentTarget.previousSibling;
                      let domain = input.value.trim().toLowerCase();
                      if (domain) {
                        if (!domain.startsWith("@")) {
                          domain = "@" + domain;
                        }
                        const currentList =
                          values.json.form.validEmails?.list || [];
                        if (!currentList.includes(domain)) {
                          setFieldValue("json.form.validEmails.list", [
                            ...currentList,
                            domain,
                          ]);
                          input.value = "";
                        }
                      }
                    }}
                  >
                    Adicionar
                  </Button>
                </div>
                <Form.Control.Feedback type="invalid">
                  {errors?.json?.form?.validEmails?.list}
                </Form.Control.Feedback>
              </Form.Group>

              {values.json.form.validEmails?.list &&
                values.json.form.validEmails.list.length > 0 && (
                  <div>
                    <Form.Label className="mt-2">
                      Domínios permitidos (
                      {values.json.form.validEmails.list.length})
                    </Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                      {values.json.form.validEmails.list.map(
                        (domain, index) => (
                          <span
                            key={index}
                            className="badge bg-primary badge-sm text-dark d-flex align-items-center gap-2"
                          >
                            <span>{domain}</span>
                            <FontAwesomeIcon
                              icon={faTimes}
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                const currentList =
                                  values.json.form.validEmails.list;
                                const newList = currentList.filter(
                                  (d) => d !== domain,
                                );
                                setFieldValue(
                                  "json.form.validEmails.list",
                                  newList,
                                );
                              }}
                            />
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                )}
            </div>
          )}
        </Form.Group>
      </Row>

      <Form.Group as={Col} className="textHeaderAplicativo mb-3 mx-2">
        <h5 className="card-title mb-0">
          Adicionar registros
        </h5>
      </Form.Group>
      <Row className="mx-0">
        <Form.Group as={Col} sm={12} className="mb-3">
          <Form.Label>Selecione um tipo de usuário</Form.Label>
          <Form.Select
            name="selectedUserType"
            value={selectedUserType}
            onChange={handleChange}
          >
            {event02UserTypes.map((userType) => (
              <option key={userType.value} value={userType.value}>
                {userType.label} ({countEmailLines(values[userType.field])})
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} sm={12} className="mb-3">
          <Form.Control
            style={{ height: "150px" }}
            type="emails"
            as="textarea"
            name={selectedUserTypeConfig.field}
            value={selectedUserEmails}
            aria-label={`${selectedUserTypeConfig.label} (${selectedUserEmailCount})`}
            onChange={handleChange}
            isInvalid={!!errors[selectedUserTypeConfig.field]}
            isValid={
              touched[selectedUserTypeConfig.field]
              && !errors[selectedUserTypeConfig.field]
            }
          />
          <Form.Text className="text-muted">
            Adicione os emails separado por linha
            <OverlayTrigger
              overlay={
                <Tooltip>
                  Emails repetidos serão ignorados automaticamente
                </Tooltip>
              }
            >
              <HelpTooltip icon={faExclamationCircle} />
            </OverlayTrigger>
          </Form.Text>
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors[selectedUserTypeConfig.field]}
          </Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mx-0">
        <Form.Group className="mb-3" as={Col} md="6">
          <p className="text-start fw-bold mb-1">
            Importar participantes (
            {dataAddForm.importUsers.users.length > 0
              ? dataAddForm.importUsers.users.length
              : 0}
            )
          </p>
          <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
            <Button
              type="button"
              variant="primary"
              className="btn-sm"
              onClick={() =>
                generateExcelTemplate(
                  dataTable.platform,
                  dataTable.title,
                  dataAddForm,
                )
              }
            >
              Baixar modelo
            </Button>
            <Button
              type="button"
              onClick={() => openHelpCenter("/ajuda/plataforma-eventos/gestao-evento/importacao")}
              variant="outline-secondary"
              className="btn-sm"
            >
              Central de ajuda
            </Button>
          </div>

          <Form.Check
            type="checkbox"
            label="Ignorar validações locais de conteúdo"
            checked={dataAddForm.importUsers.disableValidation || false}
            onChange={(e) => {
              if (e.target.checked) {
                confirmAlert({
                  closeOnEscape: false,
                  closeOnClickOutside: false,
                  customUI: ({ onClose }) => {
                    return (
                      <ShowConfirm
                        title="Confirmar ação"
                        description="Use apenas para recuperação. Este modo mantém as validações do arquivo e dos cabeçalhos, mas ignora regras locais de conteúdo. O servidor ainda valida e-mail, duplicidade e vagas. Deseja continuar?"
                        action={() => {
                          setDataAddForm((prev) => ({ ...prev, importUsers: { ...prev.importUsers, disableValidation: true } }));
                          onClose();
                        }}
                        onClose={onClose}
                      />
                    );
                  },
                });
              } else {
                setDataAddForm((prev) => ({ ...prev, importUsers: { ...prev.importUsers, disableValidation: false } }));
              }
            }}
          />

          <Form.Check
            type="checkbox"
            className="mb-3"
            label="Definir usuário como ativo (Registro completo)"
            disabled={!dataAddForm.importUsers.disableValidation}
            checked={dataAddForm.importUsers.activeUserAutomatically !== false}
            onChange={(e) => {
              const checked = e.target.checked;
              setDataAddForm((prev) => ({
                ...prev,
                importUsers: {
                  ...prev.importUsers,
                  activeUserAutomatically: checked,
                },
              }));
            }}
          />

          <InputGroup>
            <Form.Control
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) =>
                handleFileUpload(
                  dataTable.platform,
                  e,
                  dataAddForm,
                  setDataAddForm,
                  importUsersInput,
                  {
                    fields: dataAddForm.fields,
                    event_id: dataTable.evento_id,
                    platform: dataTable.platform,
                    validEmailsConfig: values.json.form.validEmails,
                    eventJson: {
                      ...values.json,
                      form: { ...values.json.form, fields: dataAddForm.fields },
                    },
                    skipRowValidation: dataAddForm.importUsers.disableValidation,
                  },
                )
              }
              isInvalid={!!dataAddForm.importUsers.error}
              isValid={Boolean(dataAddForm.importUsers.formFilename) && !dataAddForm.importUsers.error}
              ref={importUsersInput}
            />
            <OverlayTrigger overlay={<Tooltip>Resetar</Tooltip>}>
              <InputGroup.Text
                onClick={() => {
                  setDataAddForm((prevState) => ({
                    ...prevState,
                    importUsers: {
                      ...prevState.importUsers,
                      users: [],
                      fields: [],
                      error: "",
                      errorList: [],
                      report: null,
                      formValue: "",
                      formFilename: "",
                    },
                  }));
                  importUsersInput.current.value = "";
                }}
              >
                <FontAwesomeIcon icon={faRefresh} />
              </InputGroup.Text>
            </OverlayTrigger>
          </InputGroup>
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <div
            className="invalid-feedback d-flex align-items-center"
            style={{ display: dataAddForm.importUsers.error ? "block" : "none" }}
          >
            <span className="me-2">{dataAddForm.importUsers.error}</span>
            {dataAddForm.importUsers.errorList && dataAddForm.importUsers.errorList.length > 0 && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setDataAddForm((prev) => ({
                  ...prev,
                  importUsers: { ...prev.importUsers, report: { ...(prev.importUsers.report || { errors: prev.importUsers.errorList }), open: true } },
                }))}
              >
                Ver detalhes
              </Button>
            )}
          </div>
        </Form.Group>
      </Row>
      <Row className="mx-0">
        <Col md={12} className="mb-3">
          <Card className="mb-3">
            <Card.Body className="overflow-auto">
              <ColumnSortingTable
                columns={dataAddForm.importUsers.fields}
                data={dataAddForm.importUsers.users}
                title={dataTable.titulo}
                removeCard={true}
                getCellProps={(cellinfo) => ({
                  className:
                    cellinfo.column.id === "icons" ? "table-icons-triple" : "",
                })}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ImportValidationReportModal
        report={dataAddForm.importUsers.report}
        onClose={() => setDataAddForm((prev) => ({
          ...prev,
          importUsers: { ...prev.importUsers, report: { ...prev.importUsers.report, open: false } },
        }))}
      />
    </>
  );
};

export const AppDefinitions = ({
  dataTable,
  values,
  touched,
  errors,
  handleChange,
  setFieldValue,
  setDataAddForm,
  dataAddForm,
  handlePreviewError,
  fonts,
  setModal,
  errorNavigation,
}) => {
  const isSimplifiedCustomizationEnabled = (personalizationType) =>
    personalizationType ? personalizationType === "simplified" : true;

  const [isSimplifiedCustomization, setIsSimplifiedCustomization] = useState(
    isSimplifiedCustomizationEnabled(values?.json?.personalizationType)
  );
  const [simplifiedThemeMode, setSimplifiedThemeMode] = useState(
    values?.json?.simplifiedThemeMode || "light"
  );
  const [activeLoginAccordion, setActiveLoginAccordion] = useState(null);

  useEffect(() => {
    setIsSimplifiedCustomization(
      isSimplifiedCustomizationEnabled(values?.json?.personalizationType),
    );
  }, [values?.json?.personalizationType]);

  useEffect(() => {
    setSimplifiedThemeMode(values?.json?.simplifiedThemeMode || "light");
  }, [values?.json?.simplifiedThemeMode]);

  useEffect(() => {
    if (errorNavigation?.path?.startsWith("json.login")) {
      setActiveLoginAccordion("tela-de-login");
    }
  }, [errorNavigation]);

  const applySimplifiedTheme = (primaryColor, themeMode) => {
    const contrast = getContrastColor(primaryColor) || "#ffffff";

    setFieldValue("json.simplifiedThemeMode", themeMode);
    setFieldValue("json.primaryColor", primaryColor);
    setFieldValue("json.primaryColorText", contrast);
    setFieldValue("json.secondaryColor", "rgb(108, 117, 125)");
    setFieldValue("json.secondaryColorText", "rgba(255, 255, 255, 1)");

    if (themeMode === "light") {
      setFieldValue("json.background.color", "rgba(246,248,251,1)");
      setFieldValue("json.bodyBackgroundColor", "rgba(255, 255, 255, 0.8)");
      setFieldValue("json.navBackgroundColor", "rgba(255, 255, 255, 1)");
      setFieldValue("json.modalBackgroundColor", "rgba(255, 255, 255, 1)");
      setFieldValue("json.containerColor", getContrastColor("rgba(246,248,251,1)"));
      setFieldValue("json.login.loading.backgroundColor", "rgba(246,248,251,1)");

      setFieldValue("json.bodyColorText", "rgba(90, 90, 90, 1)");
      setFieldValue("json.navPrimaryColor", "rgba(90, 90, 90, 1)");
      setFieldValue("json.modalColorText", "rgba(90, 90, 90, 1)");
      setFieldValue("json.borderColor", "rgb(206, 212, 218)");

      setProps((prevState) => ({
        ...prevState,
        primaryColor: primaryColor,
        primaryColorText: contrast,
        navPrimaryColor: "rgba(90, 90, 90, 1)",
        backgroundColor: "rgba(246,248,251,1)",
        bodyBackgroundColor: "rgba(255, 255, 255, 0.8)",
        navBackgroundColor: "rgba(255, 255, 255, 1)",
        modalBackgroundColor: "rgba(255, 255, 255, 1)",
        containerColor: getContrastColor("rgba(246,248,251,1)"),
        loadingBackgroundColor: "rgba(246,248,251,1)",
        bodyColorText: "rgba(90, 90, 90, 1)",
        modalColorText: "rgba(90, 90, 90, 1)",
        borderColor: "rgb(206, 212, 218)",
        secondaryColor: "rgb(108, 117, 125)",
        secondaryColorText: "rgba(255, 255, 255, 1)"
      }));
    } else {
      setFieldValue("json.background.color", "rgba(45, 45, 45, 1)");
      setFieldValue("json.bodyBackgroundColor", "rgba(20, 20, 20, 0.4)");
      setFieldValue("json.navBackgroundColor", "rgba(20, 20, 20, 1)");
      setFieldValue("json.modalBackgroundColor", "rgba(45, 45, 45, 1)");
      setFieldValue("json.containerColor", getContrastColor("rgba(45, 45, 45, 1)"));
      setFieldValue("json.login.loading.backgroundColor", "rgba(45, 45, 45, 1)");

      setFieldValue("json.bodyColorText", "rgba(255, 255, 255, 1)");
      setFieldValue("json.navPrimaryColor", "rgba(255, 255, 255, 1)");
      setFieldValue("json.modalColorText", "rgba(255, 255, 255, 1)");
      setFieldValue("json.borderColor", "rgb(60, 60, 60)");
      setFieldValue("json.secondaryColor", "rgba(255, 255, 255, 1)");
      setFieldValue("json.secondaryColorText", "rgb(108, 117, 125)");

      setProps((prevState) => ({
        ...prevState,
        primaryColor: primaryColor,
        primaryColorText: contrast,
        navPrimaryColor: "rgba(255, 255, 255, 1)",
        backgroundColor: "rgba(45, 45, 45, 1)",
        bodyBackgroundColor: "rgba(20, 20, 20, 0.4)",
        navBackgroundColor: "rgba(20, 20, 20, 1)",
        modalBackgroundColor: "rgba(45, 45, 45, 1)",
        containerColor: getContrastColor("rgba(45, 45, 45, 1)"),
        loadingBackgroundColor: "rgba(45, 45, 45, 1)",
        bodyColorText: "rgba(255, 255, 255, 1)",
        modalColorText: "rgba(255, 255, 255, 1)",
        borderColor: "rgb(60, 60, 60)",
        secondaryColor: "rgba(255, 255, 255, 1)",
        secondaryColorText: "rgb(108, 117, 125)"
      }));
    }
  };

  const [props, setProps] = useState({
    backgroundColor: dataTable.json.background.color,
    primaryColor: dataTable.json.primaryColor,
    primaryColorText: dataTable.json.primaryColorText,
    secondaryColor: dataTable.json.secondaryColor,
    secondaryColorText: dataTable.json.secondaryColorText,
    navPrimaryColor: dataTable.json.navPrimaryColor,
    navBackgroundColor: dataTable.json.navBackgroundColor,
    modalBackgroundColor: dataTable.json.modalBackgroundColor,
    modalColorText: dataTable.json.modalColorText,
    containerColor: dataTable.json.containerColor,
    bodyColorText: dataTable.json.bodyColorText,
    borderColor: dataTable.json.borderColor,
    bodyBackgroundColor: dataTable.json.bodyBackgroundColor,
    loadingBackgroundColor: dataTable.json.login.loading.backgroundColor,
  });

  useEffect(() => {
    const json = values?.json;
    if (!json) return;

    setProps((prevState) => ({
      ...prevState,
      backgroundColor: json.background?.color,
      primaryColor: json.primaryColor,
      primaryColorText: json.primaryColorText,
      secondaryColor: json.secondaryColor,
      secondaryColorText: json.secondaryColorText,
      navPrimaryColor: json.navPrimaryColor,
      navBackgroundColor: json.navBackgroundColor,
      modalBackgroundColor: json.modalBackgroundColor,
      modalColorText: json.modalColorText,
      containerColor: json.containerColor,
      bodyColorText: json.bodyColorText,
      borderColor: json.borderColor,
      bodyBackgroundColor: json.bodyBackgroundColor,
      loadingBackgroundColor: json.login?.loading?.backgroundColor,
    }));
  }, [
    values?.json?.background?.color,
    values?.json?.primaryColor,
    values?.json?.primaryColorText,
    values?.json?.secondaryColor,
    values?.json?.secondaryColorText,
    values?.json?.navPrimaryColor,
    values?.json?.navBackgroundColor,
    values?.json?.modalBackgroundColor,
    values?.json?.modalColorText,
    values?.json?.containerColor,
    values?.json?.bodyColorText,
    values?.json?.borderColor,
    values?.json?.bodyBackgroundColor,
    values?.json?.login?.loading?.backgroundColor,
  ]);

  return (
    <>



      <Form.Group as={Col} className="textHeaderAplicativo mb-3 mx-2">
        <h5 className="card-title mb-0">Rotas</h5>
      </Form.Group>
      <Row className="mb-3 mx-0">
        <Accordion className="mb-3"
          activeKey={activeLoginAccordion}
          onSelect={(eventKey) => setActiveLoginAccordion(eventKey)}
        >
          <Accordion.Item eventKey="tela-de-login" className="mb-2 border rounded">
            <Accordion.Header>
              <div className="d-flex flex-column text-start">
                <span className="fw-semibold">
                  Login
                </span>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <Row className="mb-3">
                <Form.Group>
                  <Form.Check
                    className="w-auto"
                    checked={values.json.login.enableRegister ? true : false}
                    type="checkbox"
                    name="json.login.enableRegister"
                    label={
                      <>
                        Habilitar formulário de registro
                        <OverlayTrigger
                          overlay={
                            <Tooltip>
                              Libera o formulário de registro para criação de contas no
                              webapp
                            </Tooltip>
                          }
                        >
                          <HelpTooltip icon={faExclamationCircle} />
                        </OverlayTrigger>
                      </>
                    }
                    onChange={(e) => {
                      setFieldValue(
                        "json.login.enableRegister",
                        values.json.login.enableRegister ? false : true,
                      );
                    }}
                  />
                  <Form.Check
                    className="w-auto mb-3"
                    checked={values.json.login.enableGuest ? true : false}
                    type="checkbox"
                    name="json.login.enableGuest"
                    label={
                      <>
                        Habilitar acesso de convidado (Visitante)
                        <OverlayTrigger
                          overlay={
                            <Tooltip>
                              Permite que usuários acessem o evento sem a necessidade de login ou cadastro, com recursos limitados.
                            </Tooltip>
                          }
                        >
                          <HelpTooltip icon={faExclamationCircle} />
                        </OverlayTrigger>
                      </>
                    }
                    onChange={(e) => {
                      setFieldValue(
                        "json.login.enableGuest",
                        values.json.login.enableGuest ? false : true,
                      );
                    }}
                  />
                </Form.Group>
                <Form.Group as={Col} md="6" className="mb-2">
                  <Form.Label>Método de login</Form.Label>
                  <Form.Select
                    name="json.login.loginType"
                    value={values.json.login.loginType}
                    onChange={(e) => {
                      const numericValue = parseInt(e.target.value, 10);
                      setFieldValue("json.login.loginType", numericValue);
                    }}
                  >
                    <option value={1}>Código de acesso único</option>
                    <option value={2}>Código de acesso fixo</option>
                    <option value={3}>Acesso apenas com email (pré-registrado)</option>
                    <option value={4}>Acesso com email e cadastro automático</option>
                  </Form.Select>
                  <Form.Text className="text-muted mt-2 d-block">
                    {values.json.login.loginType === 1 && (
                      <>Envio de código de acesso por email com uso único</>
                    )}
                    {values.json.login.loginType === 2 && (
                      <>Envio de código de acesso por email com uso indefinitivo</>
                    )}
                    {values.json.login.loginType === 3 && (
                      <>Não é preciso código de acesso e o usuário acessa apenas com o email cadastrado no evento</>
                    )}
                    {values.json.login.loginType === 4 && (
                      <>Não é preciso código de acesso; e-mails ainda não cadastrados criam um registro incompleto para concluir no aplicativo</>
                    )}
                  </Form.Text>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <p className="text-start fw-bold mb-2"><FontAwesomeIcon
                  icon={faTabletScreenButton}
                  className="me-1"
                />Tela de login</p>
                <Form.Group as={Col} md="12" className="mb-3">
                  <Form.Label>Texto de boas-vindas</Form.Label>
                  <Form.Control
                    type="text"
                    name="json.login.welcomeTitle"
                    value={values.json.login.welcomeTitle}
                    onChange={handleChange}
                    isInvalid={!!errors.json?.login?.welcomeTitle}
                    isValid={touched.json?.login?.welcomeTitle && !errors.json?.login?.welcomeTitle}
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors.json?.login?.welcomeTitle}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} md="6" className="mb-3">
                  <Form.Label>Rodapé</Form.Label>
                  <Form.Select
                    name="json.layout.footer.type"
                    onChange={(e) => {
                      if (e.target.value === "noFooter")
                        setFieldValue("json.layout.footer.socialMedia", []);
                      setFieldValue("json.layout.footer.type", e.target.value);
                    }}
                    value={values.json.layout.footer.type}
                    isInvalid={!!errors.json?.layout?.footer?.type}
                    isValid={
                      touched.json?.layout?.footer?.type &&
                      !errors.json?.layout?.footer?.type
                    }
                  >
                    <option value={"noFooter"}>Sem rodapé</option>
                    <option value={"image"}>Imagem</option>
                    <option value={"icons"}>Ícones</option>
                  </Form.Select>
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors.json?.layout?.type}
                  </Form.Control.Feedback>
                </Form.Group>
                <FooterFormComponent
                  dataAddForm={dataAddForm}
                  setDataAddForm={setDataAddForm}
                  setModal={setModal}
                  touched={touched}
                  errors={errors}
                  values={values}
                  setFieldValue={setFieldValue}
                  handlePreviewSuccess={handlePreviewSuccess}
                  handlePreviewError={handlePreviewError}
                  platform={dataTable.platform}
                ></FooterFormComponent>
              </Row>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </Row>
      <Form.Group as={Col} className="textHeaderAplicativo mb-3 mx-2">
        <h5 className="card-title mb-0">Gerenciamento de componentes</h5>
      </Form.Group>
      <ComponentsTabs
        setFieldValue={setFieldValue}
        dataTable={values}
        handleChange={handleChange}
        touched={touched}
        errors={errors}
        handlePreviewError={handlePreviewError}
        dataAddForm={dataAddForm}
        setDataAddForm={setDataAddForm}
        setModal={setModal}
      />

      <Form.Group as={Col} className="textHeaderAplicativo mb-3 mx-2">
        <h5 className="card-title mb-0">Configurações</h5>
      </Form.Group>

      <Row className="mx-0 mb-3">
        <Form.Group className="mb-3">
          <p className="text-start fw-bold mb-2">Menu</p>
          <Form.Check
            className="w-auto"
            checked={values.json.configs.qrcode.hideUserQR ? true : false}
            type="checkbox"
            name="json.configs.qrcode.hideUserQR"
            label='Esconder botão do "Código QR" do cabeçalho'
            onChange={(e) => {
              setFieldValue(
                "json.configs.qrcode.hideUserQR",
                values.json.configs.qrcode.hideUserQR ? false : true,
              );
            }}
          />
          <Form.Check
            className="w-auto"
            checked={values.json.configs.sections.hideMenu ? true : false}
            type="checkbox"
            name="json.configs.sections.hideMenu"
            label='Esconder botão do "Menu" do rodapé'
            onChange={(e) => {
              setFieldValue(
                "json.configs.sections.hideMenu",
                values.json.configs.sections.hideMenu ? false : true,
              );
            }}
          />
          <Form.Check
            className="w-auto"
            checked={values.json.configs.navbar?.userIconIco ? true : false}
            type="checkbox"
            name="json.configs.navbar.userIconIco"
            label='Exibir menu do usuário como ícone no cabeçalho'
            onChange={(e) => {
              setFieldValue(
                "json.configs.navbar.userIconIco",
                values.json.configs.navbar?.userIconIco ? false : true,
              );
            }}
          />
        </Form.Group>

        <Form.Group as={Col} md="6" className="mb-3">
          <p className="text-start fw-bold mb-2">Definições</p>
          <Form.Check
            className="w-auto"
            checked={values.json.login.showTutorial ? true : false}
            type="checkbox"
            name="json.login.showTutorial"
            label="Habilitar tutorial para novos usuários"
            onChange={(e) => {
              setFieldValue(
                "json.login.showTutorial",
                values.json.login.showTutorial ? false : true,
              );
            }}
          />

          <Form.Check
            className="w-auto"
            checked={values.json.login.noProfileImage ? true : false}
            type="checkbox"
            name="json.login.noProfileImage"
            label="Remover foto de perfil dos participantes"
            onChange={(e) => {
              setFieldValue(
                "json.login.noProfileImage",
                values.json.login.noProfileImage ? false : true,
              );
            }}
          />
        </Form.Group>

        <Form.Group as={Col} md="6" className="mb-3">
          <p className="text-start fw-bold mb-2">Notificações</p>
          <Form.Check
            className="w-auto"
            checked={values.json.configs.notifications.status ? true : false}
            type="checkbox"
            name="json.configs.notifications.status"
            label="Ativar sistema de notificação"
            onChange={(e) => {
              setFieldValue(
                "json.configs.notifications.status",
                values.json.configs.notifications.status ? false : true,
              );
            }}
          />
        </Form.Group>
      </Row>

      <Form.Group as={Col} className="textHeaderAplicativo mb-3 mx-2">
        <h5 className="card-title mb-0">Customização</h5>
      </Form.Group>

      <Row className="mx-0 mb-3 align-items-center">
        <Form.Group>
          <Form.Check
            type="checkbox"
            id="simplified-customization-switch"
            label="Personalização Simplificada"
            checked={isSimplifiedCustomization}
            onChange={(e) => {
              const isChecked = e.target.checked;
              if (isChecked) {
                confirmAlert({
                  closeOnEscape: false,
                  closeOnClickOutside: false,
                  customUI: ({ onClose }) => {
                    return (
                      <ShowConfirm
                        title="Confirmar ação"
                        description="Habilitar a personalização simplificada substituirá o tema atual para um layout padrão. Deseja continuar?"
                        action={() => {
                          setIsSimplifiedCustomization(true);
                          setFieldValue("json.personalizationType", "simplified");
                          applySimplifiedTheme("rgb(215, 205, 18)", simplifiedThemeMode);
                          onClose();
                        }}
                        onClose={() => {
                          onClose();
                        }}
                      />
                    );
                  },
                });
              } else {
                setIsSimplifiedCustomization(false);
                setFieldValue("json.personalizationType", "advanced");
              }
            }}
          />
        </Form.Group>
      </Row>
      {isSimplifiedCustomization && (
        <>
          <Row className="mx-0 mb-3">
            <p className="text-start fw-bold mb-2">Perfil do Aplicativo</p>
            <Form.Group as={Col} md="6" className="mb-3">
              <Form.Label>Tema</Form.Label>
              <Form.Select
                value={simplifiedThemeMode}
                onChange={(e) => {
                  const newTheme = e.target.value;
                  setSimplifiedThemeMode(newTheme);
                  applySimplifiedTheme(
                    values.json.primaryColor || props.primaryColor || "rgb(215, 205, 18)",
                    newTheme,
                  );
                }}
              >
                <option value="light">Modo Claro (Fundo Branco)</option>
                <option value="dark">Modo Escuro (Fundo Preto)</option>
              </Form.Select>
              <Form.Text className="text-muted mt-2 d-block">
                {simplifiedThemeMode === "light"
                  ? "O Modo Claro apresenta um fundo claro com textos escuros, garantindo alta visibilidade e visual limpo."
                  : "O Modo Escuro utiliza um fundo escuro com textos claros, proporcionando um design moderno e maior conforto visual."}
              </Form.Text>

            </Form.Group>
            <Form.Group as={Col} md="6" className="mb-3">
              <Form.Label>
                Cor primária
                <OverlayTrigger
                  overlay={
                    <Tooltip>
                      Para melhor legibilidade, recomendamos uma cor de destaque diferente de preto e branco.
                    </Tooltip>
                  }
                >
                  <span className="ms-1 d-inline-block">
                    <HelpTooltip icon={faExclamationCircle} />
                  </span>
                </OverlayTrigger>
              </Form.Label>
              <div className="d-flex justify-content-start align-items-center picker">
                <PopoverPicker
                  color={props.primaryColor}
                  onChangeX={(e) => {
                    setFieldValue("json.primaryColor", e);
                    const contrast = getContrastColor(e);
                    if (contrast) {
                      setFieldValue("json.primaryColorText", contrast);
                    }
                    setProps((prevState) => ({
                      ...prevState,
                      primaryColor: e,
                      ...(contrast ? { primaryColorText: contrast } : {}),
                    }));
                  }}
                />
              </div>
              <Form.Control.Feedback type="invalid">
                {getFirstError(errors?.json?.primaryColor)}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
          <Row className="mx-0 mb-3">
            <p className="text-start fw-bold mb-2">Identidade</p>
            <Form.Group as={Col} md="6" className="mb-3">
              <div>
                <Form.Label>Logomarca</Form.Label>
              </div>
              <Card className="mb-0">
                <Card.Body>
                  <Form.Control
                    type="hidden"
                    name="json.logo"
                    value={values.json.logo}
                    isInvalid={!!errors?.json?.logo}
                    isValid={touched?.json?.logo && !errors?.json?.logo}
                  />
                  <div className="imagesUpload">
                    <img
                      className={`h-100 img-thumbnail ${!!errors?.json?.logo ? "is-invalid" : ""}`}
                      src={
                        values.json.logo !== "" && values.json.logo !== null
                          ? domainConfig.imageServer +
                          "/plataforma-eventos/" +
                          values.event_id +
                          "/" +
                          values.json.logo
                          : domainConfig.imageServer + "/empty-background.png"
                      }
                      alt="Imagem preview"
                    />
                  </div>
                  <DropzoneComponent
                    className="mt-3 dropzoneComp"
                    type="image"
                    handlePreviewSuccess={(fileName) => {
                      if (fileName) {
                        setFieldValue("json.logo", fileName);
                        setFieldValue("json.logoNav", fileName);
                        setFieldValue("json.maskableIcon", fileName);
                        setFieldValue("json.ogImage", fileName);
                        setFieldValue("json.login.loading.url", fileName);
                      }
                    }}
                    handlePreviewError={handlePreviewError}
                    setFieldValue={setFieldValue}
                    folder="plataforma-eventos"
                    id={values.event_id}
                    field="json.logo"
                  />
                </Card.Body>
              </Card>
            </Form.Group>
          </Row>
        </>
      )}

      {!isSimplifiedCustomization && (
        <Row className="mx-0 mb-3">
          <p className="text-start fw-bold mb-2">Fonte</p>
          <Form.Group as={Col} md={4} className="mb-3">
            <Form.Label>
              Estilo{" "}
              <span
                className="badge text-dark bg-light cursor-pointer ms-1"
                onClick={() => redirectURL(true, "https://fonts.google.com/")}
              >
                Google fonts
              </span>
            </Form.Label>
            <Select
              name="json.fontFamily"
              options={fonts}
              value={fonts.find(
                (option) => option.value === values.json.fontFamily,
              )}
              onChange={(selectedOption) => {
                setFieldValue("json.fontFamily", selectedOption.value);
              }}
              styles={{
                control: (provided) => ({
                  ...provided,
                  padding: 5,
                }),
              }}
            />
          </Form.Group>

          <Form.Group as={Col} md={4} className="mb-3">
            <Form.Label>Tamanho fonte (rem)</Form.Label>
            <Form.Control
              type="number"
              name="json.fontFamilySize"
              step={"0.01"}
              value={values.json.fontFamilySize}
              onChange={handleChange}
              isInvalid={!!errors.json?.fontFamilySize}
              isValid={
                touched.json?.fontFamilySize && !errors.json?.fontFamilySize
              }
            />
            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              {errors.json?.fontFamilySize}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
      )}

      {!isSimplifiedCustomization && (
        <Row className="mb-3 mx-0">
          <Form.Group as={Col} md="6" className="mb-3">
            <div>
              <Form.Label>Logomarca</Form.Label>
            </div>
            <Card className="mb-0">
              <Card.Body>
                <Form.Control
                  type="hidden"
                  name="json.logo"
                  value={values.json.logo}
                  onChange={handleChange}
                  isInvalid={!!errors?.json?.logo}
                  isValid={touched?.json?.logo && !errors?.json?.logo}
                />
                <div className="imagesUpload">
                  <img
                    className={`h-100 img-thumbnail ${!!errors?.json?.logo ? "is-invalid" : ""}`}
                    src={
                      values.json.logo !== "" && values.json.logo !== null
                        ? domainConfig.imageServer +
                        "/plataforma-eventos/" +
                        values.event_id +
                        "/" +
                        values.json.logo
                        : domainConfig.imageServer + "/empty-background.png"
                    }
                    alt="Imagem preview"
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors?.json?.logo}
                  </Form.Control.Feedback>
                </div>
                <DropzoneComponent
                  className="mt-3 dropzoneComp"
                  type="image"
                  handlePreviewSuccess={handlePreviewSuccess}
                  handlePreviewError={handlePreviewError}
                  setFieldValue={setFieldValue}
                  folder="plataforma-eventos"
                  id={values.event_id}
                  field="json.logo"
                />
                <Form.Group as={Col} className="mt-3">
                  <Form.Label>Largura (px)</Form.Label>
                  <Form.Control
                    type="text"
                    name="json.logoWidth"
                    value={values.json.logoWidth}
                    onChange={handleChange}
                    isInvalid={!!errors.json?.logoWidth}
                    isValid={touched.json?.logoWidth && !errors.json?.logoWidth}
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors.json?.logoWidth}
                  </Form.Control.Feedback>
                </Form.Group>
              </Card.Body>
            </Card>
          </Form.Group>

          <Form.Group as={Col} md="6" className="mb-3">
            <div>
              <Form.Label>Logomarca (Barra de navegação)</Form.Label>
            </div>
            <Card className="mb-0">
              <Card.Body>
                <Form.Control
                  type="hidden"
                  name="json.logoNav"
                  value={values.json.logoNav}
                  onChange={handleChange}
                  isInvalid={!!errors?.json?.logoNav}
                  isValid={touched?.json?.logoNav && !errors?.json?.logoNav}
                />
                <div className="imagesUpload">
                  <img
                    className={`h-100 img-thumbnail ${!!errors?.json?.logoNav ? "is-invalid" : ""}`}
                    src={
                      values.json.logoNav !== "" && values.json.logoNav !== null
                        ? domainConfig.imageServer +
                        "/plataforma-eventos/" +
                        values.event_id +
                        "/" +
                        values.json.logoNav
                        : domainConfig.imageServer + "/empty-background.png"
                    }
                    alt="Imagem preview"
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors?.json?.logoNav}
                  </Form.Control.Feedback>
                </div>
                <DropzoneComponent
                  className="mt-3 dropzoneComp"
                  type="image"
                  handlePreviewSuccess={handlePreviewSuccess}
                  handlePreviewError={handlePreviewError}
                  setFieldValue={setFieldValue}
                  folder="plataforma-eventos"
                  id={values.event_id}
                  field="json.logoNav"
                />
              </Card.Body>
            </Card>
          </Form.Group>
          <Form.Group as={Col} md="6" className="mb-3">
            <div>
              <Form.Label>
                Ícone PWA / Maskable (512x512 PNG)
                <OverlayTrigger
                  overlay={<Tooltip>{pwaMaskableImageTooltip}</Tooltip>}
                >
                  <HelpTooltip icon={faExclamationCircle} />
                </OverlayTrigger>
              </Form.Label>
            </div>
            <Card className="mb-0">
              <Card.Body>
                <Form.Control
                  type="hidden"
                  name="json.maskableIcon"
                  value={values.json.maskableIcon || ""}
                  onChange={handleChange}
                  isInvalid={!!errors?.json?.maskableIcon}
                  isValid={
                    touched?.json?.maskableIcon && !errors?.json?.maskableIcon
                  }
                />
                <div className="imagesUpload">
                  <img
                    className={`h-100 img-thumbnail ${!!errors?.json?.maskableIcon ? "is-invalid" : ""}`}
                    src={
                      values.json.maskableIcon !== "" &&
                        values.json.maskableIcon !== null
                        ? domainConfig.imageServer +
                        "/plataforma-eventos/" +
                        values.event_id +
                        "/" +
                        values.json.maskableIcon
                        : domainConfig.imageServer + "/empty-background.png"
                    }
                    alt="Imagem preview"
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors?.json?.maskableIcon}
                  </Form.Control.Feedback>
                </div>
                <DropzoneComponent
                  className="mt-3 dropzoneComp"
                  type="image"
                  handlePreviewSuccess={handlePreviewSuccess}
                  handlePreviewError={handlePreviewError}
                  setFieldValue={setFieldValue}
                  folder="plataforma-eventos"
                  id={values.event_id}
                  field="json.maskableIcon"
                />
              </Card.Body>
            </Card>
          </Form.Group>

          <Form.Group as={Col} md="6" className="mb-3">
            <div>
              <Form.Label>
                Imagem OG (1200x630)
                <OverlayTrigger
                  overlay={<Tooltip>{ogImageTooltip}</Tooltip>}
                >
                  <HelpTooltip icon={faExclamationCircle} />
                </OverlayTrigger>
              </Form.Label>
            </div>
            <Card className="mb-0">
              <Card.Body>
                <Form.Control
                  type="hidden"
                  name="json.ogImage"
                  value={values.json.ogImage || ""}
                  onChange={handleChange}
                  isInvalid={!!errors?.json?.ogImage}
                  isValid={
                    touched?.json?.ogImage && !errors?.json?.ogImage
                  }
                />
                <div className="imagesUpload">
                  <img
                    className={`h-100 img-thumbnail ${!!errors?.json?.ogImage ? "is-invalid" : ""}`}
                    src={
                      values.json.ogImage !== "" &&
                        values.json.ogImage !== null
                        ? domainConfig.imageServer +
                        "/plataforma-eventos/" +
                        values.event_id +
                        "/" +
                        values.json.ogImage
                        : domainConfig.imageServer + "/empty-background.png"
                    }
                    alt="Imagem preview"
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors?.json?.ogImage}
                  </Form.Control.Feedback>
                </div>
                <DropzoneComponent
                  className="mt-3 dropzoneComp"
                  type="image"
                  handlePreviewSuccess={handlePreviewSuccess}
                  handlePreviewError={handlePreviewError}
                  setFieldValue={setFieldValue}
                  folder="plataforma-eventos"
                  id={values.event_id}
                  field="json.ogImage"
                />
              </Card.Body>
            </Card>
          </Form.Group>
        </Row>
      )}

      {!isSimplifiedCustomization && (
        <MetaTagFields
          titleName="json.metaTagTitle"
          titleValue={values.json.metaTagTitle}
          titleTouched={touched?.json?.metaTagTitle}
          titleError={errors?.json?.metaTagTitle}
          descriptionName="json.metaTagDescription"
          descriptionValue={values.json.metaTagDescription}
          descriptionTouched={touched?.json?.metaTagDescription}
          descriptionError={errors?.json?.metaTagDescription}
          handleChange={handleChange}
        />
      )}

      <>
        <Row className="mx-0 mb-3">
          <p className="text-start fw-bold mb-2">Plano de fundo</p>
          <Form.Group as={Col} md="6">
            <Form.Label>
              Tipo{" "}
              {values.json.background.type === "color" && (
                <OverlayTrigger
                  overlay={
                    <Tooltip>
                      <b>Exemplo: </b>linear-gradient(90deg,rgba(42, 123, 155, 1)
                      0%, rgba(87, 199, 133, 1) 50%, rgba(237, 221, 83, 1) 100%)
                    </Tooltip>
                  }
                >
                  <span
                    className="badge text-dark bg-light cursor-pointer ms-1"
                    onClick={() => redirectURL(true, "https://cssgradient.io/")}
                  >
                    CSS Gradient
                  </span>
                </OverlayTrigger>
              )}
            </Form.Label>
            <Form.Select
              name="json.background.type"
              onChange={handleChange}
              value={values.json.background.type}
              isInvalid={!!errors.json?.background?.type}
              isValid={
                touched.json?.background?.type && !errors.json?.background?.type
              }
            >
              <option value={"color"}>Cor</option>
              <option value={"image"}>Imagem</option>
              <option value={"video"}>Vídeo</option>
            </Form.Select>
            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              {errors.json?.background?.type}
            </Form.Control.Feedback>
          </Form.Group>
        </Row>
        {values.json.background.type !== "color" && (
          <>
            {isSimplifiedCustomization && (
              <Row className="mb-3 mx-0">
                <SimplifiedTextColorPicker
                  color={props.containerColor || values.json.containerColor || "#000000"}
                  error={errors?.json?.containerColor}
                  onChange={(e) => {
                    setFieldValue("json.containerColor", e);
                    setProps((prevState) => ({
                      ...prevState,
                      containerColor: e,
                    }));
                  }}
                />
              </Row>
            )}
            <Row className="mb-3 mx-0">
              <Form.Group as={Col} md="6">
                <BackgroundFormComponent
                  touched={touched}
                  errors={errors}
                  values={values}
                  setFieldValue={setFieldValue}
                  handlePreviewSuccess={handlePreviewSuccess}
                  handlePreviewError={handlePreviewError}
                  platform={dataTable.platform}
                ></BackgroundFormComponent>
              </Form.Group>
            </Row>
          </>
        )}
        {values.json.background.type === "color" && (
          <Row
            className="mx-0 mb-3"
            data-error-path="json.background.color,json.primaryColor,json.primaryColorText,json.secondaryColor,json.secondaryColorText,json.navPrimaryColor,json.navBackgroundColor,json.modalBackgroundColor,json.modalColorText,json.containerColor,json.bodyColorText,json.borderColor,json.bodyBackgroundColor"
          >
            <Col
              sm={6}
              className={
                errors?.json?.background?.color ||
                  errors?.json?.baseColorText ||
                  errors?.json?.transparency ||
                  errors?.json?.primaryColor ||
                  errors?.json?.primaryColorText ||
                  errors?.json?.bsBorderColor ||
                  errors?.json?.boxShadow
                  ? "mb-3 is-invalid"
                  : "mb-3"
              }
            >
              <Row>
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.background?.color ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.backgroundColor}
                    onChangeX={(e) => {
                      setFieldValue("json.background.color", e);
                      const contrast = isSimplifiedCustomization
                        ? getContrastColor(e)
                        : null;
                      if (isSimplifiedCustomization) {
                        if (contrast) {
                          setFieldValue("json.containerColor", contrast);
                        }
                      }
                      setProps((prevState) => ({
                        ...prevState,
                        backgroundColor: e,
                        ...(isSimplifiedCustomization && contrast
                          ? { containerColor: contrast }
                          : {}),
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.background?.color ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor de fundo</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(
                    errors?.json?.background?.color, // Add more variables as needed
                  )}
                </Form.Control.Feedback>
              </Row>
            </Col>
          </Row>
        )}
      </>

      {!isSimplifiedCustomization && (
        <>
          <Row className="mx-0 mb-3">
            <p className="text-start fw-bold mb-2">Cores</p>

            <Col
              sm={6}
              className={errors?.json?.containerColor
                ? "mb-3 is-invalid"
                : "mb-3"
              }
            >
              <Row className="mb-2">
                <Form.Label>Contêineres</Form.Label>
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.containerColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.containerColor}
                    onChangeX={(e) => {
                      setFieldValue("json.containerColor", e);
                      setProps((prevState) => ({
                        ...prevState,
                        containerColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.containerColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor do texto</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.containerColor)}
                </Form.Control.Feedback>
              </Row>
            </Col>

            <Col
              sm={6}
              className={
                errors?.json?.background?.color ||
                  errors?.json?.bodyColorText ||
                  errors?.json?.bodyBackgroundColor ||
                  errors?.json?.primaryColor ||
                  errors?.json?.primaryColorText ||
                  errors?.json?.borderColor ||
                  errors?.json?.navBackgroundColor
                  ? "mb-3 is-invalid"
                  : "mb-3"
              }
            >
              <Row className="mb-2">
                <Form.Label>Cards</Form.Label>
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.bodyBackgroundColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.bodyBackgroundColor}
                    onChangeX={(e) => {
                      setFieldValue("json.bodyBackgroundColor", e);
                      setProps((prevState) => ({
                        ...prevState,
                        bodyBackgroundColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.bodyBackgroundColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor de fundo</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.bodyBackgroundColor)}
                </Form.Control.Feedback>
              </Row>

              <Row className="mb-2">
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.bodyColorText ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.bodyColorText}
                    onChangeX={(e) => {
                      setFieldValue("json.bodyColorText", e);
                      setProps((prevState) => ({
                        ...prevState,
                        bodyColorText: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.bodyColorText ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor do texto</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.bodyColorText)}
                </Form.Control.Feedback>
              </Row>

              <Row className="mb-2">
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.borderColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.borderColor}
                    onChangeX={(e) => {
                      setFieldValue("json.borderColor", e);
                      setProps((prevState) => ({
                        ...prevState,
                        borderColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.borderColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Bordas</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.borderColor)}
                </Form.Control.Feedback>
              </Row>
            </Col>
            <Col
              sm={6}
              className={
                errors?.json?.background?.color ||
                  errors?.json?.primaryColor ||
                  errors?.json?.primaryColorText ||
                  errors?.json?.navPrimaryColor ||
                  errors?.json?.navBackgroundColor ||
                  errors?.json?.modalBackgroundColor ||
                  errors?.json?.modalColorText ||
                  errors?.json?.containerColor ||
                  errors?.json?.bodyColorText ||
                  errors?.json?.borderColor ||
                  errors?.json?.bodyBackgroundColor
                  ? "mb-3 is-invalid"
                  : "mb-3"
              }
            >
              <Row className="mb-2">
                <Form.Label>Botões e elementos destacáveis</Form.Label>
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.primaryColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.primaryColor}
                    onChangeX={(e) => {
                      setFieldValue("json.primaryColor", e);
                      setProps((prevState) => ({
                        ...prevState,
                        primaryColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.primaryColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">
                    Cor de fundo (Primária)
                  </Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.primaryColor)}
                </Form.Control.Feedback>
              </Row>

              <Row className="mb-2">
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.primaryColorText ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.primaryColorText}
                    onChangeX={(e) => {
                      setFieldValue("json.primaryColorText", e);
                      setProps((prevState) => ({
                        ...prevState,
                        primaryColorText: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.primaryColorText ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">
                    Cor do texto (Primária)
                  </Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.primaryColorText)}
                </Form.Control.Feedback>
              </Row>

              <Row className="mb-2">
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.secondaryColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.secondaryColor}
                    onChangeX={(e) => {
                      setFieldValue("json.secondaryColor", e);
                      setProps((prevState) => ({
                        ...prevState,
                        secondaryColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.secondaryColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">
                    Cor de fundo (Secundária)
                  </Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.secondaryColor)}
                </Form.Control.Feedback>
              </Row>

              <Row className="mb-2">
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.secondaryColorText ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.secondaryColorText}
                    onChangeX={(e) => {
                      setFieldValue("json.secondaryColorText", e);
                      setProps((prevState) => ({
                        ...prevState,
                        secondaryColorText: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.secondaryColorText ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">
                    Cor do texto (Secundária)
                  </Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.secondaryColorText)}
                </Form.Control.Feedback>
              </Row>
            </Col>
            <Col
              sm={6}
              className={
                errors?.json?.background?.color ||
                  errors?.json?.bodyColorText ||
                  errors?.json?.bodyBackgroundColor ||
                  errors?.json?.primaryColor ||
                  errors?.json?.primaryColorText ||
                  errors?.json?.borderColor ||
                  errors?.json?.navBackgroundColor
                  ? "mb-3 is-invalid"
                  : "mb-3"
              }
            >
              <Row className="mb-2">
                <Form.Label>Barra de navegação</Form.Label>
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.navBackgroundColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.navBackgroundColor}
                    onChangeX={(e) => {
                      setFieldValue("json.navBackgroundColor", e);
                      setProps((prevState) => ({
                        ...prevState,
                        navBackgroundColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.navBackgroundColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor de fundo</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.navBackgroundColor)}
                </Form.Control.Feedback>
              </Row>

              <Row className="mb-2">
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.navPrimaryColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.navPrimaryColor}
                    onChangeX={(e) => {
                      setFieldValue("json.navPrimaryColor", e);
                      setProps((prevState) => ({
                        ...prevState,
                        navPrimaryColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.navPrimaryColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor do texto</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.navPrimaryColor)}
                </Form.Control.Feedback>
              </Row>
            </Col>
          </Row>

          <Row className="mx-0 mb-3">
            <Col
              sm={6}
              className={
                errors?.json?.modalBackgroundColor ||
                  errors?.json?.modalColorText ||
                  errors?.json?.containerColor
                  ? "mb-3 is-invalid"
                  : "mb-3"
              }
            >
              <Row className="mb-2">
                <Form.Label>Modais e Dropdowns</Form.Label>
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.modalBackgroundColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.modalBackgroundColor}
                    onChangeX={(e) => {
                      setFieldValue("json.modalBackgroundColor", e);
                      setProps((prevState) => ({
                        ...prevState,
                        modalBackgroundColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.modalBackgroundColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor de fundo</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.modalBackgroundColor)}
                </Form.Control.Feedback>
              </Row>

              <Row className="mb-2">
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.modalColorText ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.modalColorText}
                    onChangeX={(e) => {
                      setFieldValue("json.modalColorText", e);
                      setProps((prevState) => ({
                        ...prevState,
                        modalColorText: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.modalColorText ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor do texto</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.modalColorText)}
                </Form.Control.Feedback>
              </Row>
            </Col>
          </Row>
        </>
      )}

      {!isSimplifiedCustomization && (
        <>
          <Row className="mx-0">
            <p className="text-start fw-bold mb-2"><FontAwesomeIcon
              icon={faTabletScreenButton}
              className="me-1"
            />Tela de carregamento</p>
            <Form.Group as={Col} md={4}>
              <Form.Label>Duração do carregamento (milissegundos)</Form.Label>
              <Form.Control
                className="mb-3"
                type="number"
                name="json.login.loading.loaderTime"
                step={"1000"}
                value={values.json.login.loading.loaderTime}
                onChange={handleChange}
                isInvalid={!!errors.json?.login?.loading?.loaderTime}
                isValid={
                  touched.json?.login?.loading?.loaderTime &&
                  !errors.json?.login?.loading?.loaderTime
                }
              />
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {errors.json?.login?.loading?.loaderTime}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
          <Row className="mx-0">
            <Form.Group as={Col} md="6" className="mb-3">
              <Row>
                <Form.Group as={Col} md="12">
                  <Form.Label>Logomarca</Form.Label>
                  <Form.Select
                    name="json.login.loading.type"
                    onChange={handleChange}
                    value={values.json.login.loading.type}
                    isInvalid={!!errors.json?.login?.loading?.type}
                    isValid={
                      touched.json?.login?.loading?.type &&
                      !errors.json?.login?.loading?.type
                    }
                  >
                    <option value={"image"}>Imagem</option>
                    <option value={"video"}>Vídeo</option>
                  </Form.Select>
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors.json?.login?.loading?.type}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <LoadingFormComponent
                  touched={touched}
                  errors={errors}
                  values={values}
                  setFieldValue={setFieldValue}
                  handlePreviewSuccess={handlePreviewSuccess}
                  handlePreviewError={handlePreviewError}
                ></LoadingFormComponent>
              </Row>
            </Form.Group>

            <Form.Group as={Col} md="6" className="mb-3">
              <Form.Label>Cor de fundo</Form.Label>
              <div
                className={`d-flex justify-content-start align-items-center ${errors?.json?.login?.loading?.backgroundColor ? "is-invalid" : ""}`}
              >
                <Form.Control
                  type="hidden"
                  name="json.login.loading.backgroundColor"
                  value={values.json?.login?.loading?.backgroundColor}
                  isInvalid={!!errors?.json?.login?.loading?.backgroundColor}
                  isValid={
                    touched?.json?.login?.loading?.backgroundColor &&
                    !errors?.json?.login?.loading?.backgroundColor
                  }
                />
                <PopoverPicker
                  color={props.loadingBackgroundColor}
                  onChangeX={(e) => {
                    setFieldValue("json.login.loading.backgroundColor", e);
                    setProps((prevState) => ({
                      ...prevState,
                      loadingBackgroundColor: e,
                    }));
                  }}
                  classes={`me-1 picker ${errors?.json?.login?.loading?.backgroundColor ? "is-invalid" : ""}`}
                />
              </div>
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {errors?.json?.login?.loading?.backgroundColor
                  ? errors?.json?.login?.loading?.backgroundColor
                  : ""}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          {values.json.login.enableRegister && <Row className="mx-0 mb-3">
            <p className="text-start fw-bold mb-2"><FontAwesomeIcon
              icon={faTabletScreenButton}
              className="me-1"
            />Tela de registro</p>
            <Form.Group className="mb-3" as={Col} sm="12">
              <Form.Label>Título</Form.Label>
              <Form.Control
                as="textarea"
                name="json.form.title.value"
                value={values.json.form.title.value}
                onChange={(e) => {
                  if (e.target.value === "") {
                    setFieldValue("json.form.title.status", false);
                  } else {
                    setFieldValue("json.form.title.status", true);
                  }
                  setFieldValue("json.form.title.value", e.target.value);
                }}
                isInvalid={!!errors?.json?.form?.title?.value}
                isValid={
                  touched?.json?.form?.title?.value &&
                  !errors?.json?.form?.title?.value
                }
              />
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {errors.json?.form?.title?.value}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" as={Col} sm="12">
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                name="json.form.description.value"
                as="textarea"
                value={values.json.form.description.value}
                onChange={(e) => {
                  if (e.target.value === "") {
                    setFieldValue("json.form.description.status", false);
                  } else {
                    setFieldValue("json.form.description.status", true);
                  }
                  setFieldValue("json.form.description.value", e.target.value);
                }}
                isInvalid={!!errors?.json?.form?.description?.value}
                isValid={
                  touched?.json?.form?.description?.value &&
                  !errors?.json?.form?.description?.value
                }
              />
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {errors.json?.form?.description?.value}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} sm="6" className="mb-3">
              <Form.Label>Texto do botão</Form.Label>
              <Form.Control
                name="json.form.buttonText"
                value={values.json.form.buttonText}
                onChange={handleChange}
                isInvalid={!!errors?.json?.form?.buttonText}
                isValid={
                  touched?.json?.form?.buttonText && !errors?.json?.form?.buttonText
                }
              />
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {errors?.json?.form?.buttonText}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>}
        </>
      )}
    </>
  );
};

export const SymplaComponent = ({
  dataTable,
  values,
  touched,
  errors,
  handleChange,
  setFieldValue,
  setDataAddForm,
  dataAddForm,
}) => {
  const { setLoading } = useLayout();
  const notyf = useContext(NotyfContext);
  const [symplaEvents, setSymplaEvents] = useState([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [mappings, setMappings] = useState({}); // { sympla_field_name: form_field_inputID }

  const getFieldInputID = (field) => String(field.inputID);

  const getMappingError = (symplaFieldName) =>
    errors.json?.sympla?.fieldMappings?.[symplaFieldName];

  // Initialise / sync mappings whenever fieldsSympla changes (preserves existing selections)
  useEffect(() => {
    if (dataAddForm.fieldsSympla && dataAddForm.fieldsSympla.length > 0) {
      setMappings((prev) => {
        const next = {};
        dataAddForm.fieldsSympla.forEach((sf) => {
          if (prev[sf.name]) {
            next[sf.name] = prev[sf.name];
          } else {
            const match = dataAddForm.fields.find((f) => f._id === sf.name);
            next[sf.name] = match ? getFieldInputID(match) : "";
          }
        });
        return next;
      });
    }
  }, [dataAddForm.fieldsSympla, dataAddForm.fields]); // eslint-disable-line react-hooks/exhaustive-deps

  const allMapped =
    dataAddForm.fieldsSympla.length > 0 &&
    dataAddForm.fieldsSympla.every(
      (sf) => mappings[sf.name] && mappings[sf.name] !== "",
    );

  const applyMappings = (currentMappings) => {
    setDataAddForm((prev) => ({
      ...prev,
      fields: prev.fields.map((field) => {
        const sympName =
          Object.entries(currentMappings).find(
            ([, v]) => v === getFieldInputID(field),
          )?.[0] ?? "";
        return { ...field, _id: sympName };
      }),
    }));
  };

  const updateMapping = (symplaFieldName, inputID) => {
    const nextMappings = { ...mappings, [symplaFieldName]: inputID };

    setMappings(nextMappings);
    applyMappings(nextMappings);
  };

  const credentialSync = (text, setFieldValue) => {
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => {
        return (
          <ShowConfirm
            title="Confirmar ação"
            description={text}
            action={() => setFieldValue("json.sympla.dataSync", true)}
            onClose={onClose}
          />
        );
      },
    });
  };

  const handleRemoveIntegration = () => {
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => {
        return (
          <ShowConfirm
            title="Confirmar ação"
            description="É recomendado excluir todos os participantes antes de continuar. Deseja desassociar a chave de acesso da API e o evento selecionado?"
            action={() => {
              setFieldValue("json.sympla.id", "");
              setFieldValue("json.sympla.authToken", "");
              setFieldValue("json.sympla.last_update", "");
              setFieldValue("json.sympla.selectedEventId", "");
              setFieldValue("json.sympla.selectedEventName", "");
              setFieldValue("json.sympla.sync_status", "");
              setFieldValue("json.sympla.sync_paused", false);
              setFieldValue("json.sympla.last_error", "");
              setFieldValue("json.sympla.last_error_date", "");
              setSymplaEvents([]);
              setDataAddForm((prev) => ({
                ...prev,
                fieldsSympla: [],
              }));
              onClose();
            }}
            onClose={onClose}
          />
        );
      },
    });
  };

  function symplaIntegration(
    setFieldValue,
    setDataAddForm,
    authToken,
    selectedEventId = null,
    openMappingModal = false,
  ) {
    setLoading(true);
    if (!authToken || authToken === null) {
      setLoading(false);
      notyf.open({
        type: "danger",
        message: "Chave de acesso inválido",
        ripple: true,
        dismissible: true,
      });
      return;
    }
    axios
      .post(`/api/event/plataformaEventos/symplaIntegration`, {
        authToken: authToken,
        event_id: dataTable.event_id,
        selectedEventId: selectedEventId,
      })
      .then(function (response) {
        let data = response.data;
        if (data.status === 201) {
          setFieldValue("json.sympla.authToken", authToken);
          const dateX = new Date();
          const datetime = formatInTimeZone(
            dateX,
            "America/Sao_Paulo",
            "yyyy-MM-dd HH:mm",
          );
          setFieldValue("json.sympla.last_update", datetime);
          setFieldValue("json.sympla.sync_status", "ok");
          setFieldValue("json.sympla.sync_paused", false);
          setFieldValue("json.sympla.last_error", "");
          setFieldValue("json.sympla.last_error_date", "");
          setSymplaEvents(data.events || []);
          if (selectedEventId) {
            // Store custom form fields if available
            if (data.customFormFields && data.customFormFields.length > 0) {
              // Include additional objects
              const additionalFields = [
                { name: "first_name" },
                { name: "last_name" },
              ];

              // Combine custom fields with additional fields
              const allFields = [...additionalFields, ...data.customFormFields];

              // Filter duplicates based on 'name' property
              const uniqueFields = allFields.filter(
                (field, index, self) =>
                  index === self.findIndex((f) => f.name === field.name),
              );

              setDataAddForm((prev) => ({
                ...prev,
                fieldsSympla: uniqueFields,
              }));

              if (openMappingModal) {
                setShowMappingModal(true);
              }
            } else {
              notyf.open({
                type: "danger",
                message:
                  "Nenhum participante encontrado para identificar os ID dos campos dos formulários",
                ripple: true,
                dismissible: true,
              });
            }
          }
          setLoading(false);
        } else {
          setLoading(false);
          notyf.open({
            type: "danger",
            message:
              data.message ||
              "O servidor da Sympla não respondeu corretamente, Verifique a Chave de acesso",
            ripple: true,
            dismissible: true,
          });
        }
      })
      .catch(function (error) {
        setLoading(false);
        notyf.open({
          type: "danger",
          message: error.response?.data?.message
            ? error.response?.data?.message
            : "Houve um erro, tente novamente",
          ripple: true,
          dismissible: true,
        });
      });
  }

  function updateSymplaEvent(selectedEvent) {
    if (!selectedEvent) return;

    setFieldValue("json.sympla.id", selectedEvent.id);
    setFieldValue("json.sympla.selectedEventId", selectedEvent.id);
    setFieldValue("json.sympla.selectedEventName", selectedEvent.name);

    // Fetch participants to get custom form fields
    symplaIntegration(
      setFieldValue,
      setDataAddForm,
      values.json.sympla.authToken,
      selectedEvent.id,
      true, // open mapping modal on first event selection
    );
  }

  return (
    <>
      {(values.json?.sympla?.sync_status === "error" || values.json?.sympla?.sync_paused === true) && (
        <Alert variant="danger" className="mx-3 mb-3 d-flex align-items-center">
          <FontAwesomeIcon icon={faExclamationCircle} className="me-2 flex-shrink-0" size="lg" />
          <div>
            <strong>Falha na sincronização da Sympla: </strong>
            {values.json?.sympla?.last_error || "O token de acesso da Sympla expirou ou é inválido."}
            <div className="small mt-1 text-muted">
              A sincronização periódica foi pausada para evitar requisições com falha. Clique em "Remover" abaixo para atualizar a chave de acesso.
            </div>
          </div>
        </Alert>
      )}
      <Row className="mx-0">
        <Form.Group className="mb-3" as={Col} sm="6">
          <Form.Label>
            Chave de acesso
            {values.json.sympla.last_update &&
              values.json.sympla.last_update !== "" ? (
              <>
                <Button
                  type="button"
                  variant="danger"
                  className="btn-sm ms-2"
                  onClick={handleRemoveIntegration}
                >
                  Remover
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="primary"
                  className="btn-sm ms-2"
                  onClick={() =>
                    symplaIntegration(
                      setFieldValue,
                      setDataAddForm,
                      values.json.sympla.authToken,
                    )
                  }
                >
                  Integrar
                </Button>
              </>
            )}
          </Form.Label>
          <Form.Control
            name="json.sympla.authToken"
            type="text"
            disabled={
              values.json.sympla.last_update &&
                values.json.sympla.last_update !== ""
                ? true
                : false
            }
            value={values.json.sympla.authToken}
            onChange={handleChange}
            isInvalid={!!errors.json?.sympla?.authToken}
            isValid={
              touched.json?.sympla?.authToken && !errors.json?.sympla?.authToken
            }
          />
          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
          <Form.Control.Feedback type="invalid">
            {errors.json?.sympla?.authToken}
          </Form.Control.Feedback>
        </Form.Group>
        {values.json.sympla.last_update &&
          values.json.sympla.last_update !== "" &&
          symplaEvents.length > 0 &&
          !values.json.sympla.selectedEventId && (
            <Form.Group className="mb-3" as={Col} sm="6">
              <Form.Label>Associe um evento</Form.Label>
              <Form.Select
                name="json.sympla.selectedEventId"
                onChange={(e) => {
                  const selectedEvent = symplaEvents.find(
                    (event) => event.id === e.target.value,
                  );
                  updateSymplaEvent(selectedEvent);
                }}
                value={values.json.sympla.selectedEventId || ""}
                isInvalid={!!errors.json?.sympla?.selectedEventId}
                isValid={
                  touched.json?.sympla?.selectedEventId &&
                  !errors.json?.sympla?.selectedEventId
                }
              >
                <option value={""} hidden>
                  Selecione um evento...
                </option>
                {symplaEvents.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.name} ({event.reference_id})
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {errors.json?.sympla?.selectedEventId}
              </Form.Control.Feedback>
            </Form.Group>
          )}
        {values.json.sympla.selectedEventName && (
          <Form.Group className="mb-3" as={Col} sm="6">
            <Form.Label>Evento</Form.Label>
            <Form.Control
              type="text"
              disabled={true}
              value={values.json.sympla.selectedEventName}
            />
          </Form.Group>
        )}
        {values.json.sympla.selectedEventId && dataAddForm.fieldsSympla.length > 0 && (
          <Col sm={12} className="mb-3">
            <p className="text-start mb-2">Associação de campos</p>
            {dataAddForm.fieldsSympla.map((sf) => {
              const mappingError = getMappingError(sf.name);
              const takenInputIDs = Object.entries(mappings)
                .filter(([k]) => k !== sf.name)
                .map(([, v]) => v);
              const available = dataAddForm.fields.filter(
                (f) => !takenInputIDs.includes(getFieldInputID(f)),
              );
              return (
                <Row key={sf.name} className="mb-2 align-items-center">
                  <Col sm={5}>
                    <Form.Control type="text" value={sf.name} disabled />
                  </Col>
                  <Col sm={1} className="text-center px-0">
                    <FontAwesomeIcon icon={faArrowRight} />
                  </Col>
                  <Col sm={6}>
                    <Form.Select
                      name={`json.sympla.fieldMappings.${sf.name}`}
                      value={mappings[sf.name] || ""}
                      onChange={(e) => updateMapping(sf.name, e.target.value)}
                      isInvalid={!mappings[sf.name] || !!mappingError}
                    >
                      <option value="">Selecione um campo...</option>
                      {available.map((f) => (
                        <option key={f.inputID} value={getFieldInputID(f)}>
                          {f.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {mappingError || "Campo obrigatório"}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              );
            })}
            <div className="mt-3">
              <Button
                type="button"
                variant="primary"
                className="btn-sm"
                disabled={!allMapped}
                onClick={() => {
                  applyMappings(mappings);
                  symplaIntegration(
                    setFieldValue,
                    setDataAddForm,
                    values.json.sympla.authToken,
                    values.json.sympla.selectedEventId,
                    false,
                  );
                }}
              >
                Atualizar integração
              </Button>
              {!allMapped && (
                <Form.Text className="text-danger ms-2">
                  Associe todos os campos antes de continuar.
                </Form.Text>
              )}
            </div>
          </Col>
        )}

        {/* Mapping modal — shown on first event selection */}
        <Modal
          show={showMappingModal}
          onHide={() => setShowMappingModal(false)}
          centered
          backdrop="static"
          fullscreen="sm-down"
        >
          <Modal.Header>
            <Modal.Title>Associação de campos Sympla</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-3 text-muted small">
              Associe cada campo do Sympla a um campo do formulário de participantes.
              Cada campo só pode ser usado uma vez.
            </p>
            {dataAddForm.fieldsSympla.map((sf) => {
              const mappingError = getMappingError(sf.name);
              const takenInputIDs = Object.entries(mappings)
                .filter(([k]) => k !== sf.name)
                .map(([, v]) => v);
              const available = dataAddForm.fields.filter(
                (f) => !takenInputIDs.includes(getFieldInputID(f)),
              );
              return (
                <Row key={sf.name} className="mb-3 align-items-center">
                  <Col sm={5}>
                    <Form.Control type="text" value={sf.name} disabled />
                  </Col>
                  <Col sm={1} className="text-center px-0">
                    <FontAwesomeIcon icon={faArrowRight} />
                  </Col>
                  <Col sm={6}>
                    <Form.Select
                      name={`json.sympla.fieldMappings.${sf.name}`}
                      value={mappings[sf.name] || ""}
                      onChange={(e) => updateMapping(sf.name, e.target.value)}
                      isInvalid={!mappings[sf.name] || !!mappingError}
                    >
                      <option value="">Selecione um campo...</option>
                      {available.map((f) => (
                        <option key={f.inputID} value={getFieldInputID(f)}>
                          {f.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {mappingError || "Campo obrigatório"}
                    </Form.Control.Feedback>
                  </Col>
                </Row>
              );
            })}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              disabled={!allMapped}
              onClick={() => {
                applyMappings(mappings);
                setShowMappingModal(false);
              }}
            >
              Confirmar
            </Button>
          </Modal.Footer>
        </Modal>
      </Row>
      <Row className="mx-0 mb-3">
        <Form.Group className="mb-3">
          <Form.Control type="hidden" name="json.sympla.dataSyncOrigin" value="sympla" />
          <Form.Check
            className="mb-0"
            checked={values.json.sympla.dataSync ? true : false}
            type="checkbox"
            name="json.sympla.dataSync"
            label={
              <>
                Sincronização de dados
              </>
            }
            onChange={(e) => {
              if (!e.target.checked) {
                setFieldValue("json.sympla.dataSync", false);
              } else {
                credentialSync(
                  "ATENÇÃO: Participantes registrados no Painel de controle que NÃO existirem no Sympla serão EXCLUÍDOS. Deseja continuar?",
                  setFieldValue,
                );
              }
            }}
          />
          <Form.Text className="text-muted mt-2 d-block">
            Quando ativado, os dados dos participantes são atualizados automaticamente com base no Sympla. Participantes cadastrados no Painel que não existirem no Sympla serão excluídos.
          </Form.Text>
        </Form.Group>
      </Row>
    </>
  );
};

const RegistrationUpdateAccordion = ({
  values,
  touched,
  errors,
  handleChange,
  setFieldValue,
  dataAddForm,
}) => {
  const registrationUpdate =
    values.json.credentialAccess.apresentation.registrationUpdate;
  const checkin = registrationUpdate.checkin;
  const afterCheckin = registrationUpdate.afterCheckin;
  const endPage = registrationUpdate.endPage;
  const basePath = "json.credentialAccess.apresentation.registrationUpdate";
  const checkinPath = `${basePath}.checkin`;
  const afterCheckinPath = `${basePath}.afterCheckin`;
  const endPagePath = `${basePath}.endPage`;
  const fieldOptions = [
    {
      label: values.json.form.emailLabel
        ? values.json.form.emailLabel
        : "E-mail",
      value: "Email",
      inputID: "email",
    },
    ...dataAddForm.fields.map((field) => ({
      label: field.label,
      value: field.label,
      inputID: field.inputID,
    })),
  ];
  const registrationUpdateErrors =
    errors?.json?.credentialAccess?.apresentation?.registrationUpdate;
  const registrationUpdateTouched =
    touched?.json?.credentialAccess?.apresentation?.registrationUpdate;

  return (
    <Accordion.Item eventKey="atualizacao-cadastro" className="mb-2 border rounded">
      <Accordion.Header>
        <div className="d-flex flex-column text-start">
          <span className="fw-semibold">
            <FontAwesomeIcon icon={faUserAlt} className="me-1" /> Atualização de Cadastro
          </span>
        </div>
      </Accordion.Header>
      <Accordion.Body>
        <Row>
          <p className="text-start fw-bold mb-2">
            <FontAwesomeIcon icon={faTabletScreenButton} className="me-1" />
            Tela de check-in
          </p>
          <Form.Group className="mb-3" as={Col} sm="4">
            <Form.Label>Tipo de checkin</Form.Label>
            <Form.Select
              name={`${checkinPath}.type`}
              onChange={(event) => {
                setFieldValue(`${checkinPath}.type`, parseInt(event.target.value, 10));
              }}
              value={checkin.type}
              isInvalid={!!registrationUpdateErrors?.checkin?.type}
              isValid={
                registrationUpdateTouched?.checkin?.type &&
                !registrationUpdateErrors?.checkin?.type
              }
            >
              <option value={1}>Apenas campo de pesquisa</option>
              <option value={2}>Apenas Leitor de Código QR</option>
              <option value={3}>Campo de pesquisa e Leitor de Código QR</option>
              <option value={4}>RFID</option>
            </Form.Select>
            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              {registrationUpdateErrors?.checkin?.type}
            </Form.Control.Feedback>
          </Form.Group>
          {checkin.type === 3 && (
            <Form.Group className="mb-3" as={Col} sm="4">
              <Form.Label>Opção inicial</Form.Label>
              <Form.Select
                name={`${checkinPath}.initialType`}
                onChange={(event) => {
                  setFieldValue(
                    `${checkinPath}.initialType`,
                    parseInt(event.target.value, 10),
                  );
                }}
                value={checkin.initialType}
                isInvalid={!!registrationUpdateErrors?.checkin?.initialType}
                isValid={
                  registrationUpdateTouched?.checkin?.initialType &&
                  !registrationUpdateErrors?.checkin?.initialType
                }
              >
                <option value={1}>Campo de pesquisa</option>
                <option value={2}>Leitor de Código QR</option>
              </Form.Select>
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {registrationUpdateErrors?.checkin?.initialType}
              </Form.Control.Feedback>
            </Form.Group>
          )}
        </Row>

        <Row className="mb-3">
          {checkin.type !== 2 && (
            <Form.Group className="mb-3" as={Col} sm="12">
              <Form.Label>Texto da barra de pesquisa</Form.Label>
              <Form.Control
                as="textarea"
                name={`${checkinPath}.textSelect`}
                value={checkin.textSelect}
                onChange={handleChange}
                isInvalid={!!registrationUpdateErrors?.checkin?.textSelect}
                isValid={
                  registrationUpdateTouched?.checkin?.textSelect &&
                  !registrationUpdateErrors?.checkin?.textSelect
                }
              />
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {registrationUpdateErrors?.checkin?.textSelect}
              </Form.Control.Feedback>
            </Form.Group>
          )}
          {checkin.type !== 1 && (
            <Form.Group className="mb-3" as={Col} sm="12">
              <Form.Label>Texto do Leitor</Form.Label>
              <Form.Control
                as="textarea"
                name={`${checkinPath}.textScan`}
                value={checkin.textScan}
                onChange={handleChange}
                isInvalid={!!registrationUpdateErrors?.checkin?.textScan}
                isValid={
                  registrationUpdateTouched?.checkin?.textScan &&
                  !registrationUpdateErrors?.checkin?.textScan
                }
              />
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {registrationUpdateErrors?.checkin?.textScan}
              </Form.Control.Feedback>
            </Form.Group>
          )}
          {checkin.type !== 2 && (
            <Form.Group
              as={Col}
              md={6}
              className="mb-3"
              data-error-path={`${checkinPath}.fieldsToSearch`}
            >
              <Form.Label>Campos a serem buscados</Form.Label>
              <Select
                isMulti
                name={`${checkinPath}.fieldsToSearch`}
                placeholder="Selecione os campos..."
                value={checkin.fieldsToSearch}
                onChange={(selectedOptions) => {
                  setFieldValue(`${checkinPath}.fieldsToSearch`, selectedOptions);
                }}
                options={fieldOptions}
                className={`basic-multi-select ${registrationUpdateErrors?.checkin?.fieldsToSearch ? "is-invalid" : ""}`}
                classNamePrefix="select"
              />
              {registrationUpdateErrors?.checkin?.fieldsToSearch && (
                <div className="invalid-feedback d-block">
                  {registrationUpdateErrors.checkin.fieldsToSearch}
                </div>
              )}
            </Form.Group>
          )}
          {checkin.type !== 2 && (
            <Form.Group as={Col} md={6} className="mb-3">
              <Form.Label>Campos exibidos no label da busca</Form.Label>
              <Select
                isMulti
                name={`${checkinPath}.fieldsForLabel`}
                placeholder="Padrão: mesmos que os campos buscados..."
                value={checkin.fieldsForLabel || []}
                onChange={(selectedOptions) => {
                  setFieldValue(`${checkinPath}.fieldsForLabel`, selectedOptions);
                }}
                options={fieldOptions}
                className="basic-multi-select"
                classNamePrefix="select"
              />
              <Form.Text className="text-muted">
                Se vazio, usa os mesmos campos da busca.
              </Form.Text>
            </Form.Group>
          )}
        </Row>

        <Row className="mb-3">
          <p className="text-start fw-bold mb-2">
            <FontAwesomeIcon icon={faTabletScreenButton} className="me-1" />
            Tela de confirmação de dados
          </p>

          <Form.Group
            as={Col}
            md={6}
            className="mb-3"
            data-error-path={`${afterCheckinPath}.fieldsToShow`}
          >
            <Form.Label>Campos a serem destacados</Form.Label>
            <Select
              isMulti
              name={`${afterCheckinPath}.fieldsToShow`}
              placeholder="Selecione os campos..."
              value={afterCheckin.fieldsToShow}
              onChange={(selectedOptions) => {
                setFieldValue(`${afterCheckinPath}.fieldsToShow`, selectedOptions);
              }}
              options={fieldOptions}
              className={`basic-multi-select ${registrationUpdateErrors?.afterCheckin?.fieldsToShow ? "is-invalid" : ""}`}
              classNamePrefix="select"
            />
            {registrationUpdateErrors?.afterCheckin?.fieldsToShow && (
              <div className="invalid-feedback d-block">
                {registrationUpdateErrors.afterCheckin.fieldsToShow}
              </div>
            )}
          </Form.Group>
          <Form.Group
            as={Col}
            md={6}
            className="mb-3"
            data-error-path={`${afterCheckinPath}.fieldsToEdit`}
          >
            <Form.Label>Campos a serem editados</Form.Label>
            <Select
              isMulti
              name={`${afterCheckinPath}.fieldsToEdit`}
              placeholder="Selecione os campos..."
              value={afterCheckin.fieldsToEdit || []}
              onChange={(selectedOptions) => {
                setFieldValue(`${afterCheckinPath}.fieldsToEdit`, selectedOptions);
              }}
              options={fieldOptions}
              className={`basic-multi-select ${registrationUpdateErrors?.afterCheckin?.fieldsToEdit ? "is-invalid" : ""}`}
              classNamePrefix="select"
            />
            {registrationUpdateErrors?.afterCheckin?.fieldsToEdit && (
              <div className="invalid-feedback d-block">
                {registrationUpdateErrors.afterCheckin.fieldsToEdit}
              </div>
            )}
          </Form.Group>
        </Row>

        <Row className="mb-3">
          <p className="text-start fw-bold mb-2">
            <FontAwesomeIcon icon={faTabletScreenButton} className="me-1" />
            Tela de redirecionamento
          </p>
          <Form.Group className="mb-3" as={Col} sm="12">
            <Form.Label>Texto</Form.Label>
            <Form.Control
              as="textarea"
              name={`${endPagePath}.text`}
              value={endPage.text}
              onChange={handleChange}
              isInvalid={!!registrationUpdateErrors?.endPage?.text}
              isValid={
                registrationUpdateTouched?.endPage?.text &&
                !registrationUpdateErrors?.endPage?.text
              }
            />
            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              {registrationUpdateErrors?.endPage?.text}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3" as={Col} sm="4">
            <Form.Label>Finalizar fluxo automaticamente (segundos)</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faClock} />
              </InputGroup.Text>
              <Form.Control
                type="number"
                name={`${endPagePath}.timer`}
                value={endPage.timer}
                onChange={handleChange}
                isInvalid={!!registrationUpdateErrors?.endPage?.timer}
                isValid={
                  registrationUpdateTouched?.endPage?.timer &&
                  !registrationUpdateErrors?.endPage?.timer
                }
              />
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {registrationUpdateErrors?.endPage?.timer}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Row>
      </Accordion.Body>
    </Accordion.Item>
  );
};

export const CredentialsComponent = ({
  dataTable,
  values,
  notyf,
  touched,
  errors,
  handleChange,
  setModal,
  setFieldValue,
  setDataAddForm,
  dataAddForm,
  handlePreviewError,
  fonts,
  errorNavigation,
}) => {
  const [props, setProps] = useState({
    primaryColor:
      dataTable.json.credentialAccess.apresentation.textSide.primaryColor,
    primaryColorText:
      dataTable.json.credentialAccess.apresentation.textSide.primaryColorText,
    bodyColorText:
      dataTable.json.credentialAccess.apresentation.textSide.bodyColorText,
    bodyBackgroundColor:
      dataTable.json.credentialAccess.apresentation.textSide
        .bodyBackgroundColor,
    borderColor:
      dataTable.json.credentialAccess.apresentation.textSide.borderColor,
    backgroundColor:
      dataTable.json.credentialAccess.apresentation.textSide.background.color,
    containerColor:
      dataTable.json.credentialAccess.apresentation.textSide.containerColor ?? "#000",
    secondaryColor:
      dataTable.json.credentialAccess.apresentation.textSide.secondaryColor ?? "rgb(108, 117, 125)",
    secondaryColorText:
      dataTable.json.credentialAccess.apresentation.textSide.secondaryColorText ?? "rgba(255, 255, 255, 1)",
    navBackgroundColor:
      dataTable.json.credentialAccess.apresentation.textSide.navBackgroundColor ?? "rgba(255, 255, 255, 1)",
    navPrimaryColor:
      dataTable.json.credentialAccess.apresentation.textSide.navPrimaryColor ?? "#000",
    modalBackgroundColor:
      dataTable.json.credentialAccess.apresentation.textSide.modalBackgroundColor ?? "rgba(255, 255, 255, 1)",
    modalColorText:
      dataTable.json.credentialAccess.apresentation.textSide.modalColorText ?? "rgba(90, 90, 90, 1)",
    registerPrimaryColor:
      dataTable.json.credentialAccess.apresentation.register.primaryColor,
    registerPrimaryColorText:
      dataTable.json.credentialAccess.apresentation.register.primaryColorText,
    registerBackgroundColor:
      dataTable.json.credentialAccess.apresentation.register.background?.color,
    registerTransparency:
      dataTable.json.credentialAccess.apresentation.register.transparency,
    registerBaseColorText:
      dataTable.json.credentialAccess.apresentation.register.baseColorText,
    registerBoxShadow:
      dataTable.json.credentialAccess.apresentation.register.boxShadow,
    registerBsBorderColor:
      dataTable.json.credentialAccess.apresentation.register.bsBorderColor,
  });

  const isSimplifiedCredentialCustomizationEnabled = (personalizationType) =>
    personalizationType ? personalizationType === "simplified" : true;
  const isSimplifiedRegisterCustomizationEnabled = (personalizationType) =>
    personalizationType ? personalizationType === "simplified" : true;

  const [isSimplifiedCredentialCustomization, setIsSimplifiedCredentialCustomization] = useState(
    isSimplifiedCredentialCustomizationEnabled(
      values?.json?.credentialAccess?.apresentation?.personalizationType,
    ),
  );
  const [simplifiedCredentialThemeMode, setSimplifiedCredentialThemeMode] = useState(
    values?.json?.credentialAccess?.apresentation?.simplifiedThemeMode || "light"
  );

  const [isSimplifiedRegisterCustomization, setIsSimplifiedRegisterCustomization] = useState(
    isSimplifiedRegisterCustomizationEnabled(
      values?.json?.credentialAccess?.apresentation?.register?.personalizationType,
    ),
  );
  const [activeRouteAccordion, setActiveRouteAccordion] = useState(null);

  useEffect(() => {
    setIsSimplifiedCredentialCustomization(
      isSimplifiedCredentialCustomizationEnabled(
        values?.json?.credentialAccess?.apresentation?.personalizationType,
      ),
    );
  }, [values?.json?.credentialAccess?.apresentation?.personalizationType]);

  useEffect(() => {
    setSimplifiedCredentialThemeMode(
      values?.json?.credentialAccess?.apresentation?.simplifiedThemeMode || "light",
    );
  }, [values?.json?.credentialAccess?.apresentation?.simplifiedThemeMode]);

  useEffect(() => {
    setIsSimplifiedRegisterCustomization(
      isSimplifiedRegisterCustomizationEnabled(
        values?.json?.credentialAccess?.apresentation?.register?.personalizationType,
      ),
    );
  }, [values?.json?.credentialAccess?.apresentation?.register?.personalizationType]);

  useEffect(() => {
    const errorPath = errorNavigation?.path;
    if (!errorPath) return;

    if (
      errorPath.startsWith(
        "json.credentialAccess.apresentation.registrationUpdate",
      )
    ) {
      setActiveRouteAccordion("atualizacao-cadastro");
    } else if (
      errorPath.startsWith(
        "json.credentialAccess.apresentation.checkin",
      ) ||
      errorPath.startsWith(
        "json.credentialAccess.apresentation.afterCheckin",
      )
    ) {
      setActiveRouteAccordion("checkin");
    } else if (
      errorPath.startsWith(
        "json.credentialAccess.apresentation.register",
      )
    ) {
      setActiveRouteAccordion("formulario-registro");
    }
  }, [errorNavigation]);

  useEffect(() => {
    const presentation = values?.json?.credentialAccess?.apresentation;
    const register = presentation?.register;
    const textSide = presentation?.textSide;
    if (!presentation || !register || !textSide) return;

    setProps((prevState) => ({
      ...prevState,
      primaryColor: textSide.primaryColor,
      primaryColorText: textSide.primaryColorText,
      bodyColorText: textSide.bodyColorText,
      bodyBackgroundColor: textSide.bodyBackgroundColor,
      borderColor: textSide.borderColor,
      backgroundColor: textSide.background?.color,
      containerColor: textSide.containerColor,
      secondaryColor: textSide.secondaryColor,
      secondaryColorText: textSide.secondaryColorText,
      navBackgroundColor: textSide.navBackgroundColor,
      navPrimaryColor: textSide.navPrimaryColor,
      modalBackgroundColor: textSide.modalBackgroundColor,
      modalColorText: textSide.modalColorText,
      registerPrimaryColor: register.primaryColor,
      registerPrimaryColorText: register.primaryColorText,
      registerBackgroundColor: register.background?.color,
      registerTransparency: register.transparency,
      registerBaseColorText: register.baseColorText,
      registerBoxShadow: register.boxShadow,
      registerBsBorderColor: register.bsBorderColor,
    }));
  }, [
    values?.json?.credentialAccess?.apresentation?.textSide?.primaryColor,
    values?.json?.credentialAccess?.apresentation?.textSide?.primaryColorText,
    values?.json?.credentialAccess?.apresentation?.textSide?.bodyColorText,
    values?.json?.credentialAccess?.apresentation?.textSide?.bodyBackgroundColor,
    values?.json?.credentialAccess?.apresentation?.textSide?.borderColor,
    values?.json?.credentialAccess?.apresentation?.textSide?.background?.color,
    values?.json?.credentialAccess?.apresentation?.textSide?.containerColor,
    values?.json?.credentialAccess?.apresentation?.textSide?.secondaryColor,
    values?.json?.credentialAccess?.apresentation?.textSide?.secondaryColorText,
    values?.json?.credentialAccess?.apresentation?.textSide?.navBackgroundColor,
    values?.json?.credentialAccess?.apresentation?.textSide?.navPrimaryColor,
    values?.json?.credentialAccess?.apresentation?.textSide?.modalBackgroundColor,
    values?.json?.credentialAccess?.apresentation?.textSide?.modalColorText,
    values?.json?.credentialAccess?.apresentation?.register?.primaryColor,
    values?.json?.credentialAccess?.apresentation?.register?.primaryColorText,
    values?.json?.credentialAccess?.apresentation?.register?.background?.color,
    values?.json?.credentialAccess?.apresentation?.register?.transparency,
    values?.json?.credentialAccess?.apresentation?.register?.baseColorText,
    values?.json?.credentialAccess?.apresentation?.register?.boxShadow,
    values?.json?.credentialAccess?.apresentation?.register?.bsBorderColor,
  ]);

  const applySimplifiedCredentialTheme = (primaryColor, themeMode) => {
    const contrast = getContrastColor(primaryColor) || "#ffffff";
    setFieldValue("json.credentialAccess.apresentation.textSide.primaryColor", primaryColor);
    setFieldValue("json.credentialAccess.apresentation.textSide.primaryColorText", contrast);
    setFieldValue("json.credentialAccess.apresentation.simplifiedThemeMode", themeMode);
    if (themeMode === "light") {
      setFieldValue("json.credentialAccess.apresentation.textSide.background.color", "rgba(246,248,251,1)");
      setFieldValue("json.credentialAccess.apresentation.textSide.bodyBackgroundColor", "rgba(255, 255, 255, 0.8)");
      setFieldValue("json.credentialAccess.apresentation.textSide.bodyColorText", "rgba(90, 90, 90, 1)");
      setFieldValue("json.credentialAccess.apresentation.textSide.borderColor", "rgb(206, 212, 218)");
      setFieldValue("json.credentialAccess.apresentation.textSide.containerColor", getContrastColor("rgba(246,248,251,1)"));
      setFieldValue("json.credentialAccess.apresentation.textSide.secondaryColor", "rgb(108, 117, 125)");
      setFieldValue("json.credentialAccess.apresentation.textSide.secondaryColorText", "rgba(255, 255, 255, 1)");
      setFieldValue("json.credentialAccess.apresentation.textSide.navBackgroundColor", "rgba(255, 255, 255, 1)");
      setFieldValue("json.credentialAccess.apresentation.textSide.navPrimaryColor", "rgba(90, 90, 90, 1)");
      setFieldValue("json.credentialAccess.apresentation.textSide.modalBackgroundColor", "rgba(255, 255, 255, 1)");
      setFieldValue("json.credentialAccess.apresentation.textSide.modalColorText", "rgba(90, 90, 90, 1)");

      setProps((prevState) => ({
        ...prevState,
        primaryColor: primaryColor,
        primaryColorText: contrast,
        navPrimaryColor: "rgba(90, 90, 90, 1)",
        backgroundColor: "rgba(246,248,251,1)",
        bodyBackgroundColor: "rgba(255, 255, 255, 0.8)",
        navBackgroundColor: "rgba(255, 255, 255, 1)",
        modalBackgroundColor: "rgba(255, 255, 255, 1)",
        containerColor: getContrastColor("rgba(246,248,251,1)"),
        bodyColorText: "rgba(90, 90, 90, 1)",
        modalColorText: "rgba(90, 90, 90, 1)",
        borderColor: "rgb(206, 212, 218)",
        secondaryColor: "rgb(108, 117, 125)",
        secondaryColorText: "rgba(255, 255, 255, 1)"
      }));
    } else {
      setFieldValue("json.credentialAccess.apresentation.textSide.background.color", "rgba(45, 45, 45, 1)");
      setFieldValue("json.credentialAccess.apresentation.textSide.bodyBackgroundColor", "rgba(20, 20, 20, 0.4)");
      setFieldValue("json.credentialAccess.apresentation.textSide.bodyColorText", "rgba(255, 255, 255, 1)");
      setFieldValue("json.credentialAccess.apresentation.textSide.borderColor", "rgb(60, 60, 60)");
      setFieldValue("json.credentialAccess.apresentation.textSide.containerColor", getContrastColor("rgba(45, 45, 45, 1)"));
      setFieldValue("json.credentialAccess.apresentation.textSide.secondaryColor", "rgba(255, 255, 255, 1)");
      setFieldValue("json.credentialAccess.apresentation.textSide.secondaryColorText", "rgb(108, 117, 125)");
      setFieldValue("json.credentialAccess.apresentation.textSide.navBackgroundColor", "rgba(20, 20, 20, 1)");
      setFieldValue("json.credentialAccess.apresentation.textSide.navPrimaryColor", "rgba(255, 255, 255, 1)");
      setFieldValue("json.credentialAccess.apresentation.textSide.modalBackgroundColor", "rgba(45, 45, 45, 1)");
      setFieldValue("json.credentialAccess.apresentation.textSide.modalColorText", "rgba(255, 255, 255, 1)");

      setProps((prevState) => ({
        ...prevState,
        primaryColor: primaryColor,
        primaryColorText: contrast,
        navPrimaryColor: "rgba(255, 255, 255, 1)",
        backgroundColor: "rgba(45, 45, 45, 1)",
        bodyBackgroundColor: "rgba(20, 20, 20, 0.4)",
        navBackgroundColor: "rgba(20, 20, 20, 1)",
        modalBackgroundColor: "rgba(45, 45, 45, 1)",
        containerColor: getContrastColor("rgba(45, 45, 45, 1)"),
        bodyColorText: "rgba(255, 255, 255, 1)",
        modalColorText: "rgba(255, 255, 255, 1)",
        borderColor: "rgb(60, 60, 60)",
        secondaryColor: "rgba(255, 255, 255, 1)",
        secondaryColorText: "rgb(108, 117, 125)"
      }));
    }
  };

  const applyRegisterLayoutStyle = (type) => {
    const isCardLayout = type === 2;
    const p = "json.credentialAccess.apresentation.register";
    const layoutTransparency = isCardLayout
      ? "rgba(255, 255, 255, 1)"
      : "rgba(255, 255, 255, 0)";
    const backgroundColor =
      values?.json?.credentialAccess?.apresentation?.register?.background?.type === "color"
        ? values.json.credentialAccess.apresentation.register.background.color
        : "#ffffff";
    setFieldValue(`${p}.layout.type`, type);
    setFieldValue(`${p}.mainLayout.justifyContent`, "center");
    setFieldValue(`${p}.mainLayout.paddingBox`, "1.50 1.50 1.50 1.50");
    setFieldValue(
      `${p}.paddingBox`,
      isCardLayout
        ? "1.50 1.50 1.50 1.50"
        : "0.00 0.00 0.00 0.00",
    );
    const layoutValues = {
      registerTransparency: layoutTransparency,
      registerBoxShadow: isCardLayout
        ? "rgba(0, 0, 0, 0.15)"
        : "rgba(0, 0, 0, 0)",
      registerBaseColorText:
        getContrastColor(layoutTransparency, backgroundColor) ||
        "rgba(33, 37, 41, 1)",
      registerBsBorderColor: isCardLayout
        ? "rgba(0, 0, 0, 0.1)"
        : "rgba(206, 212, 218, 1)",
    };
    setFieldValue(`${p}.transparency`, layoutValues.registerTransparency);
    setFieldValue(`${p}.boxShadow`, layoutValues.registerBoxShadow);
    setFieldValue(`${p}.baseColorText`, layoutValues.registerBaseColorText);
    setFieldValue(`${p}.bsBorderColor`, layoutValues.registerBsBorderColor);

    setProps((prevState) => ({
      ...prevState,
      ...layoutValues,
    }));
  };

  const applySimplifiedRegisterTheme = (primaryColor) => {
    const contrast = getContrastColor(primaryColor) || "#ffffff";
    const p = "json.credentialAccess.apresentation.register";
    setFieldValue(`${p}.personalizationType`, "simplified");
    setFieldValue(`${p}.primaryColor`, primaryColor);
    setFieldValue(`${p}.primaryColorText`, contrast);
    applyRegisterLayoutStyle(1);

    setProps((prevState) => ({
      ...prevState,
      registerPrimaryColor: primaryColor,
      registerPrimaryColorText: contrast,
    }));
  };

  return (
    <>
      <Row className="mx-0 mb-3">
        <Form.Group className="mb-3" as={Col} sm="12">
          <p className="mb-0">
            <b>
              <FontAwesomeIcon icon={faUserAlt} className="me-1" /> Inscritos:
            </b>{" "}
            {dataTable.credentials.allUsers}
          </p>
          <p className="mb-0">
            <b>
              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />{" "}
              Presentes:
            </b>{" "}
            {dataTable.credentials.onlyWithCheckin}
          </p>
          <p className="mb-0">
            <b>
              <FontAwesomeIcon icon={faTimesCircle} className="me-1" />{" "}
              Ausentes:
            </b>{" "}
            {dataTable.credentials.onlyWithoutCheckin}
          </p>
          <p className="mb-0">
            <b>
              <FontAwesomeIcon icon={faCheckToSlot} className="me-1" /> Total de
              check-ins registrados:
            </b>{" "}
            {dataTable.credentials.allCheckins}
          </p>
        </Form.Group>
        <h6>Área do admin</h6>
        <Form.Group as={Col} sm="4" className="mb-3">
          <Form.Label>Código de acesso</Form.Label>
          <InputGroup>
            <Form.Control
              type="text"
              name="codigo_credenciamento"
              value={values.codigo_credenciamento}
              onChange={handleChange}
              isInvalid={!!errors.codigo_credenciamento}
              isValid={
                touched.codigo_credenciamento && !errors.codigo_credenciamento
              }
            />
            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              {errors.codigo_credenciamento}
            </Form.Control.Feedback>
            <OverlayTrigger
              overlay={<Tooltip>Copiar código de acesso</Tooltip>}
            >
              <InputGroup.Text
                onClick={(e) => {
                  handleShare(values.codigo_credenciamento, notyf);
                }}
              >
                <FontAwesomeIcon icon={faCopy} />
              </InputGroup.Text>
            </OverlayTrigger>
            <OverlayTrigger
              overlay={
                <Tooltip>Gerar novo código</Tooltip>
              }
            >
              <Button
                variant="primary"
                className="btn-sm float-end ms-2"
                onClick={() => {
                  let code = generateSixDigitCode();
                  setFieldValue("codigo_credenciamento", code);
                }}
              >
                <FontAwesomeIcon icon={faRefresh} />
              </Button>
            </OverlayTrigger>
          </InputGroup>
        </Form.Group>
        <div className="w-100"></div>
        <Form.Group as={Col} sm="4" className="mb-3">
          <Form.Label>Código de acesso do monitor</Form.Label>
          <InputGroup>
            <Form.Control
              type="text"
              name="json.credentialAccess.monitorCode"
              value={values.json.credentialAccess.monitorCode || ""}
              onChange={handleChange}
              maxLength={6}
              isInvalid={!!errors?.json?.credentialAccess?.monitorCode}
              isValid={
                touched?.json?.credentialAccess?.monitorCode && !errors?.json?.credentialAccess?.monitorCode
              }
            />
            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              {errors?.json?.credentialAccess?.monitorCode}
            </Form.Control.Feedback>
            <OverlayTrigger
              overlay={<Tooltip>Copiar código de visualização</Tooltip>}
            >
              <InputGroup.Text
                onClick={() => handleShare(values.json.credentialAccess.monitorCode, notyf)}
              >
                <FontAwesomeIcon icon={faCopy} />
              </InputGroup.Text>
            </OverlayTrigger>
            <OverlayTrigger
              overlay={<Tooltip>Gerar novo código</Tooltip>}
            >
              <Button
                variant="primary"
                className="btn-sm float-end ms-2"
                onClick={() => {
                  let code = "";
                  let attempts = 0;
                  do {
                    code = String(generateSixDigitCode()).padStart(6, "0");
                    attempts += 1;
                  } while (code === String(values.codigo_credenciamento ?? "").trim() && attempts < 20);
                  setFieldValue("json.credentialAccess.monitorCode", code);
                }}
              >
                <FontAwesomeIcon icon={faRefresh} />
              </Button>
            </OverlayTrigger>
          </InputGroup>
        </Form.Group>
      </Row>

      <Form.Group as={Col} className="textHeaderAplicativo mb-3 mx-2">
        <h5 className="card-title mb-0">Rotas</h5>
      </Form.Group>
      <Row className="mb-3 mx-0">
        <Accordion className="mb-3"
          activeKey={activeRouteAccordion}
          onSelect={(eventKey) => setActiveRouteAccordion(eventKey)}
        >
          <Accordion.Item eventKey="checkin" className="mb-2 border rounded">
            <Accordion.Header>
              <div className="d-flex flex-column text-start">
                <span className="fw-semibold">
                  <FontAwesomeIcon icon={faCheckToSlot} className="me-1" /> Checkin
                </span>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <Row className="mb-3">
                <p className="text-start fw-bold mb-2"><FontAwesomeIcon
                  icon={faTabletScreenButton}
                  className="me-1"
                />Tela inicial</p>
                <Form.Group className="mb-3" as={Col} sm="12">
                  <Form.Label>Título</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="json.credentialAccess.apresentation.title.value"
                    value={values.json.credentialAccess.apresentation.title.value}
                    onChange={(e) => {
                      if (e.target.value === "") {
                        setFieldValue(
                          "json.credentialAccess.apresentation.title.status",
                          false,
                        );
                      } else {
                        setFieldValue(
                          "json.credentialAccess.apresentation.title.status",
                          true,
                        );
                      }
                      setFieldValue(
                        "json.credentialAccess.apresentation.title.value",
                        e.target.value,
                      );
                    }}
                    isInvalid={
                      !!errors?.json?.credentialAccess?.apresentation?.title?.value
                    }
                    isValid={
                      touched?.json?.credentialAccess?.apresentation?.title?.value &&
                      !errors?.json?.credentialAccess?.apresentation?.title?.value
                    }
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors.json?.credentialAccess?.apresentation?.title?.value}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" as={Col} sm="12">
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="json.credentialAccess.apresentation.description.value"
                    value={values.json.credentialAccess.apresentation.description.value}
                    onChange={(e) => {
                      if (e.target.value === "") {
                        setFieldValue(
                          "json.credentialAccess.apresentation.description.status",
                          false,
                        );
                      } else {
                        setFieldValue(
                          "json.credentialAccess.apresentation.description.status",
                          true,
                        );
                      }
                      setFieldValue(
                        "json.credentialAccess.apresentation.description.value",
                        e.target.value,
                      );
                    }}
                    isInvalid={
                      !!errors?.json?.credentialAccess?.apresentation?.description?.value
                    }
                    isValid={
                      touched?.json?.credentialAccess?.apresentation?.description?.value &&
                      !errors?.json?.credentialAccess?.apresentation?.description?.value
                    }
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors.json?.credentialAccess?.apresentation?.description?.value}
                  </Form.Control.Feedback>
                </Form.Group>


              </Row>

              <Row>
                <p className="text-start fw-bold mb-2"><FontAwesomeIcon
                  icon={faTabletScreenButton}
                  className="me-1"
                />Tela de check-in</p>
                <Form.Group className="mb-3" as={Col} sm="4">
                  <Form.Label>Tipo de checkin</Form.Label>
                  <Form.Select
                    name="json.credentialAccess.apresentation.checkin.type"
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      setFieldValue(
                        "json.credentialAccess.apresentation.checkin.type",
                        value,
                      );
                    }}
                    value={values.json.credentialAccess.apresentation.checkin.type}
                    isInvalid={
                      !!errors.json?.credentialAccess?.apresentation?.checkin?.type
                    }
                    isValid={
                      touched.json?.credentialAccess?.apresentation?.checkin?.type &&
                      !errors.json?.credentialAccess?.apresentation?.checkin?.type
                    }
                  >
                    <option value={1}>Apenas campo de pesquisa</option>
                    <option value={2}>Apenas Leitor de Código QR</option>
                    <option value={3}>Campo de pesquisa e Leitor de Código QR</option>
                    <option value={4}>RFID</option>
                  </Form.Select>
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors.json?.credentialAccess?.apresentation?.checkin?.type}
                  </Form.Control.Feedback>
                </Form.Group>
                {values.json.credentialAccess.apresentation.checkin.type === 3 && (
                  <Form.Group className="mb-3" as={Col} sm="4">
                    <Form.Label>Opção inicial</Form.Label>
                    <Form.Select
                      name="json.credentialAccess.apresentation.checkin.initialType"
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        setFieldValue(
                          "json.credentialAccess.apresentation.checkin.initialType",
                          value,
                        );
                      }}
                      value={
                        values.json.credentialAccess.apresentation.checkin.initialType
                      }
                      isInvalid={
                        !!errors.json?.credentialAccess?.apresentation?.checkin
                          ?.initialType
                      }
                      isValid={
                        touched.json?.credentialAccess?.apresentation?.checkin
                          ?.initialType &&
                        !errors.json?.credentialAccess?.apresentation?.checkin
                          ?.initialType
                      }
                    >
                      <option value={1}>Campo de pesquisa</option>
                      <option value={2}>Leitor de Código QR</option>
                    </Form.Select>
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                      {
                        errors.json?.credentialAccess?.apresentation?.checkin
                          ?.initialType
                      }
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
              </Row>

              <Row className="mb-3">
                {values.json.credentialAccess.apresentation.checkin.type !== 2 && (
                  <Form.Group className="mb-3" as={Col} sm="12">
                    <Form.Label>Texto da barra de pesquisa</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="json.credentialAccess.apresentation.checkin.textSelect"
                      value={
                        values.json.credentialAccess.apresentation.checkin.textSelect
                      }
                      onChange={handleChange}
                      isInvalid={
                        !!errors?.json?.credentialAccess?.apresentation?.checkin
                          ?.textSelect
                      }
                      isValid={
                        touched?.json?.credentialAccess?.apresentation?.checkin
                          ?.textSelect &&
                        !errors?.json?.credentialAccess?.apresentation?.checkin
                          ?.textSelect
                      }
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                      {
                        errors.json?.credentialAccess?.apresentation?.checkin
                          ?.textSelect
                      }
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
                {values.json.credentialAccess.apresentation.checkin.type !== 1 && (
                  <Form.Group className="mb-3" as={Col} sm="12">
                    <Form.Label>Texto do Leitor</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="json.credentialAccess.apresentation.checkin.textScan"
                      value={
                        values.json.credentialAccess.apresentation.checkin.textScan
                      }
                      onChange={handleChange}
                      isInvalid={
                        !!errors?.json?.credentialAccess?.apresentation?.checkin
                          ?.textScan
                      }
                      isValid={
                        touched?.json?.credentialAccess?.apresentation?.checkin
                          ?.textScan &&
                        !errors?.json?.credentialAccess?.apresentation?.checkin
                          ?.textScan
                      }
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                      {errors.json?.credentialAccess?.apresentation?.checkin?.textScan}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
                {values.json.credentialAccess.apresentation.checkin.fieldsToSearch !==
                  2 && (
                    <Form.Group
                      as={Col}
                      md={6}
                      className="mb-3"
                      data-error-path="json.credentialAccess.apresentation.checkin.fieldsToSearch"
                    >
                      <Form.Label>Campos a serem buscados</Form.Label>
                      <Select
                        isMulti
                        name="json.credentialAccess.apresentation.checkin.fieldsToSearch"
                        placeholder="Selecione os campos..."
                        value={
                          values.json.credentialAccess.apresentation.checkin
                            .fieldsToSearch
                        }
                        onChange={(selectedOptions) => {
                          setFieldValue(
                            "json.credentialAccess.apresentation.checkin.fieldsToSearch",
                            selectedOptions,
                          );
                        }}
                        options={[
                          {
                            label: values.json.form.emailLabel
                              ? values.json.form.emailLabel
                              : "E-mail",
                            value: "Email",
                            inputID: "email",
                          },
                          ...dataAddForm.fields.map((field) => ({
                            label: field.label,
                            value: field.label,
                            inputID: field.inputID,
                          })),
                        ]}
                        className={`basic-multi-select ${errors.json?.credentialAccess?.apresentation?.checkin?.fieldsToSearch ? "is-invalid" : ""}`}
                        classNamePrefix="select"
                      />
                      {errors.json?.credentialAccess?.apresentation?.checkin
                        ?.fieldsToSearch && (
                          <div className="invalid-feedback d-block">
                            {
                              errors.json.credentialAccess.apresentation.checkin
                                .fieldsToSearch
                            }
                          </div>
                        )}
                    </Form.Group>
                  )}
                {values.json.credentialAccess.apresentation.checkin.fieldsToSearch !==
                  2 && (
                    <Form.Group as={Col} md={6} className="mb-3">
                      <Form.Label>Campos exibidos no label da busca</Form.Label>
                      <Select
                        isMulti
                        name="fieldsForLabel"
                        placeholder="Padrão: mesmos que os campos buscados..."
                        value={
                          values.json.credentialAccess.apresentation.checkin
                            .fieldsForLabel || []
                        }
                        onChange={(selectedOptions) => {
                          setFieldValue(
                            "json.credentialAccess.apresentation.checkin.fieldsForLabel",
                            selectedOptions,
                          );
                        }}
                        options={[
                          {
                            label: values.json.form.emailLabel
                              ? values.json.form.emailLabel
                              : "E-mail",
                            value: "Email",
                            inputID: "email",
                          },
                          ...dataAddForm.fields.map((field) => ({
                            label: field.label,
                            value: field.label,
                            inputID: field.inputID,
                          })),
                        ]}
                        className="basic-multi-select"
                        classNamePrefix="select"
                      />
                      <Form.Text className="text-muted">
                        Se vazio, usa os mesmos campos da busca.
                      </Form.Text>
                    </Form.Group>
                  )}
                <Form.Group className="mb-3" as={Col} sm="12">
                  <Form.Check
                    checked={values.json.credentialAccess.newRegister ? true : false}
                    type="checkbox"
                    name="json.credentialAccess.newRegister"
                    label="Habilitar registro de novos usuários"
                    onChange={(e) => {
                      setFieldValue(
                        "json.credentialAccess.newRegister",
                        values.json.credentialAccess.newRegister ? false : true,
                      );
                    }}
                  />
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <p className="text-start fw-bold mb-2"><FontAwesomeIcon
                  icon={faTabletScreenButton}
                  className="me-1"
                />Tela de confirmação de dados</p>
                <Form.Group className="mb-3" as={Col} sm="12">
                  <Form.Label>Texto para aviso de Check-in encontrado</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="json.credentialAccess.apresentation.afterCheckin.duplicatedCheckin"
                    value={
                      values.json.credentialAccess.apresentation.afterCheckin
                        .duplicatedCheckin
                    }
                    onChange={handleChange}
                    isInvalid={
                      !!errors?.json?.credentialAccess?.apresentation?.afterCheckin
                        ?.duplicatedCheckin
                    }
                    isValid={
                      touched?.json?.credentialAccess?.apresentation?.afterCheckin
                        ?.duplicatedCheckin &&
                      !errors?.json?.credentialAccess?.apresentation?.afterCheckin
                        ?.duplicatedCheckin
                    }
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {
                      errors.json?.credentialAccess?.apresentation?.afterCheckin
                        ?.duplicatedCheckin
                    }
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  as={Col}
                  md={6}
                  className="mb-3"
                  data-error-path="json.credentialAccess.apresentation.afterCheckin.fieldsToShow"
                >
                  <Form.Label>Campos a serem destacados</Form.Label>
                  <Select
                    isMulti
                    name="json.credentialAccess.apresentation.afterCheckin.fieldsToShow"
                    placeholder="Selecione os campos..."
                    value={
                      values.json.credentialAccess.apresentation.afterCheckin
                        .fieldsToShow
                    }
                    onChange={(selectedOptions) => {
                      setFieldValue(
                        "json.credentialAccess.apresentation.afterCheckin.fieldsToShow",
                        selectedOptions,
                      );
                    }}
                    options={[
                      {
                        label: values.json.form.emailLabel
                          ? values.json.form.emailLabel
                          : "E-mail",
                        value: "Email",
                        inputID: "email",
                      },
                      ...dataAddForm.fields.map((field) => ({
                        label: field.label,
                        value: field.label,
                        inputID: field.inputID,
                      })),
                    ]}
                    className={`basic-multi-select ${errors.json?.credentialAccess?.apresentation?.afterCheckin?.fieldsToShow ? "is-invalid" : ""}`}
                    classNamePrefix="select"
                  />
                  {errors.json?.credentialAccess?.apresentation?.afterCheckin
                    ?.fieldsToShow && (
                      <div className="invalid-feedback d-block">
                        {
                          errors.json.credentialAccess.apresentation.afterCheckin
                            .fieldsToShow
                        }
                      </div>
                    )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    checked={values.json.credentialAccess.checkinEdit ? true : false}
                    type="checkbox"
                    name="json.credentialAccess.checkinEdit"
                    label="Habilitar edição após Check-in"
                    onChange={(e) => {
                      setFieldValue(
                        "json.credentialAccess.checkinEdit",
                        values.json.credentialAccess.checkinEdit ? false : true,
                      );
                    }}
                  />
                  <Form.Check
                    className="mb-2"
                    checked={
                      values.json.credentialAccess.apresentation.afterCheckin.signature
                        ?.status
                        ? true
                        : false
                    }
                    type="checkbox"
                    name="afterCheckin.signature.status"
                    label="Exigir assinatura antes do Check-in"
                    onChange={() => {
                      setFieldValue(
                        "json.credentialAccess.apresentation.afterCheckin.signature.status",
                        values.json.credentialAccess.apresentation.afterCheckin.signature
                          ?.status
                          ? false
                          : true,
                      );
                    }}
                  />
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <p className="text-start fw-bold mb-2">
                  <FontAwesomeIcon
                    icon={faTabletScreenButton}
                    className="me-1"
                  />Tela final de redirecionamento
                </p>
                <Form.Group className="mb-3" as={Col} sm="12">
                  <Form.Label>Texto</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="json.credentialAccess.apresentation.endPage.text"
                    value={values.json.credentialAccess.apresentation.endPage.text}
                    onChange={handleChange}
                    isInvalid={
                      !!errors?.json?.credentialAccess?.apresentation?.endPage?.text
                    }
                    isValid={
                      touched?.json?.credentialAccess?.apresentation?.endPage?.text &&
                      !errors?.json?.credentialAccess?.apresentation?.endPage?.text
                    }
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors.json?.credentialAccess?.apresentation?.endPage?.text}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" as={Col} sm="4">
                  <Form.Label>Finalizar fluxo automaticamente (segundos)</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={faClock} />
                    </InputGroup.Text>
                    <Form.Control
                      type="number"
                      name="json.credentialAccess.apresentation.endPage.timer"
                      value={values.json.credentialAccess.apresentation.endPage.timer}
                      onChange={handleChange}
                      isInvalid={
                        !!errors.json?.credentialAccess?.apresentation?.endPage?.timer
                      }
                      isValid={
                        touched.json?.credentialAccess?.apresentation?.endPage?.timer &&
                        !errors.json?.credentialAccess?.apresentation?.endPage?.timer
                      }
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                      {errors.json?.credentialAccess?.apresentation?.endPage?.timer}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Row>

            </Accordion.Body>
          </Accordion.Item>
          <RegistrationUpdateAccordion
            values={values}
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            setFieldValue={setFieldValue}
            dataAddForm={dataAddForm}
          />
          <Accordion.Item eventKey="formulario-registro" className="mb-2 border rounded">
            <Accordion.Header>
              <div className="d-flex flex-column text-start">
                <span className="fw-semibold">
                  <FontAwesomeIcon icon={faFilePen} className="me-1" /> Formulário de registro
                </span>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <Row className="mb-3">
                <p className="text-start fw-bold mb-2"><FontAwesomeIcon
                  icon={faTabletScreenButton}
                  className="me-1"
                /> Tela de registro</p>
                <Form.Group className="mb-3" as={Col} sm="12">
                  <Form.Label>Título</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="json.credentialAccess.apresentation.register.instructions.value"
                    value={
                      values.json.credentialAccess.apresentation.register.instructions
                        .value
                    }
                    onChange={(e) => {
                      if (e.target.value === "") {
                        setFieldValue(
                          "json.credentialAccess.apresentation.register.instructions.status",
                          false,
                        );
                      } else {
                        setFieldValue(
                          "json.credentialAccess.apresentation.register.instructions.status",
                          true,
                        );
                      }
                      setFieldValue(
                        "json.credentialAccess.apresentation.register.instructions.value",
                        e.target.value,
                      );
                    }}
                    isInvalid={
                      !!errors?.json?.credentialAccess?.apresentation?.register
                        ?.instructions?.value
                    }
                    isValid={
                      touched?.json?.credentialAccess?.apresentation?.register
                        ?.instructions?.value &&
                      !errors?.json?.credentialAccess?.apresentation?.register
                        ?.instructions?.value
                    }
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {
                      errors.json?.credentialAccess?.apresentation?.register
                        ?.instructions?.value
                    }
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" as={Col} sm="12">
                  <Form.Check
                    type="checkbox"
                    name="json.credentialAccess.apresentation.register.profilePhotoEnabled"
                    label="Habilitar foto de perfil no formulário de registro"
                    checked={values.json.credentialAccess.apresentation.register.profilePhotoEnabled === true}
                    onChange={(e) => setFieldValue(
                      "json.credentialAccess.apresentation.register.profilePhotoEnabled",
                      e.target.checked,
                    )}
                  />
                  <Form.Text className="d-block">Quando habilitada, a foto é obrigatória para concluir o registro.</Form.Text>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <p className="text-start fw-bold mb-2"><FontAwesomeIcon
                  icon={faTabletScreenButton}
                  className="me-1"
                /> Tela pós-registro</p>
                <Form.Group className="mb-3" as={Col} sm="12">
                  <Form.Label>Título</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="json.credentialAccess.apresentation.register.title.value"
                    value={
                      values.json.credentialAccess.apresentation.register.title.value
                    }
                    onChange={(e) => {
                      if (e.target.value === "") {
                        setFieldValue(
                          "json.credentialAccess.apresentation.register.title.status",
                          false,
                        );
                      } else {
                        setFieldValue(
                          "json.credentialAccess.apresentation.register.title.status",
                          true,
                        );
                      }
                      setFieldValue(
                        "json.credentialAccess.apresentation.register.title.value",
                        e.target.value,
                      );
                    }}
                    isInvalid={
                      !!errors?.json?.credentialAccess?.apresentation?.register?.title
                        ?.value
                    }
                    isValid={
                      touched?.json?.credentialAccess?.apresentation?.register?.title
                        ?.value &&
                      !errors?.json?.credentialAccess?.apresentation?.register?.title
                        ?.value
                    }
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {
                      errors.json?.credentialAccess?.apresentation?.register?.title
                        ?.value
                    }
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" as={Col} sm="12">
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control
                    name="json.credentialAccess.apresentation.register.description.value"
                    as="textarea"
                    value={
                      values.json.credentialAccess.apresentation.register.description
                        .value
                    }
                    onChange={(e) => {
                      if (e.target.value === "") {
                        setFieldValue(
                          "json.credentialAccess.apresentation.register.description.status",
                          false,
                        );
                      } else {
                        setFieldValue(
                          "json.credentialAccess.apresentation.register.description.status",
                          true,
                        );
                      }
                      setFieldValue(
                        "json.credentialAccess.apresentation.register.description.value",
                        e.target.value,
                      );
                    }}
                    isInvalid={
                      !!errors?.json?.credentialAccess?.apresentation?.register
                        ?.description?.value
                    }
                    isValid={
                      touched?.json?.credentialAccess?.apresentation?.register
                        ?.description?.value &&
                      !errors?.json?.credentialAccess?.apresentation?.register
                        ?.description?.value
                    }
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {
                      errors.json?.credentialAccess?.apresentation?.register
                        ?.description?.value
                    }
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    checked={
                      values.json.credentialAccess.apresentation.register.reset
                        ? true
                        : false
                    }
                    type="checkbox"
                    name="json.credentialAccess.apresentation.register.reset"
                    label="Habilitar botão de voltar para tela de registro"
                    onChange={(e) => {
                      setFieldValue(
                        "json.credentialAccess.apresentation.register.reset",
                        values.json.credentialAccess.apresentation.register.reset
                          ? false
                          : true,
                      );
                    }}
                  />
                  <Form.Check
                    checked={
                      values.json.credentialAccess.apresentation.register.showQRCode
                        ? true
                        : false
                    }
                    type="checkbox"
                    name="json.credentialAccess.apresentation.register.showQRCode"
                    label="Mostrar código QR do participante"
                    onChange={(e) => {
                      setFieldValue(
                        "json.credentialAccess.apresentation.register.showQRCode",
                        values.json.credentialAccess.apresentation.register.showQRCode
                          ? false
                          : true,
                      );
                    }}
                  />
                  <Form.Check
                    checked={
                      values.json.credentialAccess.apresentation.register.showUserData
                        ? true
                        : false
                    }
                    type="checkbox"
                    name="json.credentialAccess.apresentation.register.showUserData"
                    label="Mostrar dados de registro do participante"
                    onChange={(e) => {
                      setFieldValue(
                        "json.credentialAccess.apresentation.register.showUserData",
                        values.json.credentialAccess.apresentation.register.showUserData
                          ? false
                          : true,
                      );
                    }}
                  />
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <p className="text-start fw-bold mt-2 mb-2">
                  <FontAwesomeIcon icon={faDesktop} className="me-1" />Tela de registro encerrado
                  <OverlayTrigger
                    overlay={
                      <Tooltip>
                        O usuário será redirecionado para essa tela em caso se o status
                        da inscrição for "Encerrado"{" "}
                      </Tooltip>
                    }
                  >
                    <HelpTooltip icon={faExclamationCircle} />
                  </OverlayTrigger>
                </p>
                <Form.Group className="mb-3" as={Col} sm="12">
                  <Form.Label>Título</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="json.form.disableFormTitle"
                    value={values.json.form.disableFormTitle}
                    onChange={handleChange}
                    isInvalid={!!errors?.json?.form?.disableFormTitle}
                    isValid={
                      touched?.json?.form?.disableFormTitle &&
                      !errors?.json?.form?.disableFormTitle
                    }
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors?.json?.form?.disableFormTitle}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" as={Col} sm="12">
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="json.form.disableDescription"
                    value={values.json.form.disableDescription}
                    onChange={handleChange}
                    isInvalid={!!errors?.json?.form?.disableDescription}
                    isValid={
                      touched?.json?.form?.disableDescription &&
                      !errors?.json?.form?.disableDescription
                    }
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors?.json?.form?.disableDescription}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row className="mb-3 align-items-center">
                <p className="text-start fw-bold mb-2">Layout e Estilo</p>
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    id="simplified-register-customization-switch"
                    label="Personalização Simplificada"
                    checked={isSimplifiedRegisterCustomization}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      if (isChecked) {
                        confirmAlert({
                          closeOnEscape: false,
                          closeOnClickOutside: false,
                          customUI: ({ onClose }) => {
                            return (
                              <ShowConfirm
                                title="Confirmar ação"
                                description="Habilitar a personalização simplificada substituirá o tema atual para um layout padrão. Deseja continuar?"
                                action={() => {
                                  setIsSimplifiedRegisterCustomization(true);
                                  setFieldValue(
                                    "json.credentialAccess.apresentation.register.personalizationType",
                                    "simplified",
                                  );
                                  applySimplifiedRegisterTheme("rgb(215, 205, 18)");
                                  onClose();
                                }}
                                onClose={() => {
                                  onClose();
                                }}
                              />
                            );
                          },
                        });
                      } else {
                        setIsSimplifiedRegisterCustomization(false);
                        setFieldValue(
                          "json.credentialAccess.apresentation.register.personalizationType",
                          "advanced",
                        );
                      }
                    }}
                  />
                </Form.Group>
              </Row>

              {isSimplifiedRegisterCustomization && (
                <Row className="mb-3">
                  <p className="text-start fw-bold mb-2">Perfil do Formulário</p>
                  <Form.Group as={Col} md="6" className="mb-3">
                    <Form.Label>Estilo de Layout</Form.Label>
                    <Form.Select
                      value={values.json.credentialAccess.apresentation.register.layout?.type ?? 1}
                      onChange={(e) => {
                        const type = parseInt(e.target.value);
                        applyRegisterLayoutStyle(type);
                      }}
                    >
                      <option value={2}>Layout em Cartão (Com Container)</option>
                      <option value={1}>Layout Minimalista (Sem Container)</option>
                    </Form.Select>
                    <Form.Text className="text-muted mt-2 d-block">
                      {(values.json.credentialAccess.apresentation.register.layout?.type ?? 1) === 2
                        ? "O Layout em Cartão é ideal para uso com imagens no plano de fundo. Ele utiliza um container com cor customizável para o conteúdo do formulário."
                        : "O Layout Minimalista exibe o formulário diretamente sobre o plano de fundo, sem container visível."}
                    </Form.Text>
                  </Form.Group>

                  <Form.Group as={Col} md="6" className="mb-3">
                    <Form.Label>
                      Cor primária
                    </Form.Label>
                    <div className="d-flex justify-content-start align-items-center picker">
                      <PopoverPicker
                        color={props.registerPrimaryColor}
                        onChangeX={(e) => {
                          setFieldValue(
                            "json.credentialAccess.apresentation.register.primaryColor",
                            e,
                          );
                          const contrast = getContrastColor(e);
                          if (contrast) {
                            setFieldValue(
                              "json.credentialAccess.apresentation.register.primaryColorText",
                              contrast,
                            );
                          }
                          setProps((prevState) => ({
                            ...prevState,
                            registerPrimaryColor: e,
                            ...(contrast ? { registerPrimaryColorText: contrast } : {}),
                          }));
                        }}
                      />
                    </div>
                  </Form.Group>
                </Row>
              )}

              {!isSimplifiedRegisterCustomization && (
                <>
                  <Row>
                    <p className="text-start fw-bold mb-2">Contêiner externo</p>
                    <Form.Group as={Col} md={4} className="mb-3">
                      <Form.Label>Alinhar verticalmente</Form.Label>
                      <Form.Select
                        name="json.credentialAccess.apresentation.register.mainLayout.justifyContent"
                        value={values.json.credentialAccess.apresentation.register.mainLayout?.justifyContent ?? "center"}
                        onChange={(e) => {
                          setFieldValue(
                            "json.credentialAccess.apresentation.register.mainLayout.justifyContent",
                            e.target.value,
                          );
                        }}
                      >
                        <option value={"flex-start"}>Topo</option>
                        <option value={"center"}>Centro</option>
                        <option value={"flex-end"}>Base</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group as={Col} md={4} className="mb-3">
                      <Form.Label>Margem (rem)</Form.Label>
                      <InputMask
                        mask="9.99 9.99 9.99 9.99"
                        value={values.json.credentialAccess.apresentation.register.mainLayout?.paddingBox ?? "1.50 1.50 1.50 1.50"}
                        onChange={handleChange}
                        isInvalid={!!errors.json?.credentialAccess?.apresentation?.register?.mainLayout?.paddingBox}
                        isValid={
                          touched.json?.credentialAccess?.apresentation?.register?.mainLayout?.paddingBox &&
                          !errors.json?.credentialAccess?.apresentation?.register?.mainLayout?.paddingBox
                        }
                      >
                        {(inputProps) => (
                          <Form.Control
                            {...inputProps}
                            type="text"
                            name="json.credentialAccess.apresentation.register.mainLayout.paddingBox"
                          />
                        )}
                      </InputMask>
                      <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                      <Form.Control.Feedback type="invalid">
                        {errors.json?.credentialAccess?.apresentation?.register?.mainLayout?.paddingBox}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <p className="text-start fw-bold mb-2">Contêiner interno</p>
                    <Form.Group as={Col} md="4" className="mb-3">
                      <Form.Label>Tipo de layout</Form.Label>
                      <Form.Select
                        name="json.credentialAccess.apresentation.register.layout.type"
                        onChange={(e) => {
                          setFieldValue(
                            "json.credentialAccess.apresentation.register.layout.type",
                            e.target.value === "1" ? 1 : 2,
                          );
                        }}
                        value={values.json.credentialAccess.apresentation.register.layout?.type ?? 1}
                        isInvalid={!!errors.json?.credentialAccess?.apresentation?.register?.layout?.type}
                        isValid={
                          touched.json?.credentialAccess?.apresentation?.register?.layout?.type &&
                          !errors.json?.credentialAccess?.apresentation?.register?.layout?.type
                        }
                      >
                        {layoutX.map((element, index) => {
                          return (
                            <option key={"registerLayout" + index} value={parseInt(index + 1)}>
                              {element}
                            </option>
                          );
                        })}
                      </Form.Select>
                      <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                      <Form.Control.Feedback type="invalid">
                        {errors.json?.credentialAccess?.apresentation?.register?.layout?.type}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} md={4} className="mb-3">
                      <Form.Label>Margem (rem)</Form.Label>
                      <InputMask
                        mask="9.99 9.99 9.99 9.99"
                        value={values.json.credentialAccess.apresentation.register.paddingBox ?? "0.00 0.00 0.00 0.00"}
                        onChange={handleChange}
                        isInvalid={!!errors.json?.credentialAccess?.apresentation?.register?.paddingBox}
                        isValid={
                          touched.json?.credentialAccess?.apresentation?.register?.paddingBox &&
                          !errors.json?.credentialAccess?.apresentation?.register?.paddingBox
                        }
                      >
                        {(inputProps) => (
                          <Form.Control
                            {...inputProps}
                            type="text"
                            name="json.credentialAccess.apresentation.register.paddingBox"
                          />
                        )}
                      </InputMask>
                      <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                      <Form.Control.Feedback type="invalid">
                        {errors.json?.credentialAccess?.apresentation?.register?.paddingBox}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row className="mb-3">
                    <p className="text-start fw-bold mb-2">Fonte</p>
                    <Form.Group as={Col} md={4} className="mb-3">
                      <Form.Label>
                        Estilo{" "}
                        <span
                          className="badge text-dark bg-light cursor-pointer ms-1"
                          onClick={() => redirectURL(true, "https://fonts.google.com/")}
                        >
                          Google fonts
                        </span>
                      </Form.Label>
                      <Select
                        name="json.credentialAccess.apresentation.register.fontFamily"
                        options={fonts}
                        value={fonts.find(
                          (option) =>
                            option.value ===
                            (values.json.credentialAccess.apresentation.register.fontFamily ?? "Open Sans"),
                        )}
                        onChange={(selectedOption) => {
                          setFieldValue(
                            "json.credentialAccess.apresentation.register.fontFamily",
                            selectedOption.value,
                          );
                        }}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            padding: 5,
                          }),
                        }}
                      />
                    </Form.Group>
                    <Form.Group as={Col} md={4} className="mb-3">
                      <Form.Label>Tamanho fonte (M) (rem)</Form.Label>
                      <Form.Control
                        type="number"
                        step={"0.01"}
                        name="json.credentialAccess.apresentation.register.h5Size"
                        value={values.json.credentialAccess.apresentation.register.h5Size ?? 1.25}
                        onChange={handleChange}
                        isInvalid={!!errors.json?.credentialAccess?.apresentation?.register?.h5Size}
                        isValid={
                          touched.json?.credentialAccess?.apresentation?.register?.h5Size &&
                          !errors.json?.credentialAccess?.apresentation?.register?.h5Size
                        }
                      />
                      <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                      <Form.Control.Feedback type="invalid">
                        {errors.json?.credentialAccess?.apresentation?.register?.h5Size}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} md={4} className="mb-3">
                      <Form.Label>Tamanho fonte (P) (rem)</Form.Label>
                      <Form.Control
                        type="number"
                        step={"0.01"}
                        name="json.credentialAccess.apresentation.register.pSize"
                        value={values.json.credentialAccess.apresentation.register.pSize ?? 1}
                        onChange={handleChange}
                        isInvalid={!!errors.json?.credentialAccess?.apresentation?.register?.pSize}
                        isValid={
                          touched.json?.credentialAccess?.apresentation?.register?.pSize &&
                          !errors.json?.credentialAccess?.apresentation?.register?.pSize
                        }
                      />
                      <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                      <Form.Control.Feedback type="invalid">
                        {errors.json?.credentialAccess?.apresentation?.register?.pSize}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                </>
              )}

              <Row className="mb-3">
                <p className="text-start fw-bold mb-2">Identidade</p>
                <Form.Group as={Col} md="6">
                  <Form.Label>Logomarca</Form.Label>
                  <Card className="mb-3">
                    <Card.Body>
                      <Form.Control
                        type="hidden"
                        name="json.credentialAccess.apresentation.register.logo"
                        value={values.json.credentialAccess.apresentation.register.logo ?? ""}
                        onChange={handleChange}
                        isInvalid={!!errors?.json?.credentialAccess?.apresentation?.register?.logo}
                        isValid={
                          touched?.json?.credentialAccess?.apresentation?.register?.logo &&
                          !errors?.json?.credentialAccess?.apresentation?.register?.logo
                        }
                      />
                      <div className="imagesUpload">
                        <img
                          className={`h-100 img-thumbnail ${!!errors?.json?.credentialAccess?.apresentation?.register?.logo ? "is-invalid" : ""}`}
                          src={
                            values.json.credentialAccess.apresentation.register.logo !== "" &&
                              values.json.credentialAccess.apresentation.register.logo !== null &&
                              values.json.credentialAccess.apresentation.register.logo !== undefined
                              ? domainConfig.imageServer +
                              "/plataforma-eventos/" +
                              values.event_id +
                              "/" +
                              values.json.credentialAccess.apresentation.register.logo
                              : domainConfig.imageServer + "/empty-background.png"
                          }
                          alt="Imagem preview"
                        />
                        <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                        <Form.Control.Feedback type="invalid">
                          {errors?.json?.credentialAccess?.apresentation?.register?.logo}
                        </Form.Control.Feedback>
                      </div>
                      <DropzoneComponent
                        className="mt-3 dropzoneComp"
                        type="image"
                        handlePreviewSuccess={handlePreviewSuccess}
                        handlePreviewError={handlePreviewError}
                        setFieldValue={setFieldValue}
                        folder="plataforma-eventos"
                        id={values.event_id}
                        field="json.credentialAccess.apresentation.register.logo"
                      />
                      <Form.Group as={Col} className="mt-3">
                        <Form.Label>Largura (px)</Form.Label>
                        <Form.Control
                          type="text"
                          name="json.credentialAccess.apresentation.register.logoWidth"
                          value={values.json.credentialAccess.apresentation.register.logoWidth ?? 225}
                          onChange={handleChange}
                          isInvalid={!!errors.json?.credentialAccess?.apresentation?.register?.logoWidth}
                          isValid={
                            touched.json?.credentialAccess?.apresentation?.register?.logoWidth &&
                            !errors.json?.credentialAccess?.apresentation?.register?.logoWidth
                          }
                        />
                        <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                        <Form.Control.Feedback type="invalid">
                          {errors.json?.credentialAccess?.apresentation?.register?.logoWidth}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Card.Body>
                  </Card>
                </Form.Group>
              </Row>

              {!isSimplifiedRegisterCustomization && (
                <Row className="mb-3">
                  {(values.json.credentialAccess.apresentation.register.layout?.type ?? 1) === 1 && (
                    <Form.Group as={Col} md="4" className="mb-3">
                      <Form.Label>Rodapé</Form.Label>
                      <Form.Select
                        name="json.credentialAccess.apresentation.register.layout.footer.type"
                        onChange={(e) => {
                          if (e.target.value === "noFooter")
                            setFieldValue(
                              "json.credentialAccess.apresentation.register.layout.footer.socialMedia",
                              [],
                            );
                          setFieldValue(
                            "json.credentialAccess.apresentation.register.layout.footer.type",
                            e.target.value,
                          );
                        }}
                        value={values.json.credentialAccess.apresentation.register.layout?.footer?.type ?? "noFooter"}
                        isInvalid={!!errors.json?.credentialAccess?.apresentation?.register?.layout?.footer?.type}
                        isValid={
                          touched.json?.credentialAccess?.apresentation?.register?.layout?.footer?.type &&
                          !errors.json?.credentialAccess?.apresentation?.register?.layout?.footer?.type
                        }
                      >
                        <option value={"noFooter"}>Sem rodapé</option>
                        <option value={"image"}>Imagem</option>
                      </Form.Select>
                      <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                      <Form.Control.Feedback type="invalid">
                        {errors.json?.credentialAccess?.apresentation?.register?.layout?.footer?.type}
                      </Form.Control.Feedback>
                    </Form.Group>
                  )}
                  {(values.json.credentialAccess.apresentation.register.layout?.type ?? 1) === 1 &&
                    values.json.credentialAccess.apresentation.register.layout?.footer?.type === "image" && (
                      <Row className="mb-3 mx-0">
                        <Card className="mb-0 col-md-12">
                          <Card.Body>
                            <Form.Control
                              type="hidden"
                              name="json.credentialAccess.apresentation.register.layout.footer.url"
                              value={values.json.credentialAccess.apresentation.register.layout?.footer?.url ?? ""}
                              onChange={(e) => {
                                setFieldValue(
                                  "json.credentialAccess.apresentation.register.layout.footer.url",
                                  e.target.value,
                                );
                              }}
                              isInvalid={!!errors.json?.credentialAccess?.apresentation?.register?.layout?.footer?.url}
                              isValid={
                                touched.json?.credentialAccess?.apresentation?.register?.layout?.footer?.url &&
                                !errors.json?.credentialAccess?.apresentation?.register?.layout?.footer?.url
                              }
                            />
                            <div className="imagesUpload">
                              <img
                                className={`h-100 img-thumbnail ${!!errors.json?.credentialAccess?.apresentation?.register?.layout?.footer?.url ? "is-invalid" : ""}`}
                                src={
                                  values.json.credentialAccess.apresentation.register.layout?.footer?.url !== "" &&
                                    values.json.credentialAccess.apresentation.register.layout?.footer?.url !== null &&
                                    values.json.credentialAccess.apresentation.register.layout?.footer?.url !== undefined
                                    ? domainConfig.imageServer +
                                    "/plataforma-eventos/" +
                                    values.event_id +
                                    "/" +
                                    values.json.credentialAccess.apresentation.register.layout.footer.url
                                    : domainConfig.imageServer + "/empty-background.png"
                                }
                                alt="Imagem preview"
                              />
                              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                              <Form.Control.Feedback type="invalid">
                                {errors.json?.credentialAccess?.apresentation?.register?.layout?.footer?.url}
                              </Form.Control.Feedback>
                            </div>
                            <DropzoneComponent
                              className="mt-3 dropzoneComp"
                              type="image"
                              handlePreviewSuccess={handlePreviewSuccess}
                              handlePreviewError={handlePreviewError}
                              setFieldValue={setFieldValue}
                              folder="plataforma-eventos"
                              id={values.event_id}
                              field="json.credentialAccess.apresentation.register.layout.footer.url"
                            />
                            <Form.Group as={Col} className="mt-3">
                              <Form.Label>Largura (px)</Form.Label>
                              <Form.Control
                                type="number"
                                name="json.credentialAccess.apresentation.register.layout.footer.width"
                                value={values.json.credentialAccess.apresentation.register.layout?.footer?.width ?? 275}
                                onChange={(e) => {
                                  setFieldValue(
                                    "json.credentialAccess.apresentation.register.layout.footer.width",
                                    e.target.value,
                                  );
                                }}
                                isInvalid={!!errors.json?.credentialAccess?.apresentation?.register?.layout?.footer?.width}
                                isValid={
                                  touched.json?.credentialAccess?.apresentation?.register?.layout?.footer?.width &&
                                  !errors.json?.credentialAccess?.apresentation?.register?.layout?.footer?.width
                                }
                              />
                              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                              <Form.Control.Feedback type="invalid">
                                {errors.json?.credentialAccess?.apresentation?.register?.layout?.footer?.width}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Card.Body>
                        </Card>
                      </Row>
                    )}
                </Row>
              )}

              <Row className="mb-3">
                <p className="text-start fw-bold mb-2">Plano de fundo</p>
                <Form.Group as={Col} md="6">
                  <Form.Label>
                    Tipo
                    {values.json.credentialAccess.apresentation.register.background?.type === "color" && (
                      <OverlayTrigger
                        overlay={
                          <Tooltip>
                            <b>Exemplo: </b>linear-gradient(90deg,rgba(42, 123, 155, 1)
                            0%, rgba(87, 199, 133, 1) 50%, rgba(237, 221, 83, 1) 100%)
                          </Tooltip>
                        }
                      >
                        <span
                          className="badge text-dark bg-light cursor-pointer ms-1"
                          onClick={() => redirectURL(true, "https://cssgradient.io/")}
                        >
                          CSS Gradient
                        </span>
                      </OverlayTrigger>
                    )}
                  </Form.Label>
                  <Form.Select
                    name="json.credentialAccess.apresentation.register.background.type"
                    onChange={handleChange}
                    value={values.json.credentialAccess.apresentation.register.background?.type ?? "color"}
                    isInvalid={!!errors.json?.credentialAccess?.apresentation?.register?.background?.type}
                    isValid={
                      touched.json?.credentialAccess?.apresentation?.register?.background?.type &&
                      !errors.json?.credentialAccess?.apresentation?.register?.background?.type
                    }
                  >
                    <option value={"color"}>Cor</option>
                    <option value={"image"}>Imagem</option>
                    <option value={"video"}>Vídeo</option>
                  </Form.Select>
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors.json?.credentialAccess?.apresentation?.register?.background?.type}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
              {isSimplifiedRegisterCustomization &&
                values.json.credentialAccess.apresentation.register.background?.type !== "color" && (
                  <Row className="mb-3">
                    <SimplifiedTextColorPicker
                      color={
                        props.registerBaseColorText ||
                        values.json.credentialAccess.apresentation.register.baseColorText ||
                        "rgba(33, 37, 41, 1)"
                      }
                      error={
                        errors?.json?.credentialAccess?.apresentation?.register
                          ?.baseColorText
                      }
                      onChange={(e) => {
                        setFieldValue(
                          "json.credentialAccess.apresentation.register.baseColorText",
                          e,
                        );
                        setProps((prevState) => ({
                          ...prevState,
                          registerBaseColorText: e,
                        }));
                      }}
                    />
                  </Row>
                )}
              {values.json.credentialAccess.apresentation.register.background?.type === "image" && (
                <Row className="mb-3">
                  <Form.Group as={Col} md="6" className="mb-3">
                    <Card className="mb-0">
                      <Card.Body>
                        <Form.Control
                          type="hidden"
                          name="json.credentialAccess.apresentation.register.background.url"
                          value={values.json.credentialAccess.apresentation.register.background?.url ?? ""}
                          onChange={(e) => {
                            setFieldValue(
                              "json.credentialAccess.apresentation.register.background.url",
                              e.target.value,
                            );
                          }}
                          isInvalid={!!errors?.json?.credentialAccess?.apresentation?.register?.background?.url}
                          isValid={
                            touched?.json?.credentialAccess?.apresentation?.register?.background?.url &&
                            !errors?.json?.credentialAccess?.apresentation?.register?.background?.url
                          }
                        />
                        <div className="imagesUpload">
                          <img
                            className={`h-100 img-thumbnail ${!!errors?.json?.credentialAccess?.apresentation?.register?.background?.url ? "is-invalid" : ""}`}
                            src={
                              values.json.credentialAccess.apresentation.register.background?.url !== "" &&
                                values.json.credentialAccess.apresentation.register.background?.url !== null &&
                                values.json.credentialAccess.apresentation.register.background?.url !== undefined
                                ? domainConfig.imageServer +
                                "/plataforma-eventos/" +
                                values.event_id +
                                "/" +
                                values.json.credentialAccess.apresentation.register.background.url
                                : domainConfig.imageServer + "/empty-background.png"
                            }
                            alt="Imagem preview"
                          />
                          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            {errors.json?.credentialAccess?.apresentation?.register?.background?.url}
                          </Form.Control.Feedback>
                        </div>
                        <DropzoneComponent
                          className="mt-3 dropzoneComp"
                          type="image"
                          handlePreviewSuccess={handlePreviewSuccess}
                          handlePreviewError={handlePreviewError}
                          setFieldValue={setFieldValue}
                          folder="plataforma-eventos"
                          id={values.event_id}
                          field="json.credentialAccess.apresentation.register.background.url"
                        />
                      </Card.Body>
                    </Card>
                  </Form.Group>
                </Row>
              )}
              {values.json.credentialAccess.apresentation.register.background?.type === "video" && (
                <Row className="mb-3">
                  <Form.Group as={Col} md="6" className="mb-3">
                    <Card className="mb-0">
                      <Card.Body>
                        <Form.Control
                          type="hidden"
                          name="json.credentialAccess.apresentation.register.background.url"
                          value={values.json.credentialAccess.apresentation.register.background?.url ?? ""}
                          onChange={(e) => {
                            setFieldValue(
                              "json.credentialAccess.apresentation.register.background.url",
                              e.target.value,
                            );
                          }}
                          isInvalid={!!errors?.json?.credentialAccess?.apresentation?.register?.background?.url}
                          isValid={
                            touched?.json?.credentialAccess?.apresentation?.register?.background?.url &&
                            !errors?.json?.credentialAccess?.apresentation?.register?.background?.url
                          }
                        />
                        <div className="imagesUpload">
                          <video
                            className={`h-100 img-thumbnail ${!!errors?.json?.credentialAccess?.apresentation?.register?.background?.url ? "is-invalid" : ""}`}
                            src={
                              values.json.credentialAccess.apresentation.register.background?.url !== "" &&
                                values.json.credentialAccess.apresentation.register.background?.url !== null &&
                                values.json.credentialAccess.apresentation.register.background?.url !== undefined
                                ? domainConfig.imageServer +
                                "/plataforma-eventos/" +
                                values.event_id +
                                "/" +
                                values.json.credentialAccess.apresentation.register.background.url
                                : domainConfig.imageServer + "/empty-background.png"
                            }
                            controls={true}
                          />
                          <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                          <Form.Control.Feedback type="invalid">
                            {errors.json?.credentialAccess?.apresentation?.register?.background?.url}
                          </Form.Control.Feedback>
                        </div>
                        <DropzoneComponent
                          className="mt-3 dropzoneComp"
                          type="video"
                          handlePreviewSuccess={handlePreviewSuccess}
                          handlePreviewError={handlePreviewError}
                          setFieldValue={setFieldValue}
                          folder="plataforma-eventos"
                          id={values.event_id}
                          field="json.credentialAccess.apresentation.register.background.url"
                        />
                      </Card.Body>
                    </Card>
                  </Form.Group>
                </Row>
              )}
              {values.json.credentialAccess.apresentation.register.background?.type === "color" && (
                <Row className="mb-3">
                  <Col
                    sm={6}
                    className={
                      errors?.json?.credentialAccess?.apresentation?.register?.background?.color
                        ? "mb-3 is-invalid"
                        : "mb-3"
                    }
                  >
                    <Row>
                      <div
                        className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.register?.background?.color ? "is-invalid" : ""}`}
                      >
                        <PopoverPicker
                          color={props.registerBackgroundColor}
                          onChangeX={(e) => {
                            const registerLayoutType =
                              values.json.credentialAccess.apresentation.register.layout?.type ?? 1;
                            const textColor = registerLayoutType === 1
                              ? getContrastColor(e)
                              : getContrastColor(
                                values.json.credentialAccess.apresentation.register.transparency,
                                e,
                              );
                            setFieldValue(
                              "json.credentialAccess.apresentation.register.background.color",
                              e,
                            );
                            if (isSimplifiedRegisterCustomization && textColor) {
                              setFieldValue(
                                "json.credentialAccess.apresentation.register.baseColorText",
                                textColor,
                              );
                            }
                            setProps((prevState) => ({
                              ...prevState,
                              registerBackgroundColor: e,
                              ...(isSimplifiedRegisterCustomization && textColor
                                ? { registerBaseColorText: textColor }
                                : {}),
                            }));
                          }}
                          classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.register?.background?.color ? "is-invalid" : ""}`}
                        />
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {getFirstError(errors?.json?.credentialAccess?.apresentation?.register?.background?.color)}
                      </Form.Control.Feedback>
                    </Row>
                  </Col>
                </Row>
              )}

              {!isSimplifiedRegisterCustomization && (
                <Row className="mb-3">
                  <p className="text-start fw-bold mb-2">Cores</p>
                  <Col
                    sm={6}
                    className={
                      errors?.json?.credentialAccess?.apresentation?.register?.transparency ||
                        errors?.json?.credentialAccess?.apresentation?.register?.baseColorText ||
                        errors?.json?.credentialAccess?.apresentation?.register?.boxShadow ||
                        errors?.json?.credentialAccess?.apresentation?.register?.bsBorderColor
                        ? "mb-3 is-invalid"
                        : "mb-3"
                    }
                  >
                    <Row className="mb-2">
                      <Form.Label>Cards e Contêiners</Form.Label>
                      <div
                        className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.register?.transparency ? "is-invalid" : ""}`}
                      >
                        <PopoverPicker
                          color={props.registerTransparency}
                          onChangeX={(e) => {
                            setFieldValue(
                              "json.credentialAccess.apresentation.register.transparency",
                              e,
                            );
                            setProps((prevState) => ({
                              ...prevState,
                              registerTransparency: e,
                            }));
                          }}
                          classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.register?.transparency ? "is-invalid" : ""}`}
                        />
                        <Form.Label className="mb-0 ms-2">Cor de fundo</Form.Label>
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {getFirstError(errors?.json?.credentialAccess?.apresentation?.register?.transparency)}
                      </Form.Control.Feedback>
                    </Row>
                    <Row className="mb-2">
                      <div
                        className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.register?.baseColorText ? "is-invalid" : ""}`}
                      >
                        <PopoverPicker
                          color={props.registerBaseColorText}
                          onChangeX={(e) => {
                            setFieldValue(
                              "json.credentialAccess.apresentation.register.baseColorText",
                              e,
                            );
                            setProps((prevState) => ({
                              ...prevState,
                              registerBaseColorText: e,
                            }));
                          }}
                          classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.register?.baseColorText ? "is-invalid" : ""}`}
                        />
                        <Form.Label className="mb-0 ms-2">Cor do texto</Form.Label>
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {getFirstError(errors?.json?.credentialAccess?.apresentation?.register?.baseColorText)}
                      </Form.Control.Feedback>
                    </Row>
                    <Row className="mb-2">
                      <div
                        className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.register?.boxShadow ? "is-invalid" : ""}`}
                      >
                        <PopoverPicker
                          color={props.registerBoxShadow}
                          onChangeX={(e) => {
                            setFieldValue(
                              "json.credentialAccess.apresentation.register.boxShadow",
                              e,
                            );
                            setProps((prevState) => ({
                              ...prevState,
                              registerBoxShadow: e,
                            }));
                          }}
                          classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.register?.boxShadow ? "is-invalid" : ""}`}
                        />
                        <Form.Label className="mb-0 ms-2">Sombra</Form.Label>
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {getFirstError(errors?.json?.credentialAccess?.apresentation?.register?.boxShadow)}
                      </Form.Control.Feedback>
                    </Row>
                    <Row className="mb-2">
                      <div
                        className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.register?.bsBorderColor ? "is-invalid" : ""}`}
                      >
                        <PopoverPicker
                          color={props.registerBsBorderColor}
                          onChangeX={(e) => {
                            setFieldValue(
                              "json.credentialAccess.apresentation.register.bsBorderColor",
                              e,
                            );
                            setProps((prevState) => ({
                              ...prevState,
                              registerBsBorderColor: e,
                            }));
                          }}
                          classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.register?.bsBorderColor ? "is-invalid" : ""}`}
                        />
                        <Form.Label className="mb-0 ms-2">Bordas</Form.Label>
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {getFirstError(errors?.json?.credentialAccess?.apresentation?.register?.bsBorderColor)}
                      </Form.Control.Feedback>
                    </Row>
                  </Col>
                  <Col
                    sm={6}
                    className={
                      errors?.json?.credentialAccess?.apresentation?.register?.primaryColor ||
                        errors?.json?.credentialAccess?.apresentation?.register?.primaryColorText
                        ? "mb-3 is-invalid"
                        : "mb-3"
                    }
                  >
                    <Row className="mb-2">
                      <Form.Label>Botões e elementos destacáveis</Form.Label>
                      <div
                        className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.register?.primaryColor ? "is-invalid" : ""}`}
                      >
                        <PopoverPicker
                          color={props.registerPrimaryColor}
                          onChangeX={(e) => {
                            setFieldValue(
                              "json.credentialAccess.apresentation.register.primaryColor",
                              e,
                            );
                            setProps((prevState) => ({
                              ...prevState,
                              registerPrimaryColor: e,
                            }));
                          }}
                          classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.register?.primaryColor ? "is-invalid" : ""}`}
                        />
                        <Form.Label className="mb-0 ms-2">Cor de fundo</Form.Label>
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {getFirstError(errors?.json?.credentialAccess?.apresentation?.register?.primaryColor)}
                      </Form.Control.Feedback>
                    </Row>
                    <Row className="mb-2">
                      <div
                        className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.register?.primaryColorText ? "is-invalid" : ""}`}
                      >
                        <PopoverPicker
                          color={props.registerPrimaryColorText}
                          onChangeX={(e) => {
                            setFieldValue(
                              "json.credentialAccess.apresentation.register.primaryColorText",
                              e,
                            );
                            setProps((prevState) => ({
                              ...prevState,
                              registerPrimaryColorText: e,
                            }));
                          }}
                          classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.register?.primaryColorText ? "is-invalid" : ""}`}
                        />
                        <Form.Label className="mb-0 ms-2">Cor do texto</Form.Label>
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {getFirstError(errors?.json?.credentialAccess?.apresentation?.register?.primaryColorText)}
                      </Form.Control.Feedback>
                    </Row>
                  </Col>
                </Row>
              )}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </Row>

      <Form.Group as={Col} className="textHeaderAplicativo mb-3 mx-2">
        <h5 className="card-title mb-0">Configurações</h5>
      </Form.Group>
      <Row className="mb-3 mx-0">
        <p className="text-start fw-bold mb-2">Impressora ZPL</p>
        <Form.Label>Modelos de etiqueta</Form.Label>
        <ZPLModelsComponent
          dataAddForm={dataAddForm}
          setDataAddForm={setDataAddForm}
          setModal={setModal}
          touched={touched}
          errors={errors}
          values={values}
          setFieldValue={setFieldValue}
          handlePreviewSuccess={handlePreviewSuccess}
          handlePreviewError={handlePreviewError}
          platform={dataTable.platform}
        ></ZPLModelsComponent>
      </Row>
      <Form.Group as={Col} className="textHeaderAplicativo mb-3 mx-2">
        <h5 className="card-title mb-0">Customização</h5>
      </Form.Group>
      <Row className="mx-0 mb-3 align-items-center">
        <Form.Group>
          <Form.Check
            type="checkbox"
            id="simplified-credential-customization-switch"
            label="Personalização Simplificada"
            checked={isSimplifiedCredentialCustomization}
            onChange={(e) => {
              const isChecked = e.target.checked;
              if (isChecked) {
                confirmAlert({
                  closeOnEscape: false,
                  closeOnClickOutside: false,
                  customUI: ({ onClose }) => {
                    return (
                      <ShowConfirm
                        title="Confirmar ação"
                        description="Habilitar a personalização simplificada substituirá o tema atual para um layout padrão. Deseja continuar?"
                        action={() => {
                          setIsSimplifiedCredentialCustomization(true);
                          setFieldValue("json.credentialAccess.apresentation.personalizationType", "simplified");
                          applySimplifiedCredentialTheme(props.primaryColor, simplifiedCredentialThemeMode);
                          onClose();
                        }}
                        onClose={() => {
                          onClose();
                        }}
                      />
                    );
                  },
                });
              } else {
                setIsSimplifiedCredentialCustomization(false);
                setFieldValue("json.credentialAccess.apresentation.personalizationType", "advanced");
              }
            }}
          />
        </Form.Group>
      </Row>
      {isSimplifiedCredentialCustomization && (
        <>
          <Row className="mx-0 mb-3">
            <p className="text-start fw-bold mb-2">Perfil do Aplicativo</p>
            <Form.Group as={Col} md="6" className="mb-3">
              <Form.Label>Tema</Form.Label>
              <Form.Select
                value={simplifiedCredentialThemeMode}
                onChange={(e) => {
                  const newTheme = e.target.value;
                  setSimplifiedCredentialThemeMode(newTheme);
                  applySimplifiedCredentialTheme(props.primaryColor, newTheme);
                }}
              >
                <option value="light">Modo Claro (Fundo Branco)</option>
                <option value="dark">Modo Escuro (Fundo Preto)</option>
              </Form.Select>
              <Form.Text className="text-muted mt-2 d-block">
                {simplifiedCredentialThemeMode === "light"
                  ? "O Modo Claro apresenta um fundo claro com textos escuros, garantindo alta visibilidade e visual limpo."
                  : "O Modo Escuro utiliza um fundo escuro com textos claros, proporcionando um design moderno e maior conforto visual."}
              </Form.Text>
            </Form.Group>
            <Form.Group as={Col} md="6" className="mb-3">
              <Form.Label>
                Cor primária
              </Form.Label>
              <div className="d-flex justify-content-start align-items-center picker">
                <PopoverPicker
                  color={props.primaryColor}
                  onChangeX={(e) => {
                    setFieldValue("json.credentialAccess.apresentation.textSide.primaryColor", e);
                    const contrast = getContrastColor(e);
                    if (contrast) {
                      setFieldValue(
                        "json.credentialAccess.apresentation.textSide.primaryColorText",
                        contrast,
                      );
                    }
                    setProps((prevState) => ({
                      ...prevState,
                      primaryColor: e,
                      ...(contrast ? { primaryColorText: contrast } : {}),
                    }));
                  }}
                />
              </div>
            </Form.Group>
          </Row>
          <Row className="mx-0 mb-3">
            <p className="text-start fw-bold mb-2">Identidade</p>
            <Form.Group as={Col} md="6" className="mb-3">
              <div>
                <Form.Label>Logomarca</Form.Label>
              </div>
              <Card className="mb-0">
                <Card.Body>
                  <Form.Control
                    type="hidden"
                    name="json.credentialAccess.apresentation.logo"
                    value={values.json.credentialAccess.apresentation.logo}
                    isInvalid={!!errors?.json?.credentialAccess?.apresentation?.logo}
                    isValid={touched?.json?.credentialAccess?.apresentation?.logo && !errors?.json?.credentialAccess?.apresentation?.logo}
                  />
                  <div className="imagesUpload">
                    <img
                      className={`h-100 img-thumbnail ${!!errors?.json?.credentialAccess?.apresentation?.logo ? "is-invalid" : ""}`}
                      src={
                        values.json.credentialAccess.apresentation.logo !== "" &&
                          values.json.credentialAccess.apresentation.logo !== null
                          ? domainConfig.imageServer +
                          "/plataforma-eventos/" +
                          values.event_id +
                          "/" +
                          values.json.credentialAccess.apresentation.logo
                          : domainConfig.imageServer + "/empty-background.png"
                      }
                      alt="Imagem preview"
                    />
                  </div>
                  <DropzoneComponent
                    className="mt-3 dropzoneComp"
                    type="image"
                    handlePreviewSuccess={(fileName) => {
                      if (fileName) {
                        setFieldValue("json.credentialAccess.apresentation.logo", fileName);
                        setFieldValue("json.credentialAccess.apresentation.logoNav", fileName);
                        setFieldValue("json.credentialAccess.apresentation.maskableIcon", fileName);
                        setFieldValue("json.credentialAccess.apresentation.ogImage", fileName);
                      }
                    }}
                    handlePreviewError={handlePreviewError}
                    setFieldValue={setFieldValue}
                    folder="plataforma-eventos"
                    id={values.event_id}
                    field="json.credentialAccess.apresentation.logo"
                  />
                </Card.Body>
              </Card>
            </Form.Group>
          </Row>
        </>
      )}
      {!isSimplifiedCredentialCustomization && (
        <>
          <Row className="mx-0 mb-3">
            <p className="text-start fw-bold mb-2">Fonte</p>
            <Form.Group as={Col} md={4} className="mb-3">
              <Form.Label>
                Estilo{" "}
                <span
                  className="badge text-dark bg-light cursor-pointer ms-1"
                  onClick={() => redirectURL(true, "https://fonts.google.com/")}
                >
                  Google fonts
                </span>
              </Form.Label>
              <Select
                name="json.credentialAccess.apresentation.textSide.fontFamily"
                options={fonts}
                value={fonts.find(
                  (option) =>
                    option.value ===
                    values.json.credentialAccess.apresentation.textSide.fontFamily,
                )}
                onChange={(selectedOption) => {
                  setFieldValue(
                    "json.credentialAccess.apresentation.textSide.fontFamily",
                    selectedOption.value,
                  );
                }}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    padding: 5,
                  }),
                }}
              />
            </Form.Group>
            <Form.Group as={Col} md={4} className="mb-3">
              <Form.Label>Tamanho fonte (M) (rem)</Form.Label>
              <Form.Control
                type="number"
                name="json.credentialAccess.apresentation.textSide.h5"
                step={"0.01"}
                value={values.json.credentialAccess.apresentation?.textSide?.h5}
                onChange={handleChange}
                isInvalid={
                  !!errors.json?.credentialAccess?.apresentation?.textSide?.h5
                }
                isValid={
                  touched.json?.credentialAccess?.apresentation?.textSide?.h5 &&
                  !errors.json?.credentialAccess?.apresentation?.textSide?.h5
                }
              />
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {errors.json?.credentialAccess?.apresentation?.textSide?.h5}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md={4} className="mb-3">
              <Form.Label>Tamanho fonte (P) (rem)</Form.Label>
              <Form.Control
                type="number"
                name="json.credentialAccess.apresentation.textSide.p"
                step={"0.01"}
                value={values.json.credentialAccess.apresentation?.textSide?.p}
                onChange={handleChange}
                isInvalid={
                  !!errors.json?.credentialAccess?.apresentation?.textSide?.p
                }
                isValid={
                  touched.json?.credentialAccess?.apresentation?.textSide?.p &&
                  !errors.json?.credentialAccess?.apresentation?.textSide?.p
                }
              />
              <Form.Control.Feedback type="valid"></Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {errors.json?.credentialAccess?.apresentation?.textSide?.p}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
        </>
      )}

      {!isSimplifiedCredentialCustomization && (
        <Row className="mx-0 mb-3">
          <Form.Group as={Col} md="6" className="mb-3">
            <div>
              <Form.Label>Logomarca</Form.Label>
            </div>
            <Card className="mb-0">
              <Card.Body>
                <Form.Control
                  type="hidden"
                  name="json.credentialAccess.apresentation.logo"
                  value={values.json.credentialAccess.apresentation.logo}
                  onChange={handleChange}
                  isInvalid={
                    !!errors?.json?.credentialAccess?.apresentation?.logo
                  }
                  isValid={
                    touched?.json?.credentialAccess?.apresentation?.logo &&
                    !errors?.json?.credentialAccess?.apresentation?.logo
                  }
                />
                <div className="imagesUpload">
                  <img
                    className={`h-100 img-thumbnail ${!!errors?.json?.credentialAccess?.apresentation?.logo ? "is-invalid" : ""}`}
                    src={
                      values.json.credentialAccess.apresentation.logo !== "" &&
                        values.json.credentialAccess.apresentation.logo !== null
                        ? domainConfig.imageServer +
                        "/plataforma-eventos/" +
                        values.event_id +
                        "/" +
                        values.json.credentialAccess.apresentation.logo
                        : domainConfig.imageServer + "/empty-background.png"
                    }
                    alt="Imagem preview"
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors?.json?.credentialAccess?.apresentation?.logo}
                  </Form.Control.Feedback>
                </div>
                <DropzoneComponent
                  className="mt-3 dropzoneComp"
                  type="image"
                  handlePreviewSuccess={handlePreviewSuccess}
                  handlePreviewError={handlePreviewError}
                  setFieldValue={setFieldValue}
                  folder="plataforma-eventos"
                  id={values.event_id}
                  field="json.credentialAccess.apresentation.logo"
                />
                <Form.Group as={Col} className="mt-3">
                  <Form.Label>Largura (px)</Form.Label>
                  <Form.Control
                    type="text"
                    name="json.credentialAccess.apresentation.logoWidth"
                    value={values.json.credentialAccess.apresentation.logoWidth}
                    onChange={handleChange}
                    isInvalid={
                      !!errors.json?.credentialAccess?.apresentation?.logoWidth
                    }
                    isValid={
                      touched.json?.credentialAccess?.apresentation?.logoWidth &&
                      !errors.json?.credentialAccess?.apresentation?.logoWidth
                    }
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors.json?.credentialAccess?.apresentation?.logoWidth}
                  </Form.Control.Feedback>
                </Form.Group>
              </Card.Body>
            </Card>
          </Form.Group>

          <Form.Group as={Col} md="6" className="mb-3">
            <div>
              <Form.Label>Logomarca (Barra de navegação)</Form.Label>
            </div>
            <Card className="mb-0">
              <Card.Body>
                <Form.Control
                  type="hidden"
                  name="json.credentialAccess.apresentation.logoNav"
                  value={values.json.credentialAccess.apresentation.logoNav ?? ""}
                  onChange={handleChange}
                  isInvalid={!!errors?.json?.credentialAccess?.apresentation?.logoNav}
                  isValid={
                    touched?.json?.credentialAccess?.apresentation?.logoNav &&
                    !errors?.json?.credentialAccess?.apresentation?.logoNav
                  }
                />
                <div className="imagesUpload">
                  <img
                    className={`h-100 img-thumbnail ${!!errors?.json?.credentialAccess?.apresentation?.logoNav ? "is-invalid" : ""}`}
                    src={
                      values.json.credentialAccess.apresentation.logoNav != null &&
                        values.json.credentialAccess.apresentation.logoNav !== ""
                        ? domainConfig.imageServer +
                        "/plataforma-eventos/" +
                        values.event_id +
                        "/" +
                        values.json.credentialAccess.apresentation.logoNav
                        : domainConfig.imageServer + "/empty-background.png"
                    }
                    alt="Imagem preview"
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors?.json?.credentialAccess?.apresentation?.logoNav}
                  </Form.Control.Feedback>
                </div>
                <DropzoneComponent
                  className="mt-3 dropzoneComp"
                  type="image"
                  handlePreviewSuccess={handlePreviewSuccess}
                  handlePreviewError={handlePreviewError}
                  setFieldValue={setFieldValue}
                  folder="plataforma-eventos"
                  id={values.event_id}
                  field="json.credentialAccess.apresentation.logoNav"
                />
              </Card.Body>
            </Card>
          </Form.Group>
          <Form.Group as={Col} md="6" className="mb-3">
            <div>
              <Form.Label>
                Ícone PWA / Maskable (512x512 PNG)
                <OverlayTrigger
                  overlay={<Tooltip>{pwaMaskableImageTooltip}</Tooltip>}
                >
                  <HelpTooltip icon={faExclamationCircle} />
                </OverlayTrigger>
              </Form.Label>
            </div>
            <Card className="mb-0">
              <Card.Body>
                <Form.Control
                  type="hidden"
                  name="json.credentialAccess.apresentation.maskableIcon"
                  value={values.json.credentialAccess?.apresentation?.maskableIcon || ""}
                  onChange={handleChange}
                  isInvalid={
                    !!errors?.json?.credentialAccess?.apresentation?.maskableIcon
                  }
                  isValid={
                    touched?.json?.credentialAccess?.apresentation?.maskableIcon &&
                    !errors?.json?.credentialAccess?.apresentation?.maskableIcon
                  }
                />
                <div className="imagesUpload">
                  <img
                    className={`h-100 img-thumbnail ${!!errors?.json?.credentialAccess?.apresentation?.maskableIcon ? "is-invalid" : ""}`}
                    src={
                      values.json.credentialAccess?.apresentation?.maskableIcon !== "" &&
                        values.json.credentialAccess?.apresentation?.maskableIcon != null
                        ? domainConfig.imageServer +
                        "/plataforma-eventos/" +
                        values.event_id +
                        "/" +
                        values.json.credentialAccess.apresentation.maskableIcon
                        : domainConfig.imageServer + "/empty-background.png"
                    }
                    alt="Imagem preview"
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors?.json?.credentialAccess?.apresentation?.maskableIcon}
                  </Form.Control.Feedback>
                </div>
                <DropzoneComponent
                  className="mt-3 dropzoneComp"
                  type="image"
                  handlePreviewSuccess={handlePreviewSuccess}
                  handlePreviewError={handlePreviewError}
                  setFieldValue={setFieldValue}
                  folder="plataforma-eventos"
                  id={values.event_id}
                  field="json.credentialAccess.apresentation.maskableIcon"
                />
              </Card.Body>
            </Card>
          </Form.Group>

          <Form.Group as={Col} md="6" className="mb-3">
            <div>
              <Form.Label>
                Imagem OG (1200x630)
                <OverlayTrigger
                  overlay={<Tooltip>{ogImageTooltip}</Tooltip>}
                >
                  <HelpTooltip icon={faExclamationCircle} />
                </OverlayTrigger>
              </Form.Label>
            </div>
            <Card className="mb-0">
              <Card.Body>
                <Form.Control
                  type="hidden"
                  name="json.credentialAccess.apresentation.ogImage"
                  value={values.json.credentialAccess?.apresentation?.ogImage || ""}
                  onChange={handleChange}
                  isInvalid={
                    !!errors?.json?.credentialAccess?.apresentation?.ogImage
                  }
                  isValid={
                    touched?.json?.credentialAccess?.apresentation?.ogImage &&
                    !errors?.json?.credentialAccess?.apresentation?.ogImage
                  }
                />
                <div className="imagesUpload">
                  <img
                    className={`h-100 img-thumbnail ${!!errors?.json?.credentialAccess?.apresentation?.ogImage ? "is-invalid" : ""}`}
                    src={
                      values.json.credentialAccess?.apresentation?.ogImage !== "" &&
                        values.json.credentialAccess?.apresentation?.ogImage != null
                        ? domainConfig.imageServer +
                        "/plataforma-eventos/" +
                        values.event_id +
                        "/" +
                        values.json.credentialAccess.apresentation.ogImage
                        : domainConfig.imageServer + "/empty-background.png"
                    }
                    alt="Imagem preview"
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors?.json?.credentialAccess?.apresentation?.ogImage}
                  </Form.Control.Feedback>
                </div>
                <DropzoneComponent
                  className="mt-3 dropzoneComp"
                  type="image"
                  handlePreviewSuccess={handlePreviewSuccess}
                  handlePreviewError={handlePreviewError}
                  setFieldValue={setFieldValue}
                  folder="plataforma-eventos"
                  id={values.event_id}
                  field="json.credentialAccess.apresentation.ogImage"
                />
              </Card.Body>
            </Card>
          </Form.Group>
        </Row>
      )}

      {!isSimplifiedCredentialCustomization && (
        <MetaTagFields
          titleName="json.credentialAccess.apresentation.metaTagTitle"
          titleValue={values.json.credentialAccess.apresentation.metaTagTitle}
          titleTouched={
            touched?.json?.credentialAccess?.apresentation?.metaTagTitle
          }
          titleError={errors?.json?.credentialAccess?.apresentation?.metaTagTitle}
          descriptionName="json.credentialAccess.apresentation.metaTagDescription"
          descriptionValue={
            values.json.credentialAccess.apresentation.metaTagDescription
          }
          descriptionTouched={
            touched?.json?.credentialAccess?.apresentation?.metaTagDescription
          }
          descriptionError={
            errors?.json?.credentialAccess?.apresentation?.metaTagDescription
          }
          handleChange={handleChange}
        />
      )}

      <>
        <Row className="mx-0 mb-3">
          <p className="text-start fw-bold mb-2">Plano de fundo</p>
          <Form.Group as={Col} md="6">
            <Form.Label>
              Tipo
              {values.json.credentialAccess.apresentation.textSide.background
                .type === "color" && (
                  <OverlayTrigger
                    overlay={
                      <Tooltip>
                        <b>Exemplo: </b>linear-gradient(90deg,rgba(42, 123, 155, 1)
                        0%, rgba(87, 199, 133, 1) 50%, rgba(237, 221, 83, 1) 100%)
                      </Tooltip>
                    }
                  >
                    <span
                      className="badge text-dark bg-light cursor-pointer ms-1"
                      onClick={() => redirectURL(true, "https://cssgradient.io/")}
                    >
                      CSS Gradient
                    </span>
                  </OverlayTrigger>
                )}
            </Form.Label>
            <Form.Select
              name="json.credentialAccess.apresentation.textSide.background.type"
              onChange={handleChange}
              value={
                values.json.credentialAccess.apresentation.textSide.background
                  .type
              }
              isInvalid={
                !!errors.json?.credentialAccess?.apresentation?.textSide
                  ?.background?.type
              }
              isValid={
                touched.json?.credentialAccess?.apresentation?.textSide
                  ?.background?.type &&
                !errors.json?.credentialAccess?.apresentation?.textSide
                  ?.background?.type
              }
            >
              <option value={"color"}>Cor</option>
              <option value={"image"}>Imagem</option>
            </Form.Select>
            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              {
                errors.json?.credentialAccess?.apresentation?.textSide?.background
                  ?.type
              }
            </Form.Control.Feedback>
          </Form.Group>
        </Row>

        {values.json.credentialAccess.apresentation.textSide.background.type !==
          "color" && (
            <>
              {isSimplifiedCredentialCustomization && (
                <Row className="mb-3 mx-0">
                  <SimplifiedTextColorPicker
                    color={
                      props.bodyColorText ||
                      values.json.credentialAccess.apresentation.textSide.bodyColorText ||
                      "rgba(255, 255, 255, 1)"
                    }
                    error={
                      errors?.json?.credentialAccess?.apresentation?.textSide
                        ?.bodyColorText
                    }
                    onChange={(e) => {
                      setFieldValue(
                        "json.credentialAccess.apresentation.textSide.bodyColorText",
                        e,
                      );
                      setFieldValue(
                        "json.credentialAccess.apresentation.textSide.containerColor",
                        e,
                      );
                      setProps((prevState) => ({
                        ...prevState,
                        bodyColorText: e,
                        containerColor: e,
                      }));
                    }}
                  />
                </Row>
              )}
              <Row className="mb-3 mx-0">
                <Form.Group as={Col} md="6">
                  <BackgroundCredentialsFormComponent
                    touched={touched}
                    errors={errors}
                    values={values}
                    setFieldValue={setFieldValue}
                    handlePreviewSuccess={handlePreviewSuccess}
                    handlePreviewError={handlePreviewError}
                    platform={dataTable.platform}
                  ></BackgroundCredentialsFormComponent>
                </Form.Group>
              </Row>
            </>
          )}
        {values.json.credentialAccess.apresentation.textSide.background.type ===
          "color" && (
            <Row className="mx-0 mb-3">
              <Col
                sm={6}
                className={
                  errors?.json?.credentialAccess?.apresentation?.textSide
                    ?.background?.color ||
                    errors?.json?.credentialAccess?.apresentation?.textSide
                      ?.bodyColorText ||
                    errors?.json?.credentialAccess?.apresentation?.textSide
                      ?.bodyBackgroundColor
                    ? "mb-3 is-invalid"
                    : "mb-3"
                }
              >
                <Row className="mb-2">
                  <div
                    className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.textSide?.background?.color ? "is-invalid" : ""}`}
                  >
                    <PopoverPicker
                      color={props.backgroundColor}
                      onChangeX={(e) => {
                        const contrast = getContrastColor(e);
                        setFieldValue(
                          "json.credentialAccess.apresentation.textSide.background.color",
                          e,
                        );
                        if (isSimplifiedCredentialCustomization && contrast) {
                          setFieldValue(
                            "json.credentialAccess.apresentation.textSide.bodyColorText",
                            contrast,
                          );
                          setFieldValue(
                            "json.credentialAccess.apresentation.textSide.containerColor",
                            contrast,
                          );
                        }
                        setProps((prevState) => ({
                          ...prevState,
                          backgroundColor: e,
                          ...(isSimplifiedCredentialCustomization && contrast
                            ? {
                              bodyColorText: contrast,
                              containerColor: contrast,
                            }
                            : {}),
                        }));
                      }}
                      classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.textSide?.background?.color ? "is-invalid" : ""}`}
                    />
                  </div>
                  <Form.Control.Feedback type="invalid">
                    {getFirstError(
                      errors?.json?.credentialAccess?.apresentation?.textSide
                        ?.background?.color, // Add more variables as needed
                    )}
                  </Form.Control.Feedback>
                </Row>
              </Col>
            </Row>
          )}
      </>

      {!isSimplifiedCredentialCustomization && (
        <>
          <Row className="mx-0 my-3">
            <p className="text-start fw-bold mb-2">Cores</p>
            <Col
              sm={6}
              className={
                errors?.json?.credentialAccess?.apresentation?.textSide?.containerColor
                  ? "mb-3 is-invalid"
                  : "mb-3"
              }
            >
              <Row className="mb-2">
                <Form.Label>Contêineres</Form.Label>
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.textSide?.containerColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.containerColor}
                    onChangeX={(e) => {
                      setFieldValue("json.credentialAccess.apresentation.textSide.containerColor", e);
                      setProps((prevState) => ({
                        ...prevState,
                        containerColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.textSide?.containerColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor do texto</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.credentialAccess?.apresentation?.textSide?.containerColor)}
                </Form.Control.Feedback>
              </Row>
            </Col>

            <Col
              sm={6}
              className={
                errors?.json?.credentialAccess?.apresentation?.textSide?.background
                  ?.color ||
                  errors?.json?.credentialAccess?.apresentation?.textSide
                    ?.bodyColorText ||
                  errors?.json?.credentialAccess?.apresentation?.textSide
                    ?.bodyBackgroundColor
                  ? "mb-3 is-invalid"
                  : "mb-3"
              }
            >
              <Row className="mb-2">
                <Form.Label>Cards</Form.Label>
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.textSide?.bodyBackgroundColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.bodyBackgroundColor}
                    onChangeX={(e) => {
                      setFieldValue(
                        "json.credentialAccess.apresentation.textSide.bodyBackgroundColor",
                        e,
                      );
                      setProps((prevState) => ({
                        ...prevState,
                        bodyBackgroundColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.textSide?.bodyBackgroundColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor de fundo</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(
                    errors?.json?.credentialAccess?.apresentation?.textSide
                      ?.bodyBackgroundColor,
                  )}
                </Form.Control.Feedback>
              </Row>

              <Row className="mb-2">
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.textSide?.bodyColorText ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.bodyColorText}
                    onChangeX={(e) => {
                      setFieldValue(
                        "json.credentialAccess.apresentation.textSide.bodyColorText",
                        e,
                      );
                      setProps((prevState) => ({
                        ...prevState,
                        bodyColorText: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.textSide?.bodyColorText ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor do texto</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(
                    errors?.json?.credentialAccess?.apresentation?.textSide
                      ?.bodyColorText,
                  )}
                </Form.Control.Feedback>
              </Row>

              <Row className="mb-2">
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.textSide?.borderColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.borderColor}
                    onChangeX={(e) => {
                      setFieldValue(
                        "json.credentialAccess.apresentation.textSide.borderColor",
                        e,
                      );
                      setProps((prevState) => ({
                        ...prevState,
                        borderColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.textSide?.borderColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Bordas</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(
                    errors?.json?.credentialAccess?.apresentation?.textSide
                      ?.borderColor,
                  )}
                </Form.Control.Feedback>
              </Row>
            </Col>
            <Col
              sm={6}
              className={
                errors?.json?.credentialAccess?.apresentation?.textSide
                  ?.primaryColor ||
                  errors?.json?.credentialAccess?.apresentation?.textSide
                    ?.primaryColorText ||
                  errors?.json?.credentialAccess?.apresentation?.textSide?.secondaryColor ||
                  errors?.json?.credentialAccess?.apresentation?.textSide?.secondaryColorText
                  ? "mb-3 is-invalid"
                  : "mb-3"
              }
            >
              <Row className="mb-2">
                <Form.Label>Botões e elementos destacáveis</Form.Label>
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.textSide?.primaryColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.primaryColor}
                    onChangeX={(e) => {
                      setFieldValue(
                        "json.credentialAccess.apresentation.textSide.primaryColor",
                        e,
                      );
                      setProps((prevState) => ({
                        ...prevState,
                        primaryColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.textSide?.primaryColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">
                    Cor de fundo (Primária)
                  </Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(
                    errors?.json?.credentialAccess?.apresentation?.textSide
                      ?.primaryColor,
                  )}
                </Form.Control.Feedback>
              </Row>

              <Row className="mb-2">
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.textSide?.primaryColorText ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.primaryColorText}
                    onChangeX={(e) => {
                      setFieldValue(
                        "json.credentialAccess.apresentation.textSide.primaryColorText",
                        e,
                      );
                      setProps((prevState) => ({
                        ...prevState,
                        primaryColorText: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.textSide?.primaryColorText ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">
                    Cor do texto (Primária)
                  </Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(
                    errors?.json?.credentialAccess?.apresentation?.textSide
                      ?.primaryColorText,
                  )}
                </Form.Control.Feedback>
              </Row>

              <Row className="mb-2">
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.textSide?.secondaryColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.secondaryColor}
                    onChangeX={(e) => {
                      setFieldValue("json.credentialAccess.apresentation.textSide.secondaryColor", e);
                      setProps((prevState) => ({
                        ...prevState,
                        secondaryColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.textSide?.secondaryColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">
                    Cor de fundo (Secundária)
                  </Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.credentialAccess?.apresentation?.textSide?.secondaryColor)}
                </Form.Control.Feedback>
              </Row>

              <Row className="mb-2">
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.textSide?.secondaryColorText ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.secondaryColorText}
                    onChangeX={(e) => {
                      setFieldValue("json.credentialAccess.apresentation.textSide.secondaryColorText", e);
                      setProps((prevState) => ({
                        ...prevState,
                        secondaryColorText: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.textSide?.secondaryColorText ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">
                    Cor do texto (Secundária)
                  </Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.credentialAccess?.apresentation?.textSide?.secondaryColorText)}
                </Form.Control.Feedback>
              </Row>
            </Col>
            <Col
              sm={6}
              className={
                errors?.json?.credentialAccess?.apresentation?.textSide?.navBackgroundColor ||
                  errors?.json?.credentialAccess?.apresentation?.textSide?.navPrimaryColor
                  ? "mb-3 is-invalid"
                  : "mb-3"
              }
            >
              <Row className="mb-2">
                <Form.Label>Barra de navegação</Form.Label>
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.textSide?.navBackgroundColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.navBackgroundColor}
                    onChangeX={(e) => {
                      setFieldValue("json.credentialAccess.apresentation.textSide.navBackgroundColor", e);
                      setProps((prevState) => ({
                        ...prevState,
                        navBackgroundColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.textSide?.navBackgroundColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor de fundo</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.credentialAccess?.apresentation?.textSide?.navBackgroundColor)}
                </Form.Control.Feedback>
              </Row>

              <Row className="mb-2">
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.textSide?.navPrimaryColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.navPrimaryColor}
                    onChangeX={(e) => {
                      setFieldValue("json.credentialAccess.apresentation.textSide.navPrimaryColor", e);
                      setProps((prevState) => ({
                        ...prevState,
                        navPrimaryColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.textSide?.navPrimaryColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor do texto</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.credentialAccess?.apresentation?.textSide?.navPrimaryColor)}
                </Form.Control.Feedback>
              </Row>
            </Col>
          </Row>
          <Row className="mx-0 mb-3">
            <Col
              sm={6}
              className={
                errors?.json?.credentialAccess?.apresentation?.textSide?.modalBackgroundColor ||
                  errors?.json?.credentialAccess?.apresentation?.textSide?.modalColorText
                  ? "mb-3 is-invalid"
                  : "mb-3"
              }
            >
              <Row className="mb-2">
                <Form.Label>Modais e Dropdowns</Form.Label>
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.textSide?.modalBackgroundColor ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.modalBackgroundColor}
                    onChangeX={(e) => {
                      setFieldValue("json.credentialAccess.apresentation.textSide.modalBackgroundColor", e);
                      setProps((prevState) => ({
                        ...prevState,
                        modalBackgroundColor: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.textSide?.modalBackgroundColor ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor de fundo</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.credentialAccess?.apresentation?.textSide?.modalBackgroundColor)}
                </Form.Control.Feedback>
              </Row>
              <Row className="mb-2">
                <div
                  className={`d-flex justify-content-start align-items-center ${errors?.json?.credentialAccess?.apresentation?.textSide?.modalColorText ? "is-invalid" : ""}`}
                >
                  <PopoverPicker
                    color={props.modalColorText}
                    onChangeX={(e) => {
                      setFieldValue("json.credentialAccess.apresentation.textSide.modalColorText", e);
                      setProps((prevState) => ({
                        ...prevState,
                        modalColorText: e,
                      }));
                    }}
                    classes={`me-1 picker ${errors?.json?.credentialAccess?.apresentation?.textSide?.modalColorText ? "is-invalid" : ""}`}
                  />
                  <Form.Label className="mb-0 ms-2">Cor do texto</Form.Label>
                </div>
                <Form.Control.Feedback type="invalid">
                  {getFirstError(errors?.json?.credentialAccess?.apresentation?.textSide?.modalColorText)}
                </Form.Control.Feedback>
              </Row>
            </Col>
          </Row>

          <Row className="mx-0 mb-3">
            <Form.Group as={Col} md="6">
              <Form.Label>Tipo de layout</Form.Label>
              <Form.Select
                onChange={(e) => {
                  if (e.target.value === "2") {
                    setFieldValue(
                      "json.credentialAccess.apresentation.imageSide.value",
                      40,
                    );
                    setFieldValue(
                      "json.credentialAccess.apresentation.textSide.value",
                      60,
                    );
                  } else {
                    setFieldValue(
                      "json.credentialAccess.apresentation.imageSide.value",
                      0,
                    );
                    setFieldValue(
                      "json.credentialAccess.apresentation.textSide.value",
                      100,
                    );
                  }
                }}
                value={
                  values.json.credentialAccess.apresentation?.imageSide?.value &&
                    values.json.credentialAccess.apresentation?.imageSide?.value > 0
                    ? 2
                    : 1
                }
              >
                <option value={1}>Simples</option>
                <option value={2}>Com banner</option>
              </Form.Select>
            </Form.Group>
          </Row>
          {values.json.credentialAccess.apresentation.imageSide?.value > 0 && (
            <>
              <Row className="mx-0">
                <Form.Group as={Col} md="3" className="mb-3">
                  <Form.Label>Tamanho do banner (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="json.credentialAccess.apresentation.imageSide.value"
                    value={
                      values.json.credentialAccess.apresentation?.imageSide?.value
                    }
                    onChange={(e) => {
                      if (e.target.value === undefined || e.target.value === "") {
                        e.target.value = 0;
                      }
                      let textSideValue = 100 - e.target.value;
                      setFieldValue(
                        "json.credentialAccess.apresentation.imageSide.value",
                        e.target.value,
                      );
                      setFieldValue(
                        "json.credentialAccess.apresentation.textSide.value",
                        textSideValue,
                      );
                    }}
                    isInvalid={
                      !!errors.json?.credentialAccess?.apresentation?.imageSide
                        ?.value
                    }
                    isValid={
                      touched.json?.credentialAccess?.apresentation?.imageSide
                        ?.value &&
                      !errors.json?.credentialAccess?.apresentation?.imageSide
                        ?.value
                    }
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {errors.json?.credentialAccess?.apresentation?.imageSide?.value}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} md="3" className="mb-3">
                  <Form.Label>Posição</Form.Label>
                  <Form.Select
                    onChange={handleChange}
                    name="json.credentialAccess.apresentation.imageSide.position"
                    value={
                      values.json.credentialAccess.apresentation.imageSide.position
                    }
                  >
                    <option value={"start"}>Esquerda</option>
                    <option value={"end"}>Direita</option>
                  </Form.Select>
                </Form.Group>
              </Row>
              <Row className="mx-0 mb-3">
                <Form.Group as={Col} md="6">
                  <Card className="mb-3">
                    <Card.Body>
                      <div className="text-center mb-3">
                        <div className="d-flex justify-content-center mb-3">
                          <Form.Label>Banner</Form.Label>
                        </div>
                      </div>
                      <Form.Control
                        type="hidden"
                        name="json.credentialAccess.apresentation.imageSide.background"
                        value={
                          values.json.credentialAccess.apresentation?.imageSide
                            ?.background
                        }
                        onChange={handleChange}
                        isInvalid={
                          !!errors?.json?.credentialAccess?.apresentation?.imageSide
                            ?.background
                        }
                        isValid={
                          touched?.json?.credentialAccess?.apresentation?.imageSide
                            ?.background &&
                          !errors?.json?.credentialAccess?.apresentation?.imageSide
                            ?.background
                        }
                      />
                      <div className="imagesUpload">
                        <img
                          className={`h-100 img-thumbnail ${!!errors?.json?.credentialAccess?.apresentation?.imageSide?.background ? "is-invalid" : ""}`}
                          src={
                            values.json.credentialAccess.apresentation?.imageSide
                              ?.background !== "" &&
                              values.json.credentialAccess.apresentation?.imageSide
                                ?.background !== null
                              ? domainConfig.imageServer +
                              "/plataforma-eventos/" +
                              values.event_id +
                              "/" +
                              values.json.credentialAccess.apresentation
                                ?.imageSide?.background
                              : domainConfig.imageServer + "/empty-background.png"
                          }
                          alt="ImagemBase"
                        />
                        <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                        <Form.Control.Feedback type="invalid">
                          {
                            errors?.json?.credentialAccess?.apresentation?.imageSide
                              ?.background
                          }
                        </Form.Control.Feedback>
                      </div>
                      <DropzoneComponent
                        className="mt-3 dropzoneComp"
                        type="image"
                        handlePreviewSuccess={handlePreviewSuccess}
                        handlePreviewError={handlePreviewError}
                        setFieldValue={setFieldValue}
                        folder="plataforma-eventos"
                        id={values.event_id}
                        field="json.credentialAccess.apresentation.imageSide.background"
                      />
                    </Card.Body>
                  </Card>
                </Form.Group>
              </Row>
            </>
          )}
        </>
      )}

      {!isSimplifiedCredentialCustomization && (
        <Row className="mx-0 mb-3">
          <Form.Group as={Col} className="mb-3" sm="12">
            <Form.Check
              checked={
                values.json.credentialAccess.apresentation?.showQRCode?.status ===
                  true
                  ? true
                  : false
              }
              type="checkbox"
              name="json.credentialAccess.apresentation.showQRCode.status"
              label="Mostrar QRCode"
              onChange={(e) => {
                setFieldValue(
                  "json.credentialAccess.apresentation.showQRCode.status",
                  values.json.credentialAccess.apresentation?.showQRCode
                    ?.status === true
                    ? false
                    : true,
                );
              }}
            />
            {values.json.credentialAccess.apresentation?.showQRCode?.status ===
              true ? (
              <Row>
                <Form.Group className="mb-3" as={Col} sm="4">
                  <Form.Label>Texto</Form.Label>
                  <Form.Control
                    type="text"
                    name="json.credentialAccess.apresentation.showQRCode.text"
                    value={
                      values.json.credentialAccess.apresentation.showQRCode.text
                    }
                    onChange={handleChange}
                    isInvalid={
                      !!errors.json?.credentialAccess?.apresentation?.showQRCode
                        ?.text
                    }
                    isValid={
                      touched.json?.credentialAccess?.apresentation?.showQRCode
                        ?.text &&
                      !errors.json?.credentialAccess?.apresentation?.showQRCode
                        ?.text
                    }
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {
                      errors.json?.credentialAccess?.apresentation?.showQRCode
                        ?.text
                    }
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" as={Col} sm="4">
                  <Form.Label>
                    URL
                    <OverlayTrigger
                      overlay={
                        <Tooltip>
                          Caso nenhuma URL seja fornecida o QRCode irá
                          redirecionar o usuário ao evento
                        </Tooltip>
                      }
                    >
                      <HelpTooltip icon={faExclamationCircle} />
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="json.credentialAccess.apresentation.showQRCode.link"
                    value={
                      values.json.credentialAccess.apresentation.showQRCode.link
                    }
                    onChange={handleChange}
                    isInvalid={
                      !!errors.json?.credentialAccess?.apresentation?.showQRCode
                        ?.link
                    }
                    isValid={
                      touched.json?.credentialAccess?.apresentation?.showQRCode
                        ?.link &&
                      !errors.json?.credentialAccess?.apresentation?.showQRCode
                        ?.link
                    }
                  />
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {
                      errors.json?.credentialAccess?.apresentation?.showQRCode
                        ?.link
                    }
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" as={Col} sm="4">
                  <Form.Label>Posição Y</Form.Label>
                  <Form.Select
                    name="json.credentialAccess.apresentation.showQRCode.position.y"
                    onChange={(e) => {
                      setFieldValue(
                        "json.credentialAccess.apresentation.showQRCode.position.y",
                        e.target.value,
                      );
                    }}
                    value={
                      values.json.credentialAccess.apresentation.showQRCode
                        .position.y
                    }
                    isInvalid={
                      !!errors.json?.credentialAccess?.apresentation?.showQRCode
                        ?.position?.y
                    }
                    isValid={
                      touched.json?.credentialAccess?.apresentation?.showQRCode
                        ?.position?.y &&
                      !errors.json?.credentialAccess?.apresentation?.showQRCode
                        ?.position?.y
                    }
                  >
                    <option value={"top"}>Topo</option>
                    <option value={"bottom"}>Fundo</option>
                  </Form.Select>
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {
                      errors.json?.credentialAccess?.apresentation?.showQRCode
                        ?.position?.y
                    }
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm="4">
                  <Form.Label>Posição X</Form.Label>
                  <Form.Select
                    name="json.credentialAccess.apresentation.showQRCode.position.x"
                    onChange={(e) => {
                      setFieldValue(
                        "json.credentialAccess.apresentation.showQRCode.position.x",
                        e.target.value,
                      );
                    }}
                    value={
                      values.json.credentialAccess.apresentation.showQRCode
                        .position.x
                    }
                    isInvalid={
                      !!errors.json?.credentialAccess?.apresentation?.showQRCode
                        ?.position?.x
                    }
                    isValid={
                      touched.json?.credentialAccess?.apresentation?.showQRCode
                        ?.position?.x &&
                      !errors.json?.credentialAccess?.apresentation?.showQRCode
                        ?.position?.x
                    }
                  >
                    <option value={"left"}>Esquerda</option>
                    <option value={"right"}>Direita</option>
                  </Form.Select>
                  <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                  <Form.Control.Feedback type="invalid">
                    {
                      errors.json?.credentialAccess?.apresentation?.showQRCode
                        ?.position?.x
                    }
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
            ) : (
              ""
            )}
          </Form.Group>
        </Row>
      )}
    </>
  );
};

export const BackgroundCredentialsFormComponent = ({
  touched,
  errors,
  values,
  setFieldValue,
  handlePreviewSuccess,
  handlePreviewError,
  platform,
}) => {
  switch (values.json.credentialAccess.apresentation.textSide.background.type) {
    case "image":
      return (
        <Form.Group as={Col} className="mb-3">
          <Card className="mb-0">
            <Card.Body>
              <Form.Control
                type="hidden"
                name="json.credentialAccess.apresentation.textSide.background.url"
                value={
                  values.json.credentialAccess.apresentation.textSide.background
                    .url
                }
                onChange={(e) => {
                  setFieldValue(
                    "json.credentialAccess.apresentation.textSide.background.url",
                    e.target.value,
                  );
                }}
                isInvalid={
                  !!errors?.json?.credentialAccess?.apresentation?.textSide
                    ?.background?.url
                }
                isValid={
                  touched?.json?.credentialAccess?.apresentation?.textSide
                    ?.background?.url &&
                  !errors?.json?.credentialAccess?.apresentation?.textSide
                    ?.background?.url
                }
              />
              <div className="imagesUpload">
                <img
                  className={`h-100 img-thumbnail ${!!errors?.json?.credentialAccess?.apresentation?.textSide?.background?.url ? "is-invalid" : ""}`}
                  src={
                    values.json.credentialAccess.apresentation.textSide
                      .background.url !== "" &&
                      values.json.credentialAccess.apresentation.textSide
                        .background.url !== null
                      ? domainConfig.imageServer +
                      (platform === 1 ? "/evento/" : "/plataforma-eventos/") +
                      (platform === 1 ? values.evento_id : values.event_id) +
                      "/" +
                      values.json.credentialAccess.apresentation.textSide
                        .background.url
                      : domainConfig.imageServer + "/empty-background.png"
                  }
                  alt="Imagem preview"
                />
                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  {
                    errors.json?.credentialAccess?.apresentation?.textSide
                      ?.background?.url
                  }
                </Form.Control.Feedback>
              </div>
              <DropzoneComponent
                className="mt-3 dropzoneComp"
                type="image"
                handlePreviewSuccess={handlePreviewSuccess}
                handlePreviewError={handlePreviewError}
                setFieldValue={setFieldValue}
                folder={platform === 1 ? "evento" : "plataforma-eventos"}
                id={platform === 1 ? values.evento_id : values.event_id}
                field="json.credentialAccess.apresentation.textSide.background.url"
              />
            </Card.Body>
          </Card>
        </Form.Group>
      );
    default:
      return "";
  }
};

export const SideButtonPanel = ({
  dataTable,
  hasUnsavedChanges,
  isSubmitting,
  onSubmit,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState("networking");
  const [previewDialogType, setPreviewDialogType] = useState(null);
  const previewUrls = getEventShareUrls(dataTable);
  const previewUrl = previewMode === "networking"
    ? previewUrls.networking
    : previewUrls.credentialLogin;

  const handlePreviewButtonClick = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    if (!dataTable?.token) {
      setPreviewDialogType("event-not-saved");
      return;
    }

    if (!previewUrl) {
      setPreviewDialogType("preview-link-unavailable");
      return;
    }

    if (hasUnsavedChanges) {
      setPreviewDialogType("unsaved-changes");
      return;
    }

    setIsOpen(true);
  };

  const confirmOpenSavedPreview = () => {
    setPreviewDialogType(null);
    setIsOpen(true);
  };

  useEffect(() => {
    let frameId = null;

    const updateModalScrollbarWidth = () => {
      frameId = null;

      const modalBodies = Array.from(
        document.querySelectorAll(".modal.show .modal-body"),
      );
      const activeModalBody = modalBodies.reverse().find((modalBody) => {
        return modalBody.offsetParent !== null;
      });
      const scrollbarWidth = activeModalBody
        ? Math.max(activeModalBody.offsetWidth - activeModalBody.clientWidth, 0)
        : 0;

      document.documentElement.style.setProperty(
        "--side-panel-modal-scrollbar-width",
        `${scrollbarWidth}px`,
      );
    };

    const scheduleUpdate = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(updateModalScrollbarWidth);
    };

    const observer = new MutationObserver(scheduleUpdate);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    window.addEventListener("resize", scheduleUpdate);
    scheduleUpdate();

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", scheduleUpdate);

      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      document.documentElement.style.removeProperty(
        "--side-panel-modal-scrollbar-width",
      );
    };
  }, []);

  const handleSave = (e) => {
    e.preventDefault();
    onSubmit(false); // false means regular save
  };

  const handleApply = (e) => {
    e.preventDefault();
    onSubmit(true); // true means apply only
  };

  return (
    <>
      <div className={`side-panel-button side-panel-button-mode02 ${isOpen ? "" : "open"}`}>
        <OverlayTrigger placement="left" overlay={<Tooltip>Visualização da customização</Tooltip>}>
          <Button
            id="btn-preview-layout"
            type="button"
            variant="primary"
            className={`my-1`}
            onClick={handlePreviewButtonClick}
          >
            <FontAwesomeIcon icon={faPalette} size={"lg"} />
          </Button>
        </OverlayTrigger>
        <OverlayTrigger placement="left" overlay={<Tooltip>Salvar e aplicar</Tooltip>}>
          <Button
            className="my-1"
            variant="primary"
            type="button"
            onClick={handleApply}
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faSave} size={"lg"} />
          </Button>
        </OverlayTrigger>
        <OverlayTrigger placement="left" overlay={<Tooltip>Salvar e finalizar</Tooltip>}>
          <Button
            className="my-1"
            variant="primary"
            type="button"
            onClick={handleSave}
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faCheck} size={"lg"} />
          </Button>
        </OverlayTrigger>
      </div>
      {/* Side panel with inputs */}
      <div className={`side-panel side-panel-mode02 ${isOpen ? "open" : ""}`}>
        <div className="modal-header px-0 pb-2 pt-0 d-flex justify-content-between align-items-center">
          <div className="btn-group" role="group" aria-label="Webapp para pré-visualização">
            <button
              type="button"
              className={`btn btn-sm ${previewMode === "networking" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setPreviewMode("networking")}
            >
              Networking
            </button>
            <button
              type="button"
              className={`btn btn-sm ${previewMode === "credentialing" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setPreviewMode("credentialing")}
            >
              Credenciamento
            </button>
          </div>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="btn-close ms-2"
            aria-label="Close"
          ></button>
        </div>
        {isOpen && previewUrl && (
          <EventWebappPreview
            src={previewUrl}
            title={previewMode === "networking"
              ? "Pré-visualização do Networking"
              : "Pré-visualização do Credenciamento"}
          />
        )}
        <Modal
          show={Boolean(previewDialogType)}
          onHide={() => setPreviewDialogType(null)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {previewDialogType === "unsaved-changes"
                ? "Alterações não salvas"
                : previewDialogType === "event-not-saved"
                  ? "Evento não salvo"
                  : "Pré-visualização indisponível"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {previewDialogType === "unsaved-changes"
              ? "A pré-visualização mostrará a última versão salva do evento; suas alterações atuais não serão incluídas. Deseja continuar?"
              : previewDialogType === "event-not-saved"
                ? "Salve o evento para gerar o link e abrir a pré-visualização."
                : "O link do webapp não está disponível para este evento."}
          </Modal.Body>
          <Modal.Footer>
            {previewDialogType === "unsaved-changes" ? (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setPreviewDialogType(null)}
                >
                  Voltar
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={confirmOpenSavedPreview}
                >
                  Abrir versão salva
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setPreviewDialogType(null)}
              >
                Fechar
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export const ZPLModelForm = ({
  dataAddForm,
  setDataAddForm,
  modal,
  setModal,
}) => {
  const addZPLModel = (values, actions) => {
    const newObject = values;
    setDataAddForm((prevDataAddForm) => {
      const indexToUpdate = prevDataAddForm.zplModels.findIndex(
        (field) => field.zplModelID === values.zplModelID,
      );
      let newDataAddForm = [...prevDataAddForm.zplModels];

      if (indexToUpdate !== -1) {
        newDataAddForm[indexToUpdate] = newObject;
      } else {
        newDataAddForm = [...newDataAddForm, newObject];
      }
      return {
        ...prevDataAddForm,
        zplModels: newDataAddForm,
      };
    });
    setModal((prevState) => ({
      ...prevState,
      showZPLModelForm: false,
    }));
    setDataAddForm((prevState) => ({
      ...prevState,
      addFieldData: false,
    }));
  };

  const removeZPLModel = (zplModelID) => {
    setDataAddForm((prevDataAddForm) => {
      const indexToRemove = prevDataAddForm.zplModels.findIndex(
        (field) => field.zplModelID === zplModelID,
      );
      const newDataAddForm = [...prevDataAddForm.zplModels];
      newDataAddForm.splice(indexToRemove, 1);
      return {
        ...prevDataAddForm,
        zplModels: newDataAddForm,
      };
    });
  };

  return (
    <>
      <Modal
        show={modal.showZPLModelForm}
        size={"sm"}
        onHide={(e) => {
          setModal((prevState) => ({
            ...prevState,
            showZPLModelForm: false,
          }));
          setDataAddForm((prevState) => ({
            ...prevState,
            addFieldData: false,
          }));
        }}
        fullscreen={"sm-down"}
        centered
        backdrop
      >
        <Formik
          enableReinitialize
          onSubmit={addZPLModel}
          initialValues={{
            zplModelID:
              dataAddForm.addFieldData.zplModelID !== undefined
                ? dataAddForm.addFieldData.zplModelID
                : generateSixDigitCode(),
            name:
              dataAddForm.addFieldData.name !== undefined
                ? dataAddForm.addFieldData.name
                : "",
            template:
              dataAddForm.addFieldData.template !== undefined
                ? dataAddForm.addFieldData.template
                : "",
          }}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            touched,
            isValid,
            errors,
            dirty,
            isSubmitting,
            setFieldValue,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>
                  {dataAddForm.addFieldData
                    ? "Editar modelo"
                    : "Adicionar modelo"}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Row className="mb-3">
                  <Form.Group className="mb-3" as={Col} sm={"12"}>
                    <Form.Label>Título</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      isInvalid={!!errors.name}
                      isValid={touched.name && !errors.name}
                    />
                    <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-3" as={Col} sm={12}>
                    <Form.Label>
                      Modelo
                      <OverlayTrigger
                        overlay={
                          <Tooltip>
                            Para adicionar campos do formulário, verifique o ID
                            do campo na composição do formulário<br></br>
                            <br></br>
                            E-mail: {`{{email}}`}
                            <br></br>
                            ID do usuário: {`{{user_id}}`}
                            <br></br>
                            QRCode do usuário: {`{{qrcode}}`}
                            <br></br>
                            Outros campos: {`{{id_campo}}`}
                          </Tooltip>
                        }
                      >
                        <HelpTooltip icon={faExclamationCircle} />
                      </OverlayTrigger>
                      <span
                        className="badge text-dark bg-light cursor-pointer ms-1"
                        onClick={() =>
                          redirectURL(
                            true,
                            `https://labelary.com/viewer.html?zpl=${encodeURIComponent(values.template)}`,
                          )
                        }
                      >
                        Visualizar modelo
                      </span>
                    </Form.Label>
                    <Form.Control
                      style={{ height: "150px" }}
                      as="textarea"
                      name={"template"}
                      value={values.template}
                      onChange={handleChange}
                      isInvalid={!!errors?.template}
                      isValid={touched?.template && !errors?.template}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.template}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                {dataAddForm.addFieldData ? (
                  <Button
                    variant="danger"
                    type="button"
                    className="float-end me-2"
                    onClick={() => {
                      removeZPLModel(values.zplModelID);
                      setModal((prevState) => ({
                        ...prevState,
                        showZPLModelForm: false,
                      }));
                    }}
                  >
                    Excluir
                  </Button>
                ) : (
                  ""
                )}
                <Button
                  variant="primary"
                  type="submit"
                  className="float-end"
                  disabled={isSubmitting}
                >
                  Salvar
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export const ZPLModelsComponent = ({
  dataAddForm,
  setDataAddForm,
  setModal,
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (index) => {
    if (draggedIndex === index) return;

    const updatedForm = [...dataAddForm.zplModels];
    const [draggedItem] = updatedForm.splice(draggedIndex, 1);
    updatedForm.splice(index, 0, draggedItem);

    setDraggedIndex(index);
    setDataAddForm((prevDataAddForm) => {
      return {
        ...prevDataAddForm,
        zplModels: updatedForm,
      };
    });
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <Row className="mb-3 mx-0">
      <Card className="mb-0 formCard col-md-12 px-0">
        <Card.Body>
          <Col className="text-center mb-2">
            {dataAddForm.zplModels.length > 0 && (
              <small className="text-center opacity-75">
                (Clique e arraste para ordenar)
              </small>
            )}
          </Col>
          {dataAddForm.zplModels.length > 0 ? (
            <Row>
              {dataAddForm.zplModels.map((valueX, index) => (
                <Col
                  key={"dataAddForm" + index}
                  className="mb-3"
                  md={3}
                  sm={12}
                >
                  <Button
                    style={{ height: "50px" }}
                    variant="secondary"
                    className={`w-100 b-15`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => {
                      e.preventDefault();
                      handleDragOver(index);
                    }}
                    onClick={() => {
                      setDataAddForm((prevState) => ({
                        ...prevState,
                        addFieldData: valueX,
                      }));
                      setModal((prevState) => ({
                        ...prevState,
                        showZPLModelForm: true,
                      }));
                    }}
                    onDragEnd={handleDragEnd}
                  >
                    {valueX.name}
                  </Button>
                </Col>
              ))}
            </Row>
          ) : (
            <>
              <p className="text-center opacity-75">Nenhum modelo encontrado</p>
            </>
          )}
        </Card.Body>
        <Col className="mb-3 text-center">
          <Button
            variant="primary"
            className="btn-sm"
            onClick={() => {
              setDataAddForm((prevState) => ({
                ...prevState,
                addFieldData: false,
              }));
              setModal((prevState) => ({
                ...prevState,
                showZPLModelForm: true,
              }));
            }}
          >
            Adicionar modelo
          </Button>
        </Col>
      </Card>
    </Row>
  );
};

export const LoadingFormComponent = ({
  touched,
  errors,
  values,
  setFieldValue,
  handlePreviewSuccess,
  handlePreviewError,
}) => {
  switch (values.json.login.loading.type) {
    case "image":
      return (
        <Form.Group as={Col} className="mt-3">
          <Card className="mb-0">
            <Card.Body>
              <Form.Control
                type="hidden"
                name="json.login.loading.url"
                value={values.json.login.loading.url}
                onChange={(e) => {
                  setFieldValue("json.login.loading.url", e.target.value);
                }}
                isInvalid={!!errors?.json?.login?.loading?.url}
                isValid={
                  touched?.json?.login?.loading?.url &&
                  !errors?.json?.login?.loading?.url
                }
              />
              <div className="imagesUpload">
                <img
                  className={`h-100 img-thumbnail ${!!errors?.json?.login?.loading?.url ? "is-invalid" : ""}`}
                  src={
                    values.json.login?.loading.url !== "" &&
                      values.json.login?.loading.url !== null
                      ? domainConfig.imageServer +
                      "/plataforma-eventos/" +
                      values.event_id +
                      "/" +
                      values.json.login?.loading.url
                      : domainConfig.imageServer + "/empty-background.png"
                  }
                  alt="Imagem preview"
                />
                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  {errors.json?.login?.loading?.url}
                </Form.Control.Feedback>
              </div>
              <DropzoneComponent
                className="mt-3 dropzoneComp"
                type="image"
                handlePreviewSuccess={handlePreviewSuccess}
                handlePreviewError={handlePreviewError}
                setFieldValue={setFieldValue}
                folder="plataforma-eventos"
                id={values.event_id}
                field="json.login.loading.url"
              />
            </Card.Body>
          </Card>
          <Form.Group as={Col} className="mt-3">
            <Form.Label>Largura (px)</Form.Label>
            <Form.Control
              type="text"
              name="json.login.loading.logoWidth"
              value={values.json.login.loading.logoWidth}
              onChange={(e) => {
                setFieldValue("json.login.loading.logoWidth", e.target.value);
              }}
              isInvalid={!!errors?.json?.login?.loading?.logoWidth}
              isValid={
                touched?.json?.login?.loading?.logoWidth &&
                !errors?.json?.login?.loading?.logoWidth
              }
            />
            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              {errors?.json?.login?.loading?.logoWidth}
            </Form.Control.Feedback>
          </Form.Group>
        </Form.Group>
      );
    case "video":
      return (
        <Form.Group as={Col} className="mt-3">
          <Card className="mb-0">
            <Card.Body>
              <Form.Control
                type="hidden"
                name="json.login.loading.url"
                value={values.json.login.loading.url}
                onChange={(e) => {
                  setFieldValue("json.login.loading.url", e.target.value);
                }}
                isInvalid={!!errors?.json?.login.loading?.url}
                isValid={
                  touched?.json?.login.loading?.url &&
                  !errors?.json?.login.loading?.url
                }
              />
              <div className="imagesUpload">
                <video
                  className={`h-100 img-thumbnail ${!!errors?.json?.login.loading?.url ? "is-invalid" : ""}`}
                  src={
                    values.json.login.loading.url !== "" &&
                      values.json.login.loading.url !== null
                      ? domainConfig.imageServer +
                      "/plataforma-eventos/" +
                      values.event_id +
                      "/" +
                      values.json.login.loading.url
                      : domainConfig.imageServer + "/empty-background.png"
                  }
                  controls={true}
                />
                <Form.Control.Feedback type="valid"></Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  {errors.json?.login.loading?.url}
                </Form.Control.Feedback>
              </div>
              <DropzoneComponent
                className="mt-3 dropzoneComp"
                type="video"
                handlePreviewSuccess={handlePreviewSuccess}
                handlePreviewError={handlePreviewError}
                setFieldValue={setFieldValue}
                folder="plataforma-eventos"
                id={values.event_id}
                field="json.login.loading.url"
              />
            </Card.Body>
          </Card>
          <Form.Group as={Col} className="mt-3">
            <Form.Label>Largura (px)</Form.Label>
            <Form.Control
              type="text"
              name="json.login.loading.logoWidth"
              value={values.json.login.loading.logoWidth}
              onChange={(e) => {
                setFieldValue("json.login.loading.logoWidth", e.target.value);
              }}
              isInvalid={!!errors?.json?.login?.loading?.logoWidth}
              isValid={
                touched?.json?.login?.loading?.logoWidth &&
                !errors?.json?.login?.loading?.logoWidth
              }
            />
            <Form.Control.Feedback type="valid"></Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
              {errors?.json?.login?.loading?.logoWidth}
            </Form.Control.Feedback>
          </Form.Group>
        </Form.Group>
      );
    default:
      return "";
  }
};
